import { type FormEvent, lazy, type ReactNode, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import {
  ApiError,
  approveContent,
  backToPlanning,
  clearUsageLimitHold,
  closeTerminal,
  type ContactSheet as ContactSheetView,
  type ContentAddress,
  type ContentLibraryUse,
  contactSheetTileBox,
  contactSheetTileSeconds,
  contactSheetUrl,
  type Content as ContentData,
  type ContentBrandBook,
  type ContentVersionPin,
  type ContentPlan,
  type CostEstimate,
  contentPath,
  createTerminal,
  deleteContent,
  fetchContent,
  rejectContent,
  retryContentAdPauses,
  unrejectContent,
  fetchContentLibraryUses,
  fetchContentPlan,
  fetchPlanning,
  fetchProduction,
  fetchReview,
  showMeNow,
  approveScript,
  adjustSketch,
  approveSketch,
  editSketchScene,
  fetchRetryPlan,
  fetchScript,
  fetchSketch,
  fetchTerminals,
  fetchVersions,
  markContentReady,
  type MergedRule,
  type OutputProbe,
  type Planning,
  type PlanProblem,
  type PlanStatus,
  type PlanStep,
  type Production,
  type ProbeReport,
  type ProductionStep,
  requestChanges,
  REVIEW_SCORE_AXES,
  REVIEW_SEVERITIES,
  type AutoReviewView,
  type ReviewFinding,
  type ReviewReport,
  type ReviewSeverity,
  type ReviewView,
  runReview,
  type ShareCopy,
  DEFAULT_SHARE_PLATFORM,
  SHARE_PLATFORMS,
  type SharePost,
  type StepStatus,
  type StepType,
  type VersionFile,
  type Versions,
  type ClipLedger,
  type ContentEvent,
  fetchClipLedger,
  fetchContentHistory,
  fetchVideoModels,
  isDutchLanguage,
  languageLabel,
  raiseSpendCap,
  setContentLanguage,
  setVideoModel,
  setAutopilot,
  updateBriefDetails,
  type VideoCatalogueView,
  retryContent,
  retryContentSetup,
  type RetryPlan,
  saveScript,
  type ScriptChange,
  type ScriptView,
  type SketchProblem,
  type SketchScene,
  type SketchSceneEdit,
  type SketchState,
  type SketchView,
  prepareSketch,
  startPlanning,
  undoSketch,
  leaveProductionQueue,
  startProduction,
  stopPlanning,
  stopProduction,
  toneLabel,
  LIBRARY_KIND_LABEL,
  libraryItemHref,
} from '../api.ts';
import { AssetGallery } from '../AssetGallery.tsx';
import { BriefDetailsPanel } from '../BriefDetailsFields.tsx';
import { CreativeNotesPanel } from '../CreativeNotes.tsx';
import { HowApprovalWorks, ScenesIntro } from '../ApprovalGuide.tsx';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import {
  DASHBOARD_POLL_MS,
  DESKTOP_QUERY,
  describeError,
  redirectIfUnauthorised,
  useAppData,
  useMediaQuery,
  useNow,
} from '../data.tsx';
import { useAssetUrls } from '../assets.ts';
import { SavedAsHouseRules, useTaughtRules } from '../HouseRulesPanel.tsx';
import { Icon, type IconName } from '../Icon.tsx';
import { ProductionLog } from '../ProductionLog.tsx';
import { ProfileProposalPanel } from '../ProductProfile.tsx';
import { StageProgress } from '../StageProgress.tsx';
import { brandBookPath, contentIdFromPath, Link, navigate, useLocation } from '../router.tsx';
import { countdown, elapsed, hourMinute, localTime, since, startsIn } from '../schedule.ts';
import { useToast } from '../toast.tsx';
import {
  Badge,
  Facts,
  Loader,
  Markdown,
  Notice,
  PageHeader,
  Panel,
  revealPanel,
  Segmented,
  Skeleton,
  VisuallyHidden,
  STEP_TONE,
  StatusBadge,
  usePanelOpen,
} from '../ui.tsx';
import { boardCardPath, ContentBoardPanel, ContentPostsPanel, useArchiveContent } from '../board/ContentPanels.tsx';
import { MarkReadyDialog } from '../board/dialogs.tsx';
import { DuplicateDialog } from '../board/Drawer.tsx';
import { Claims } from '../board/Claims.tsx';
import { Decisions } from '../board/Decisions.tsx';
import { PlanningConversation } from '../board/PlanningConversation.tsx';
import { BrandStatusBadge } from './BrandBooks.tsx';
import { DeletionWarning } from './Contents.tsx';
import * as wording from '../wording.ts';

// xterm.js is the largest dependency in the bundle and only matters once a
// terminal is actually opened, so it stays in its own chunk.
const TerminalPane = lazy(() =>
  import('../TerminalPane.tsx').then((module) => ({ default: module.TerminalPane })),
);

/**
 * One content: where it is in its life, the one thing to do next, and the
 * facts behind it.
 *
 * The header, the five-stage progress bar (`StageProgress`) and the next-step
 * card (`nextStep()`, which owns the page's one primary action) lead into one
 * stage panel chosen by status (`stagePanelOf()`): planning, the steps to be
 * produced, production, the narration gate or review. The rest is under
 * **Details**, each collapsed into a row that mounts its body — and starts its
 * poll or socket — only when it is opened, and the technical panels (the plan,
 * its steps, costs, the video model, the library, the container) are in
 * **Advanced details** at the foot of the page (US-115). The side column keeps
 * the facts and the estimated total.
 *
 * Like the list, it re-reads itself every three seconds while the tab is
 * visible: a content is moved along by an agent in another process, so looking
 * again is the only way to see it happen.
 */
export function Content() {
  const { pathname } = useLocation();
  // A new content is a new page: Duplicate navigates from one to the other,
  // and nothing read for the first may show on the second.
  return <ContentPage key={contentIdFromPath(pathname) ?? ''} />;
}

function ContentPage() {
  const { pathname } = useLocation();
  const id = contentIdFromPath(pathname);
  // `stats` for the container ceilings in the side column: they are an
  // install-wide setting, so the app-wide poll is where they come from rather
  // than from this content's own view (US-077).
  const { refresh, stats, tones } = useAppData();
  const toast = useToast();
  const [content, setContent] = useState<ContentData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [confirming, setConfirming] = useState<'delete' | 'ready' | 'resume' | 'reject' | 'unreject' | null>(null);
  /** The reason typed into the **Reject** dialog; optional, so it may stay empty. */
  const [rejectReason, setRejectReason] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const archiving = useArchiveContent(setContent);
  const [planning, setPlanning] = useState<Planning | null>(null);
  const [plan, setPlan] = useState<ContentPlan | null>(null);
  /** The errors the last **Mark ready** refused on; cleared by the next one. */
  const [readyErrors, setReadyErrors] = useState<readonly PlanProblem[] | null>(null);
  /** What Retry would do, as the server works it out (US-034). */
  const [retryPlan, setRetryPlan] = useState<RetryPlan | null>(null);
  /** Where the loop is, while it is running (US-035). */
  const [production, setProduction] = useState<Production | null>(null);
  /** The rendered versions and the files behind them (US-042). */
  const [versions, setVersions] = useState<Versions | null>(null);
  /** The review pass's findings, and the decision beside them (US-043). */
  const [review, setReview] = useState<ReviewView | null>(null);
  /** The **Request changes** box, open only once the operator asks for it. */
  const [feedback, setFeedback] = useState<string | null>(null);
  /** The narration as words, and the gate in front of it (US-090). */
  const [script, setScript] = useState<ScriptView | null>(null);
  /** The storyboard as scene cards, while the content waits at the sketch gate (US-094). */
  const [sketch, setSketch] = useState<SketchView | null>(null);
  /**
   * The narration was just approved and the scenes come next (US-105): the
   * run is on its way to the sketch gate, and the page shows the scenes panel
   * waiting for it rather than the production panel it would briefly be.
   */
  const [sketchNext, setSketchNext] = useState(false);
  /** The technical panels at the bottom of the page, folded by default (US-115). */
  const advanced = useAdvancedDetails();

  useEffect(() => {
    if (id === null) {
      setLoadError('That is not a content URL.');
      return;
    }
    const controller = new AbortController();
    const load = (): void => {
      fetchContent(id, controller.signal)
        .then((loaded) => {
          setContent(loaded);
          setLoadError(null);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          if (redirectIfUnauthorised(error)) return;
          setLoadError(describeError(error));
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id]);

  // The plan is a file an agent is writing in another process, so the panel
  // beside the terminal is polled on the same beat as the content itself. Only
  // while the content is somewhere the plan can still change: once it is
  // producing, the steps on the page are the record, not the file.
  const watchPlan = content !== null && (content.status === 'pending' || content.status === 'ready');
  useEffect(() => {
    if (id === null || !watchPlan) return;
    const controller = new AbortController();
    const load = (): void => {
      fetchPlanning(id, controller.signal)
        .then(setPlanning)
        .catch(() => {
          // A failed poll is not worth a toast; the next one usually works and
          // the panel keeps showing the last reading it had.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id, watchPlan]);

  // The steps and the plan behind them, on the same beat. This one runs for
  // every status: while the content is pending the list previews the file the
  // conversation is writing, and from `ready` onwards it is the content's own
  // steps, which production moves through under the operator's eyes.
  useEffect(() => {
    if (id === null) return;
    const controller = new AbortController();
    const load = (): void => {
      fetchContentPlan(id, controller.signal)
        .then(setPlan)
        .catch(() => {
          // As with the planning poll: the panel keeps its last reading.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id]);

  // A failure is the one state where the next action is not obvious, so the
  // server is asked what a retry would do and the page shows the answer before
  // the button is pressed (US-034). Polled on the page's own beat: the plan
  // moves with the content — a step finished by another run changes where a
  // retry would resume.
  const failed = content !== null && content.status === 'failed';
  useEffect(() => {
    if (id === null || !failed) return;
    const controller = new AbortController();
    const load = (): void => {
      fetchRetryPlan(id, controller.signal)
        .then(setRetryPlan)
        .catch(() => {
          // As with every other poll on this page: the panel keeps its last
          // reading rather than flashing an error at a transient failure.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id, failed]);

  // The narration script (US-090). Loaded for any content past planning — the
  // words are worth reading whether or not anything is waiting on them — and
  // polled while the gate is open, because the step that writes them may still
  // be finishing as the page is opened.
  const planned = content !== null && content.status !== 'pending';
  const atGate = content !== null && content.status === 'approval';
  useEffect(() => {
    if (id === null || !planned) return;
    const controller = new AbortController();
    const load = (): void => {
      fetchScript(id, controller.signal)
        .then(setScript)
        .catch(() => {
          // The panel keeps its last reading, like every other poll here.
        });
    };
    load();
    if (!atGate) return () => controller.abort();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id, planned, atGate]);

  // The plan sketch (US-094), polled for as long as the content is stopped at
  // its gate: a fit or an adjustment runs in another process and moves
  // `state` from `preparing` to `ready` without the page asking.
  const atSketch = content !== null && content.status === 'approval' && content.gate === 'sketch';
  // Only the stretch between the two gates is bridged: at the sketch gate the
  // panel is there by status, and anything other than producing ends the wait.
  const bridging = sketchNext && content !== null && content.status === 'producing';
  useEffect(() => {
    if (sketchNext && !bridging) setSketchNext(false);
  }, [sketchNext, bridging]);
  useEffect(() => {
    if (id === null || !atSketch) return;
    const controller = new AbortController();
    const load = (): void => {
      fetchSketch(id, controller.signal)
        .then(setSketch)
        .catch(() => {
          // The panel keeps its last reading, like every other poll here.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id, atSketch]);

  // The loop's own account of itself, on the same three-second beat (US-035).
  // Only while there is a run to account for: the counters are this server's
  // scratch space, and a content that is not producing or held has none. The
  // last reading is kept rather than cleared, so the panel does not blink
  // empty between a failed poll and the next good one. A content that failed
  // in production reads it once, for the stage panel that shows where it
  // stopped; nothing moves there until it is retried.
  const producing = content !== null && (content.status === 'producing' || content.status === 'waiting');
  const failedInProduction = content !== null && failed && stagePanelOf(content) === 'production';
  useEffect(() => {
    if (id === null || !(producing || failedInProduction)) return;
    const controller = new AbortController();
    const load = (): void => {
      fetchProduction(id, controller.signal)
        .then(setProduction)
        .catch(() => {
          // As with every other poll on this page: keep the last reading.
        });
    };
    load();
    if (!producing) return () => controller.abort();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id, producing, failedInProduction]);

  // The versions, once there is one. Polled like everything else on this page
  // rather than fetched once: a production still running renders v1 under the
  // operator's eyes, and a revision adds v2 to a page that is already open.
  const rendered = content !== null && content.currentVersion > 0;
  useEffect(() => {
    if (id === null || !rendered) return;
    const controller = new AbortController();
    const load = (): void => {
      fetchVersions(id, controller.signal)
        .then(setVersions)
        .catch(() => {
          // As with every other poll on this page: keep the last reading.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id, rendered]);

  // The review, from the moment there is a version to review until the content
  // is delivered. Polled like everything else here: the pass runs in another
  // process and writes its findings when it is done, so a page left open on a
  // content that was still producing fills in by itself.
  // Every status but `pending`, rather than only the ones with a version: a
  // content that reached review with nothing rendered has to be told so in
  // words, and the endpoint is two row reads.
  const reviewable = content !== null && content.status !== 'pending';
  useEffect(() => {
    if (id === null || !reviewable) return;
    const controller = new AbortController();
    const load = (): void => {
      fetchReview(id, controller.signal)
        .then(setReview)
        .catch(() => {
          // As with every other poll on this page: keep the last reading.
        });
    };
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, DASHBOARD_POLL_MS);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id, reviewable]);

  if (loadError !== null) {
    return (
      <div className="page page--narrow">
        <PageHeader back={{ href: '/content', label: 'Content' }} title={content?.name ?? 'Content'} />
        <Notice kind="error">Could not load this content: {loadError}</Notice>
      </div>
    );
  }

  if (id === null || content === null) {
    return (
      <div className="page">
        <PageHeader back={{ href: '/content', label: 'Content' }} title="Content" />
        <div className="panel">
          <div className="panel__body">
            <Skeleton lines={5} />
          </div>
        </div>
      </div>
    );
  }

  /** Runs an action, tells the operator how it went, and re-reads the content. */
  const run = (kind: NonNullable<Busy>, action: () => Promise<string | null>): void => {
    setBusy(kind);
    action()
      .then(async (message) => {
        if (message !== null) toast.ok(message);
        // The steps change with the content — **Mark ready** writes them — so
        // the panel is re-read here rather than waiting for the next poll.
        const [reloaded, , replanned] = await Promise.all([
          fetchContent(id),
          refresh(),
          fetchContentPlan(id).catch(() => null),
        ]);
        setContent(reloaded);
        if (replanned !== null) setPlan(replanned);
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  const onRetrySetup = (): void => {
    run('setup', async () => {
      const result = await retryContentSetup(id);
      if (result.setup !== null && !result.setup.ok) throw new Error(result.setup.message);
      return `${content.name} is cloned and ready to plan.`;
    });
  };

  /**
   * The one **Retry**: the server decides whether that means the clone again or
   * the production resumed, and the page has already shown which it will be.
   */
  const onRetry = (): void => {
    run('retry', async () => {
      const outcome = await retryContent(id);
      setRetryPlan(outcome.plan);
      if (!outcome.ok) throw new Error(outcome.message ?? 'This content cannot be retried.');
      return outcome.plan.action === 'setup'
        ? `${content.name} is being set up again.`
        : `${content.name} resumes at ${outcome.plan.resumeStep?.stepId ?? 'its first outstanding step'}.`;
    });
  };

  /** Opens (or rejoins) the planning conversation in `/workspace`. */
  const onStartPlanning = (): void => {
    run('planning', async () => {
      const view = await startPlanning(id);
      setPlanning(view);
      return view.mode === 'edit'
        ? `Planning resumed. The conversation is editing ${view.plan.path}.`
        : 'Planning started. Answer its questions and it writes the plan.';
    });
  };

  const onStopPlanning = (): void => {
    run('planning', async () => {
      setPlanning(await stopPlanning(id));
      return 'Planning stopped. The plan on disk is untouched.';
    });
  };

  /** Parses the plan and promotes the content; a plan that does not parse is
   * a `200` with the errors, which belong on the panel rather than in a toast. */
  const onMarkReady = (): void => {
    run('ready', async () => {
      const result = await markContentReady(id);
      setPlanning((previous) =>
        previous === null ? previous : { ...previous, plan: result.plan },
      );
      if (!result.ok) {
        setReadyErrors(result.errors);
        throw new Error(
          `${result.plan.path} does not parse yet: ${String(result.errors.length)} ${
            result.errors.length === 1 ? 'problem' : 'problems'
          } to fix.`,
        );
      }
      setReadyErrors(null);
      return `${result.content.name} is ready: ${String(result.plan.stepCount)} steps, about ${money(
        result.estimate.usd,
      )} of provider spend.`;
    });
  };

  const onBackToPlanning = (): void => {
    run('back-to-planning', async () => {
      await backToPlanning(id);
      setReadyErrors(null);
      return 'Back to planning. The steps stay until the plan is marked ready again.';
    });
  };

  /**
   * **Start production**: the loop takes the plan from here. Beyond the
   * concurrency cap this is not a refusal — the content takes a place in the
   * queue, and the answer says which of the two happened.
   */
  const onStartProduction = (): void => {
    run('production', async () => {
      const view = await startProduction(id);
      return view.queued
        ? `Every production slot is taken, so ${content.name} is #${String(view.queuePosition ?? 1)} in the queue and starts by itself.`
        : `Producing ${content.name}: ${String(view.steps.length)} steps, lowest priority first.`;
    });
  };

  /** **Stop production**: the agent is signalled and the content goes back to
   * ready. Everything already produced stays where it is. */
  const onStopProduction = (): void => {
    run('production', async () => {
      await stopProduction(id);
      return `Stopped. ${content.name} is ready again and resumes at the first step that is not done.`;
    });
  };

  /**
   * **Leave queue**: the content was waiting for a slot rather than working, so
   * there is nothing to stop — it simply stops waiting.
   */
  const onLeaveQueue = (): void => {
    run('production', async () => {
      await leaveProductionQueue(id);
      return `${content.name} left the production queue. Nothing it has produced changed; start it again whenever you like.`;
    });
  };

  const onResumeNow = (): void => {
    run('resume-hold', async () => {
      const { resumed } = await clearUsageLimitHold();
      return resumed === 0
        ? 'The hold is lifted. Nothing could start yet; every held content is on the queue.'
        : `The hold is lifted and ${String(resumed)} ${resumed === 1 ? 'content is' : 'contents are'} producing again.`;
    });
  };

  /**
   * Runs one review pass. A pass that could not run answers `ok: false` with a
   * sentence rather than an error, and that sentence belongs on the panel —
   * nothing about the content changed, and **Approve** is still there.
   */
  const onRunReview = (): void => {
    run('review', async () => {
      const result = await runReview(id);
      setReview(result.view);
      if (!result.ok) throw new Error(result.message);
      return result.message;
    });
  };

  /**
   * **Show me now**: stop the automatic rounds and take this version.
   *
   * Nothing in flight is cancelled, so the answer says which of the two
   * happened — the content is in review now, or it will be the moment the step
   * that is running lands. Both sentences are the server's.
   */
  const onShowNow = (): void => {
    run('show-now', async () => {
      const result = await showMeNow(id);
      const view = await fetchReview(id).catch(() => null);
      if (view !== null) setReview(view);
      return result.message;
    });
  };

  /** **Approve**: delivered, the container removed, the workspace kept. */
  const onApprove = (): void => {
    run('approve', async () => {
      const result = await approveContent(id);
      if (result.review !== null) setReview(result.review);
      return `${content.name} is delivered. Its container is gone; the workspace, the renders and the plan stay.`;
    });
  };

  /**
   * **Request changes**: the feedback is recorded against this version, one
   * headless pass edits the plan with it and the findings, and the next
   * version is produced from what it wrote.
   *
   * A pass that could not amend the plan answers `ok: false` with a sentence
   * and leaves `plan.md` exactly as it was — that sentence is the error, and
   * the box stays open with what was typed still in it.
   */
  const onRequestChanges = (): void => {
    const text = feedback ?? '';
    run('revise', async () => {
      const result = await requestChanges(id, text);
      const view = await fetchReview(id).catch(() => null);
      if (view !== null) setReview(view);
      setFeedback(null);
      return result.message;
    });
  };

  /** Saves the words without deciding anything: the operator is still reading. */
  const onSaveScript = (text: string): void => {
    if (id === null) return;
    setBusy('script');
    saveScript(id, text)
      .then((saved) => {
        setScript(saved);
        toast.ok(wording.NARRATION_SAVED);
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  /** **Approve**: the words are agreed, and the production goes on from here. */
  const onApproveScript = (text: string): void => {
    if (id === null) return;
    const backToSketch = script?.narrationChanged === true;
    setBusy('script');
    approveScript(id, text)
      .then(async (approved) => {
        setScript(approved);
        // Re-read at once, so a run that goes on to the scenes shows them as
        // the next step without waiting for the next poll.
        const [reloaded, next] = await Promise.all([
          fetchContent(id),
          fetchSketch(id).catch(() => null),
          refresh(),
        ]);
        setContent(reloaded);
        if (
          next !== null &&
          next.approvedAt === null &&
          reloaded.kind === 'video' &&
          reloaded.status === 'producing'
        ) {
          setSketch(next);
          setSketchNext(true);
        }
        toast.ok(
          backToSketch
            ? wording.NARRATION_APPROVED_BACK_TO_SCENES
            : approved.awaitingApproval
              ? wording.NARRATION_APPROVED
              : wording.NARRATION_APPROVED_CONTINUES,
        );
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
      })
      .finally(() => {
        setBusy(null);
      });
  };

  /**
   * **Approve scenes**: the scenes are agreed and the production
   * goes on. Through `run`, whose re-read of the content is what closes the
   * panel — the content is producing again by the time the answer lands.
   */
  const onApproveSketch = (): void => {
    run('sketch', async () => {
      setSketch(await approveSketch(id));
      return wording.SCENES_APPROVED;
    });
  };

  /**
   * **Change with a request**: the operator's prompt goes to one headless pass. Not
   * through `run` — nothing about the content changes, and the panel shows a
   * refusal next to the button rather than as a toast, so it rejects instead.
   */
  const onAdjustSketch = async (prompt: string): Promise<void> => {
    setBusy('sketch');
    try {
      setSketch(await adjustSketch(id, prompt));
    } finally {
      setBusy(null);
    }
  };

  /**
   * **Save** a hand edit of one scene card (US-100). Like the adjustment it
   * rejects rather than toasts: the refusal belongs on the card being edited.
   */
  const onEditScene = async (sceneNumber: number, edit: SketchSceneEdit): Promise<void> => {
    setBusy('sketch');
    try {
      setSketch(await editSketchScene(id, sceneNumber, edit));
    } finally {
      setBusy(null);
    }
  };

  /**
   * **Undo last change** (US-112): the plan goes back to how it was before the
   * last change. Rejects like the adjustment, so the panel shows a refusal.
   */
  const onUndoSketch = async (): Promise<void> => {
    setBusy('sketch');
    try {
      setSketch(await undoSketch(id));
    } finally {
      setBusy(null);
    }
  };

  /** **Try again** after a failed fit or adjustment: the run starts over. */
  const onPrepareSketch = (): void => {
    run('sketch', async () => {
      setSketch(await prepareSketch(id));
      return null;
    });
  };

  const onReject = (): void => {
    run('reject', async () => {
      await rejectContent(id, rejectReason);
      setRejectReason('');
      return wording.contentRejected(content.name);
    });
  };

  const onUnreject = (): void => {
    run('unreject', async () => {
      await unrejectContent(id);
      return wording.contentUnrejected(content.name);
    });
  };

  const onRetryAdPauses = (): void => {
    run('retry-pauses', async () => {
      await retryContentAdPauses(id);
      return null;
    });
  };

  const onDelete = (): void => {
    setBusy('delete');
    deleteContent(id)
      .then(async () => {
        await refresh();
        toast.ok(`Deleted ${content.name}. Nothing in any repository changed.`);
        navigate('/content');
      })
      .catch((cause: unknown) => {
        toast.error(describeError(cause));
        setBusy(null);
      });
  };

  const step = nextStep(
    content,
    { planning, retry: retryPlan, production: producing ? production : null, review },
    {
      onRetry,
      onRetrySetup,
      onStartPlanning,
      // The estimate is shown once more, beside the button that starts spending.
      onMarkReady: () => {
        setConfirming('ready');
      },
      onStartProduction,
      onBackToPlanning,
      onStopProduction,
      onLeaveQueue,
      onResume: () => {
        setConfirming('resume');
      },
      onReadScript: () => {
        revealPanel(content.gate === 'sketch' ? 'sketch' : 'script');
      },
      onShowNow,
      onApprove,
      onUnreject: () => {
        setConfirming('unreject');
      },
      onRetryAdPauses,
      // Opens the box in the review panel, where the findings are to point at.
      onRequestChanges: () => {
        setFeedback((previous) => previous ?? '');
        window.requestAnimationFrame(() => {
          const box = document.getElementById('review-feedback');
          box?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          box?.focus({ preventScroll: true });
        });
      },
    },
  );

  /** The one panel the main column leads with; everything else is in Details. */
  const stage = bridging ? 'sketch' : stagePanelOf(content);
  /**
   * What the plan panel in Advanced details counts as its problems, read the
   * way it reads them — the refused **Mark ready**'s errors win over the
   * poll's — so the notice outside and the badge inside agree (US-115).
   */
  const planRead = content.status === 'pending' && planning?.plan.exists === true ? planning.plan : null;
  const planErrors = planRead === null ? [] : (readyErrors ?? planRead.errors);
  const planProblems =
    planRead === null || (planRead.parses && planErrors.length === 0)
      ? 0
      : Math.max(planErrors.length, 1);
  /** Whether the run has a narration gate in front of the scenes (US-105). */
  const withNarration =
    plan === null ? script !== null : plan.steps.some((planStep) => planStep.type === 'script');

  // The automatic rounds are part of whichever stage they run in: production
  // while a round is producing the next version, review once they are done.
  const rounds =
    review !== null && (review.auto.running || review.auto.rounds.length > 0) ? (
      <RoundsSection auto={review.auto} busy={busy} onShowNow={onShowNow} />
    ) : null;

  // The share copy that ships with the version that was delivered.
  const delivered = versions?.versions.find((candidate) => candidate.current) ?? null;
  const shareCopy =
    delivered?.shareCopy == null ? null : { share: delivered.shareCopy, version: delivered.number };

  return (
    <div className="page">
      <PageHeader
        back={{ href: '/content', label: 'Content' }}
        eyebrow={
          <span className="crumbs">
            <Icon name={content.kind === 'video' ? 'rocket' : 'file'} />
            {content.kind === 'video' ? 'Video' : 'Images'}
            {content.tone !== null && (
              <>
                <span className="crumbs__sep">/</span>
                <span>{toneLabel(tones, content.tone)}</span>
              </>
            )}
            {content.brandBook !== null && (
              <>
                <span className="crumbs__sep">/</span>
                <Icon name="paintbrush" />
                <Link className="link" href={brandBookPath(content.brandBook.id)} title="Brand book">
                  {content.brandBook.name}
                </Link>
                <BrandStatusBadge status={content.brandBook.brandStatus} />
              </>
            )}
            {content.kitVersion !== null && (
              <>
                <span className="crumbs__sep">/</span>
                <span title={versionPinTitle('ad kit', content.kitVersion)}>
                  Kit v{content.kitVersion.version}
                </span>
              </>
            )}
            {content.guidelinesVersion !== null && (
              <>
                <span className="crumbs__sep">/</span>
                <span title={versionPinTitle('guidelines', content.guidelinesVersion)}>
                  Guidelines v{content.guidelinesVersion.version}
                </span>
              </>
            )}
          </span>
        }
        title={
          <>
            {content.name} <StatusBadge status={content.status} />
            {content.queuePosition !== null && (
              <Badge tone="wait">queued #{content.queuePosition}</Badge>
            )}
            {content.autopilot && (
              <Badge tone="active" title={wording.AUTOPILOT_BADGE_TITLE}>
                {wording.AUTOPILOT_BADGE}
              </Badge>
            )}
          </>
        }
        actions={
          <>
            <AutopilotButton content={content} onChanged={setContent} />
            <Link className="button button--quiet" href={boardCardPath(content.id)}>
              <Icon name="project" />
              {wording.CONTENT_VIEW_ON_BOARD}
            </Link>
            <button
              type="button"
              className="button"
              onClick={() => {
                setDuplicating(true);
              }}
            >
              <Icon name="duplicate" />
              {wording.BOARD_DUPLICATE}
            </button>
            {content.status === 'delivered' && (
              <button
                type="button"
                className="button button--danger"
                disabled={busy !== null}
                onClick={() => {
                  setConfirming('reject');
                }}
              >
                <Icon name="x-circle" />
                {wording.CONTENT_REJECT}
              </button>
            )}
            <button
              type="button"
              className="button"
              disabled={archiving.busy}
              onClick={() => void archiving.toggle(content)}
            >
              <Icon name="archive" />
              {content.archivedAt === null ? wording.BOARD_ARCHIVE : wording.BOARD_UNARCHIVE}
            </button>
            <button
              type="button"
              className="button button--quiet button--danger button--icon"
              onClick={() => {
                setConfirming('delete');
              }}
              disabled={busy !== null}
              aria-label={`Delete ${content.name}`}
              title="Delete content"
            >
              <Icon name="trash" />
            </button>
          </>
        }
      />

      <StageProgress
        content={content}
        planParses={planning?.plan.parses ?? null}
        production={producing ? production : null}
        auto={review?.auto ?? null}
      />

      <NextStep
        step={step}
        content={content}
        versions={versions}
        busy={busy}
        onRaiseCapAndRetry={onRetry}
      />

      {content.lastError !== null && !step.showsLastError && (
        <Notice kind="error">{content.lastError}</Notice>
      )}

      <div className="grid grid--main-aside">
        <div className="stack">
          {stage === 'planning' ? (
            <>
            <PlanningPanel
              content={content}
              planning={planning}
              brandBook={content.brandBook}
              failed={content.status === 'failed'}
              busy={busy}
              onStart={onStartPlanning}
              onStop={onStopPlanning}
            />
            {/* What the critic said when the planner asked it (synthesis C1). */}
            {content.kind === 'video' && (
              <>
              <CreativeNotesPanel
                contentId={content.id}
                atGate={false}
                busy={busy !== null}
              />
              {/* And on the staging (US-009); nothing while the plan stages nobody. */}
              <CreativeNotesPanel
                contentId={content.id}
                kind="staging"
                atGate={false}
                busy={busy !== null}
              />
              </>
            )}
            </>
          ) : stage === 'steps' ? (
            <StepsPanel
              plan={plan}
              current={null}
              id="steps"
              title="What will be produced"
            />
          ) : stage === 'production' ? (
            <ProductionPanel content={content} production={production} plan={plan}>
              {rounds}
            </ProductionPanel>
          ) : stage === 'sketch' ? (
            <>
            {/*
              * The critic's notes on the words (synthesis C1), above the scenes.
              * Advice only: nothing in them holds **Approve scenes**.
              */}
            <CreativeNotesPanel
              contentId={content.id}
              atGate={atSketch}
              busy={busy !== null}
            />
            {/*
              * The staging notes (US-009), advice the same way; no panel when
              * the plan stages nobody.
              */}
            <CreativeNotesPanel
              contentId={content.id}
              kind="staging"
              atGate={atSketch}
              busy={busy !== null}
            />
            <SketchPanel
              sketch={sketch}
              starting={!atSketch}
              withNarration={withNarration}
              busy={busy}
              onApprove={onApproveSketch}
              onAdjust={onAdjustSketch}
              onEditScene={onEditScene}
              onUndo={onUndoSketch}
              onTryAgain={onPrepareSketch}
            />
            </>
          ) : stage === 'script' ? (
            script === null ? (
              <Panel id="script" title="Narration script" icon="file">
                <Skeleton lines={6} />
              </Panel>
            ) : (
              <ScriptPanel
                script={script}
                busy={busy !== null}
                onSave={onSaveScript}
                onApprove={onApproveScript}
                withSteps
              />
            )
          ) : // The panel is shown for the whole of both statuses, so a content
          // that reached review with nothing rendered is told so in words
          // rather than left with a blank column.
          review === null ? (
            <Panel id="review" title="Review" icon="graph">
              <Skeleton lines={4} />
            </Panel>
          ) : (
            <ReviewPanel
              content={content}
              review={review}
              busy={busy}
              feedback={feedback}
              onFeedback={setFeedback}
              onRunReview={onRunReview}
              onApprove={onApprove}
              onRequestChanges={onRequestChanges}
              player={
                versions !== null && versions.versions.length > 0 ? (
                  <div id="player" className="review__player">
                    <Player versions={versions} />
                  </div>
                ) : undefined
              }
            >
              {rounds}
            </ReviewPanel>
          )}

          {/*
            * What the critic said about the draft video before the final
            * render (synthesis C8). Renders nothing until a compose step has
            * drawn a draft, and says whether it sent the composition back.
            */}
          {content.kind === 'video' && (stage === 'production' || stage === 'review') && (
            <CreativeNotesPanel
              contentId={content.id}
              kind="draft"
              atGate={false}
              busy={busy !== null}
            />
          )}

          {/*
            * The product facts the planner learned (synthesis C7), once it has
            * written some: renders nothing until then, and reads again when the
            * planning conversation changes, so it appears as that ends.
            */}
          <ProfileProposalPanel
            contentId={content.id}
            watch={`${content.status}:${String(planning?.running ?? '')}:${planning?.plan.updatedAt ?? ''}`}
          />

          {/*
            * A panel in Advanced details that needs the operator says so out
            * here, with a link that opens the section on it (US-115).
            */}
          {planProblems > 0 && (
            <Notice kind="warn">
              {wording.planProblemsNotice(planProblems)}{' '}
              <a
                className="notice__link"
                href="#plan"
                onClick={(event) => {
                  event.preventDefault();
                  advanced.show('plan');
                }}
              >
                {wording.SHOW_THE_PLAN}
              </a>
            </Notice>
          )}

          {/*
            * Everything that is not the stage, folded (US-011). A folded panel
            * renders no body, so the log's socket, the terminal and the
            * gallery's poll only run while their panel is open.
            */}
          <section className="details" aria-labelledby="content-details">
            <h2 className="details__title" id="content-details">
              Details
            </h2>

            {versions !== null && versions.versions.length > 0 && stage !== 'review' && (
              <VersionsPanel versions={versions} />
            )}

            {content.status === 'delivered' && shareCopy !== null && (
              <ShareCopyPanel share={shareCopy.share} version={shareCopy.version} />
            )}

            {stage !== 'script' && script !== null && script.text.trim() !== '' && (
              <ScriptPanel
                script={script}
                busy={busy !== null}
                onSave={onSaveScript}
                onApprove={onApproveScript}
                collapsible
              />
            )}

            {/*
              * Everything the production made, not only what it delivered
              * (US-056). It reads its own listing, on its own beat.
              */}
            <AssetGallery contentId={content.id} />

            {/*
              * The log outlives the run that wrote it, so a production stopped
              * yesterday and one running right now are read in the same place
              * (US-032).
              */}
            <ProductionLog contentId={content.id} producing={content.status === 'producing'} />

            {(content.brief !== null || content.creativeDirection !== null) && (
              <Panel
                id="brief"
                title="Brief"
                icon="file"
                collapsible
                defaultOpen={false}
                summary={firstLine(content.brief ?? content.creativeDirection ?? '')}
              >
                {content.brief === null ? (
                  <p className="muted">
                    No brief was written; planning starts from the conversation alone.
                  </p>
                ) : (
                  <pre className="output output--wrap">{content.brief}</pre>
                )}
                {content.creativeDirection !== null && (
                  <>
                    <p className="field__label">
                      Creative direction, refining{' '}
                      {content.tone === null ? 'the tone' : toneLabel(tones, content.tone)}
                    </p>
                    <pre className="output output--wrap">{content.creativeDirection}</pre>
                  </>
                )}
              </Panel>
            )}
          </section>
        </div>

        <aside className="stack">
          {plan !== null && plan.steps.length > 0 && (
            <EstimatePanel estimate={plan.estimate} kind={content.kind} />
          )}

          {/* The marketing side of the piece, editable in every status (US-003). */}
          <BriefDetailsPanel
            details={content.briefDetails}
            onSave={async (details) => {
              setContent(await updateBriefDetails(content.id, details));
              toast.ok('Brief details saved.');
            }}
          />

          {/* The card's fields, the same ones the board's drawer edits (US-031). */}
          <ContentBoardPanel
            content={content}
            archiving={archiving.busy}
            onUnarchive={() => void archiving.toggle(content)}
            onChanged={setContent}
          />

          <ContentPostsPanel
            content={content}
            onChanged={async () => {
              // Posting one is what places the content; a failed read waits for the poll.
              try {
                setContent(await fetchContent(content.id));
              } catch {
                // The page's own poll catches up.
              }
            }}
          />

          <Panel title="Details" icon="info">
            <Facts
              items={[
                { label: 'Kind', value: content.kind === 'video' ? 'Video' : 'Images' },
                ...(content.tone === null ? [] : [{ label: 'Tone', value: toneLabel(tones, content.tone) }]),
                {
                  label: 'Formats',
                  value:
                    content.formats.length === 0 ? (
                      <span className="muted">none</span>
                    ) : (
                      content.formats.join(', ')
                    ),
                },
                {
                  label: content.repositories.length === 1 ? 'Repository' : 'Repositories',
                  value: <RepositoryList content={content} />,
                },
                {
                  label: 'Container',
                  value: <ContainerId id={content.containerId} />,
                  mono: content.containerId !== null,
                },
                {
                  label: 'Container limits',
                  value:
                    stats === null ? (
                      <span className="muted">reading…</span>
                    ) : (
                      `${String(stats.containers.memoryGb)} GB, ${String(stats.containers.cpus)} CPU${
                        stats.containers.cpus === 1 ? '' : 's'
                      }`
                    ),
                },
                {
                  label: 'Video model',
                  value:
                    content.videoModel === null ? (
                      <span className="muted">the plan’s, or this install’s</span>
                    ) : (
                      content.videoModel
                    ),
                  mono: content.videoModel !== null,
                },
                {
                  label: 'Spend cap',
                  value:
                    content.spendCapUsd === null ? (
                      <span className="muted">none of its own</span>
                    ) : (
                      `$${content.spendCapUsd.toFixed(2)}`
                    ),
                },
                { label: 'Spent', value: `${content.spentUsd.toFixed(2)}` },
                ...(production === null || stage !== 'production'
                  ? []
                  : [
                      {
                        label: 'Iteration',
                        value: `${String(production.iteration)} of ${String(production.maxIterations)}`,
                      },
                      { label: 'Step timeout', value: elapsed(production.stepTimeoutMs) },
                      {
                        label: 'Production model',
                        value:
                          production.productionModel === null ? (
                            <span className="muted">the CLI’s own default</span>
                          ) : (
                            production.productionModel
                          ),
                        mono: production.productionModel !== null,
                      },
                      {
                        label: 'Slots',
                        value: `${String(production.activeProductions)} of ${String(
                          production.maxConcurrentProductions,
                        )} in use`,
                      },
                    ]),
                ...(content.scheduledStartAt === null
                  ? []
                  : [{ label: 'Scheduled', value: localTime(content.scheduledStartAt) }]),
                ...(content.approvedAt === null
                  ? []
                  : [{ label: 'Approved', value: localTime(content.approvedAt) }]),
                { label: 'Created', value: localTime(content.createdAt) },
                { label: 'Updated', value: localTime(content.updatedAt) },
              ]}
            />
            <p className="field__hint">
              The memory and CPU ceilings are this install's, from{' '}
              <a href="/settings#production">Settings → Production</a>, and they are applied when a
              container is <em>started</em>: a container already running for this content keeps the
              ones it was started with.
            </p>
          </Panel>
        </aside>
      </div>

      {/*
        * The technical panels, folded into one section at the bottom of the
        * page and closed until the operator opens it (US-115). A closed
        * section mounts none of them, so their reads wait until it is open.
        */}
      <AdvancedDetails open={advanced.open} onToggle={advanced.toggle}>
        {/* 'Mark ready when the plan is written' (US-012). */}
        {content.status === 'pending' && (
          <PlanPanel
            planning={planning}
            readyErrors={readyErrors}
            busy={busy}
            onMarkReady={() => {
              setConfirming('ready');
            }}
          />
        )}

        {stage !== 'steps' && (
          <StepsPanel
            plan={plan}
            current={production?.currentStepId ?? null}
            id="steps"
            title="Plan steps"
            collapsible
          />
        )}

        {plan !== null && <ClaudeUsagePanel steps={plan.steps} />}

        {/*
          * What the clips cost, per clip and per model (US-085), only for a
          * content that has any: a ledger with no rows renders nothing.
          */}
        {content.kind === 'video' && content.status !== 'pending' && (
          <ClipLedgerPanel contentId={content.id} />
        )}

        {/*
          * What the server did on the operator's behalf (one-shot ads
          * US-022): "Marked ready automatically", "Queued automatically",
          * with times. Re-read when the status moves, which is when an entry
          * is written; a content with no entries renders nothing.
          */}
        <HistoryPanel
          contentId={content.id}
          moment={`${content.status}:${String(production?.queued ?? false)}:${String(production?.running ?? false)}`}
        />

        {/*
          * The model this content generates on (US-085): a standing choice,
          * so it is a panel of its own rather than a dialog.
          */}
        {/*
          * The language the content is written and spoken in: set on the form,
          * and correctable here — the one way to give a content made before
          * contents had a language one of its own.
          */}
        <LanguagePanel content={content} onChanged={setContent} />

        {content.kind === 'video' && <VideoModelPanel content={content} onChanged={setContent} />}

        {/*
          * The library items the production pulled in (US-028), each a link
          * back to its detail on the Library page. Nothing at all for a
          * content that used none.
          */}
        <LibraryUsesPanel contentId={content.id} />

        <ContainerPanel content={content} onRetrySetup={onRetrySetup} busy={busy} />
      </AdvancedDetails>

      <DuplicateDialog
        open={duplicating}
        contentId={content.id}
        sourceName={content.name}
        onCancel={() => {
          setDuplicating(false);
        }}
        onDuplicated={(copy) => {
          setDuplicating(false);
          navigate(contentPath(copy));
        }}
      />

      <ConfirmDialog
        open={confirming === 'delete'}
        title={`Delete ${content.name}?`}
        confirmLabel="Delete content"
        busyLabel="Deleting…"
        danger
        busy={busy === 'delete'}
        onConfirm={() => {
          setConfirming(null);
          onDelete();
        }}
        onCancel={() => {
          setConfirming(null);
        }}
      >
        <DeletionWarning content={content} />
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === 'reject'}
        title={wording.rejectTitle(content.name)}
        confirmLabel={wording.CONTENT_REJECT}
        busyLabel={wording.CONTENT_REJECTING}
        danger
        busy={busy === 'reject'}
        onConfirm={() => {
          setConfirming(null);
          onReject();
        }}
        onCancel={() => {
          setConfirming(null);
        }}
      >
        <p>{wording.REJECT_BODY}</p>
        <div className="field">
          <label className="field__label" htmlFor="reject-reason">
            {wording.REJECT_REASON_LABEL}
          </label>
          <textarea
            id="reject-reason"
            className="field__textarea"
            rows={3}
            maxLength={1000}
            value={rejectReason}
            onChange={(event) => {
              setRejectReason(event.target.value);
            }}
            placeholder={wording.REJECT_REASON_PLACEHOLDER}
          />
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === 'unreject'}
        title={wording.unrejectTitle(content.name)}
        confirmLabel={wording.CONTENT_UNREJECT}
        busyLabel={wording.CONTENT_UNREJECTING}
        busy={busy === 'unreject'}
        onConfirm={() => {
          setConfirming(null);
          onUnreject();
        }}
        onCancel={() => {
          setConfirming(null);
        }}
      >
        <p>{wording.UNREJECT_BODY}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === 'ready'}
        title={`Mark ${content.name} ready?`}
        confirmLabel="Mark ready"
        busyLabel="Marking ready…"
        busy={busy === 'ready'}
        onConfirm={() => {
          setConfirming(null);
          onMarkReady();
        }}
        onCancel={() => {
          setConfirming(null);
        }}
      >
        <p>
          The plan becomes this content’s steps, and production starts from them. The plan can still
          be changed afterwards with <strong>Back to planning</strong>; nothing is spent until a
          step actually runs.
        </p>
        {plan === null ? (
          <p className="muted">Reading the plan…</p>
        ) : (
          <EstimateSummary estimate={plan.estimate} />
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === 'resume'}
        title="Resume every held content?"
        confirmLabel="Resume all"
        busyLabel="Resuming…"
        busy={busy === 'resume-hold'}
        onConfirm={() => {
          setConfirming(null);
          onResumeNow();
        }}
        onCancel={() => {
          setConfirming(null);
        }}
      >
        <p>
          <strong>This resumes every held content, not only this one.</strong> The usage limit is on
          the Claude account the whole server shares, so there is one hold; lifting it starts as many
          held contents as the production-slot cap allows, with the rest going on the queue in the
          order they were held.
        </p>
        <p>
          If the limit has in fact not lifted, the first agent to ask is refused again and a fresh
          hour begins. Nothing is lost either way: a refused step costs no attempt.
        </p>
      </ConfirmDialog>
    </div>
  );
}

/** Which panel leads the main column (US-011). */
type StagePanel = 'planning' | 'steps' | 'production' | 'script' | 'sketch' | 'review';

/**
 * The stage panel for a content's status. A failed content shows the panel of
 * the stage it failed in — the same reading of `failureStage` as the stage
 * bar's — with the next-step card above it saying what went wrong.
 */
function stagePanelOf(content: ContentData): StagePanel {
  switch (content.status) {
    case 'pending':
      return 'planning';
    case 'ready':
      return 'steps';
    case 'producing':
    case 'waiting':
      return 'production';
    case 'approval':
      return content.gate === 'sketch' ? 'sketch' : 'script';
    // A rejected content keeps the delivered review, which is where it was rejected.
    case 'review':
    case 'delivered':
    case 'rejected':
      return 'review';
    case 'failed':
      switch (content.failureStage) {
        case 'setup':
          return 'planning';
        case 'plan':
          return 'steps';
        case 'review':
          return 'review';
        default:
          return 'production';
      }
  }
}

type Busy =
  | 'approve'
  | 'script'
  | 'sketch'
  | 'show-now'
  | 'review'
  | 'revise'
  | 'setup'
  | 'planning'
  | 'ready'
  | 'back-to-planning'
  | 'production'
  | 'resume-hold'
  | 'retry'
  | 'delete'
  | 'reject'
  | 'unreject'
  | 'retry-pauses'
  | null;

/**
 * What the server is doing while an action is in flight, for the `Loader`
 * the panel shows beside the busy button (US-010). The button still reads
 * "Working…"; this sentence says what the work is.
 */
const BUSY_LOADER: Partial<Record<NonNullable<Busy>, string>> = {
  approve: wording.REVIEW_DELIVERING,
  sketch: 'Talking to the production loop about the sketch…',
  'show-now': 'Stopping the rounds after the step running now…',
  review: wording.REVIEW_CHECKING,
  revise: wording.REVIEW_REVISING,
  setup: 'Setting up the container and cloning the repositories…',
  planning: 'Talking to the planning conversation…',
  ready: 'Reading the plan and checking every step…',
  'back-to-planning': 'Moving the content back to planning…',
  production: 'Talking to the production loop…',
  'resume-hold': 'Lifting the usage-limit hold…',
  retry: 'Retrying: setting up again or resuming production…',
  'retry-pauses': wording.AD_PAUSE_RETRYING,
};

/* -------------------------------------------------------- advanced details */

/** Where the section's open or closed choice is remembered, per browser. */
const ADVANCED_KEY = 'mc.content.advanced-details';

/** The ids of the panels the section holds, so a `#<id>` link can open it. */
const ADVANCED_PANELS: readonly string[] = [
  'plan',
  'steps',
  'claude-usage',
  'clips',
  'video-model',
  'from-the-library',
  'container',
];

/** Storage can throw (a private window, a full quota); closed stands in. */
function readAdvancedOpen(): boolean {
  try {
    return window.localStorage.getItem(ADVANCED_KEY) === 'open';
  } catch {
    return false;
  }
}

function writeAdvancedOpen(open: boolean): void {
  try {
    window.localStorage.setItem(ADVANCED_KEY, open ? 'open' : 'closed');
  } catch {
    // Not remembered this time; the section still opens and closes.
  }
}

/** A `#<id>` in the address bar that names one of the section's panels. */
function advancedPanelInHash(): string | null {
  const named = window.location.hash.slice(1);
  return ADVANCED_PANELS.includes(named) ? named : null;
}

/**
 * The Advanced details section's state (US-115): closed unless the operator
 * left it open in this browser, or the address names a panel inside it.
 *
 * `show(id)` opens the section *and then* reveals the panel. The panels of a
 * closed section are not mounted, so revealing one in the same event would
 * find nothing to unfold or scroll to; the reveal waits for the render that
 * mounts it.
 */
function useAdvancedDetails(): {
  readonly open: boolean;
  readonly toggle: () => void;
  readonly show: (id: string) => void;
} {
  const [open, setOpen] = useState(() => advancedPanelInHash() !== null || readAdvancedOpen());
  const [reveal, setReveal] = useState<string | null>(null);

  const show = (id: string): void => {
    setOpen(true);
    writeAdvancedOpen(true);
    setReveal(id);
  };

  useEffect(() => {
    if (!open || reveal === null) return;
    revealPanel(reveal);
    setReveal(null);
  }, [open, reveal]);

  // A link on this page to `#container` and the like, after it has loaded.
  useEffect(() => {
    const onHash = (): void => {
      const named = advancedPanelInHash();
      if (named === null) return;
      setOpen(true);
      setReveal(named);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return {
    open,
    toggle: () => {
      const next = !open;
      setOpen(next);
      writeAdvancedOpen(next);
    },
    show,
  };
}

/**
 * The technical panels, in one section at the bottom of the page (US-115).
 * Closed, it renders only its heading, so none of the panels inside reads
 * anything or opens a socket until the operator asks for them.
 */
function AdvancedDetails({
  open,
  onToggle,
  children,
}: {
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly children: ReactNode;
}) {
  const bodyId = 'advanced-details-body';
  return (
    <section
      className={open ? 'details advanced advanced--open' : 'details advanced'}
      id="advanced-details"
      aria-labelledby="advanced-details-title"
    >
      <h2 className="details__title advanced__title" id="advanced-details-title">
        <button
          type="button"
          className="panel__toggle advanced__toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={onToggle}
        >
          <Icon name="chevron-right" className="panel__chevron" />
          {wording.ADVANCED_DETAILS}
          {!open && <span className="panel__summary">{wording.ADVANCED_DETAILS_HINT}</span>}
        </button>
      </h2>
      {open && (
        <div className="details advanced__body" id={bodyId}>
          {children}
        </div>
      )}
    </section>
  );
}

/** The `Loader` for `busy` when it is one of `kinds`, or nothing. */
function BusyLoader({
  busy,
  kinds,
  label,
}: {
  readonly busy: Busy;
  readonly kinds: readonly NonNullable<Busy>[];
  /** Overrides the sentence in `BUSY_LOADER`. */
  readonly label?: string;
}) {
  if (busy === null || !kinds.includes(busy)) return null;
  const sentence = label ?? BUSY_LOADER[busy];
  return sentence === undefined ? null : <Loader label={sentence} size="sm" />;
}

/* ------------------------------------------------------------ the next step */

/** One button of the next-step card. */
interface StepAction {
  /** The `Busy` this action sets, so its own label is the one that reads "Working…". */
  readonly kind: NonNullable<Busy>;
  readonly label: string;
  readonly icon: IconName;
  /** Absent while the action cannot be taken; `hint` then says why. */
  readonly onClick?: () => void;
  readonly hint?: string;
}

/**
 * What the content is doing, and the one thing to do about it (US-009).
 *
 * Every state has exactly one primary action — that is the whole design of
 * this page — and at most one secondary. `download` and `raise-cap` are the
 * two primaries that are not a plain button: a link to the current render,
 * and the cap field a budget failure is retried through.
 */
interface NextStepView {
  readonly icon: IconName;
  readonly sentence: string;
  readonly explanation?: ReactNode;
  /** The server is working on it; nothing is asked of the operator. */
  readonly loader: boolean;
  /** The content waits on the operator, which is what the amber edge says. */
  readonly waitsOnYou: boolean;
  readonly primary: StepAction | 'download' | 'raise-cap';
  readonly secondary?: StepAction;
  /** The card already shows `lastError`, so the page's own notice stays away. */
  readonly showsLastError: boolean;
}

interface NextStepHandlers {
  readonly onRetry: () => void;
  readonly onRetrySetup: () => void;
  readonly onStartPlanning: () => void;
  readonly onMarkReady: () => void;
  readonly onStartProduction: () => void;
  readonly onBackToPlanning: () => void;
  readonly onStopProduction: () => void;
  readonly onLeaveQueue: () => void;
  readonly onResume: () => void;
  readonly onReadScript: () => void;
  readonly onShowNow: () => void;
  readonly onApprove: () => void;
  readonly onRequestChanges: () => void;
  readonly onUnreject: () => void;
  readonly onRetryAdPauses: () => void;
}

function nextStep(
  content: ContentData,
  read: {
    readonly planning: Planning | null;
    readonly retry: RetryPlan | null;
    readonly production: Production | null;
    readonly review: ReviewView | null;
  },
  handlers: NextStepHandlers,
): NextStepView {
  const { planning, retry, production, review } = read;
  const version = String(content.currentVersion);

  // An automatic round produces the next version, so the content reads
  // `producing` while it runs; what the operator is waiting for is a review.
  const auto = review?.auto ?? null;
  if (auto !== null && auto.running && content.status !== 'delivered') {
    return {
      icon: 'sync',
      sentence: `Reviewing version ${version}: round ${String(Math.max(auto.round, 1))} of ${String(
        auto.limit,
      )}. Nothing is needed from you.`,
      ...(auto.phaseLabel === null ? {} : { explanation: auto.phaseLabel }),
      loader: true,
      waitsOnYou: false,
      primary: {
        kind: 'show-now',
        label: wording.SHOW_ME_NOW,
        icon: 'check-circle',
        ...(auto.canShowNow
          ? { onClick: handlers.onShowNow, hint: wording.SHOW_ME_NOW_HINT }
          : { hint: wording.SHOW_ME_NOW_STOPPING }),
      },
      showsLastError: false,
    };
  }

  switch (content.status) {
    case 'pending': {
      const missed = content.scheduleMissed ? (
        <p>
          <strong>Missed its scheduled start.</strong> This content was still being planned at{' '}
          {localTime(content.scheduledStartAt ?? '')}, so nothing ran.
        </p>
      ) : undefined;
      const withMissed = missed === undefined ? {} : { explanation: missed };
      // Setup has to have run before there is a workspace to plan in, so a
      // content whose clone failed is asked to fix that first.
      if (content.lastError !== null || content.containerId === null) {
        return {
          icon: 'alert',
          sentence: content.lastError === null ? wording.SETUP_NOT_RUN : wording.SETUP_FAILED,
          ...(content.lastError === null
            ? withMissed
            : {
                explanation: (
                  <>
                    {missed}
                    <TechnicalDetails>
                      <pre className="output output--wrap">{content.lastError}</pre>
                    </TechnicalDetails>
                  </>
                ),
              }),
          loader: false,
          waitsOnYou: true,
          primary: { kind: 'setup', label: wording.RETRY_SETUP, icon: 'sync', onClick: handlers.onRetrySetup },
          showsLastError: true,
        };
      }
      // A plan that parses is finished work waiting to be accepted.
      if (planning?.plan.parses === true) {
        return {
          icon: 'check-circle',
          sentence: wording.PLAN_WRITTEN,
          ...withMissed,
          loader: false,
          waitsOnYou: true,
          primary: { kind: 'ready', label: 'Mark ready', icon: 'check', onClick: handlers.onMarkReady },
          showsLastError: false,
        };
      }
      const problems = planning?.plan.exists === true ? planning.plan.errors.length : 0;
      if (problems > 0) {
        return {
          icon: 'alert',
          sentence: wording.planProblemsNotice(problems),
          ...withMissed,
          loader: false,
          waitsOnYou: true,
          primary: {
            kind: 'planning',
            label: 'Open planning',
            icon: 'terminal',
            onClick: handlers.onStartPlanning,
          },
          showsLastError: false,
        };
      }
      return {
        icon: 'terminal',
        sentence: 'Plan this content with Claude.',
        ...withMissed,
        loader: false,
        waitsOnYou: true,
        primary: {
          kind: 'planning',
          label: planning?.nextMode === 'edit' ? 'Resume planning' : 'Start planning',
          icon: 'terminal',
          onClick: handlers.onStartPlanning,
        },
        showsLastError: false,
      };
    }
    case 'ready':
      // Already waiting for a slot: it starts by itself when one frees, so the
      // only thing left to decide is whether it should still be waiting at all.
      if (content.queuePosition !== null) {
        return {
          icon: 'clock',
          sentence: wording.waitingInLine(content.queuePosition),
          loader: false,
          waitsOnYou: false,
          primary: { kind: 'production', label: 'Leave queue', icon: 'stop', onClick: handlers.onLeaveQueue },
          showsLastError: false,
        };
      }
      return {
        icon: 'play',
        sentence: wording.READY_TO_MAKE,
        ...(content.scheduledStartAt === null
          ? {}
          : {
              explanation: `Scheduled for ${localTime(content.scheduledStartAt)}: it ${startsIn(
                content.scheduledStartAt,
              )} by itself, or now if you start it.`,
            }),
        loader: false,
        waitsOnYou: content.scheduledStartAt === null,
        primary: {
          kind: 'production',
          label: 'Start production',
          icon: 'play',
          onClick: handlers.onStartProduction,
        },
        secondary: {
          kind: 'back-to-planning',
          label: wording.BACK_TO_PLANNING,
          icon: 'terminal',
          onClick: handlers.onBackToPlanning,
          hint: wording.BACK_TO_PLANNING_HINT,
        },
        showsLastError: false,
      };
    case 'producing': {
      const steps = production?.steps ?? [];
      const current = steps.find((step) => step.stepId === production?.currentStepId) ?? null;
      const total = steps.length > 0 ? steps.length : content.steps.total;
      const done = steps.length > 0 ? steps.filter((step) => step.status === 'done').length : content.steps.done;
      const at = current === null ? Math.min(done + 1, total) : steps.indexOf(current) + 1;
      return {
        icon: 'sync',
        sentence: total === 0 ? wording.MAKING : wording.makingAt(at, total, current?.title ?? null),
        loader: true,
        waitsOnYou: false,
        primary: {
          kind: 'production',
          label: 'Stop production',
          icon: 'stop',
          onClick: handlers.onStopProduction,
        },
        showsLastError: false,
      };
    }
    case 'waiting':
      // Claude's usage-limit hold is the only thing that puts a content here.
      return {
        icon: 'pause',
        sentence: `On hold until ${
          content.waitingUntil === null ? 'the limit lifts' : hourMinute(content.waitingUntil)
        } because Claude’s usage limit was reached. Nothing is needed from you.`,
        explanation: <HoldExplanation until={content.waitingUntil} />,
        loader: false,
        waitsOnYou: false,
        primary: { kind: 'resume-hold', label: 'Resume now', icon: 'play', onClick: handlers.onResume },
        showsLastError: true,
      };
    case 'approval':
      // The decision itself is in the sketch panel, where the scenes are.
      if (content.gate === 'sketch') {
        return {
          icon: 'eye',
          sentence: 'The scenes are ready. Look them over and approve them before the video is made.',
          loader: false,
          waitsOnYou: true,
          primary: {
            kind: 'sketch',
            label: 'Look at the scenes',
            icon: 'eye',
            onClick: handlers.onReadScript,
          },
          showsLastError: false,
        };
      }
      // The decision itself is in the script panel, where the words are.
      return {
        icon: 'file',
        sentence: 'The narration is written. Read it and approve it before it is spoken.',
        loader: false,
        waitsOnYou: true,
        primary: {
          kind: 'script',
          label: 'Read the narration',
          icon: 'file',
          onClick: handlers.onReadScript,
        },
        showsLastError: false,
      };
    case 'review': {
      const blocking = review?.blocking ?? 0;
      return {
        icon: 'eye',
        sentence: `Version ${version} is ready for you.`,
        ...(review?.blocked == null
          ? blocking > 0
            ? { explanation: wording.reviewBlocking(blocking) }
            : {}
          : { explanation: review.blocked }),
        loader: false,
        waitsOnYou: true,
        primary: {
          kind: 'approve',
          label: wording.approveVersion(content.currentVersion),
          icon: 'check',
          ...(review === null || review.canApprove
            ? { onClick: handlers.onApprove }
            : { hint: review.blocked ?? wording.CANNOT_APPROVE_YET }),
        },
        secondary: {
          kind: 'revise',
          label: wording.REQUEST_CHANGES,
          icon: 'comment',
          onClick: handlers.onRequestChanges,
        },
        showsLastError: false,
      };
    }
    case 'delivered':
      return {
        icon: 'check-circle',
        sentence: wording.DELIVERED_SENTENCE,
        ...(content.approvedAt === null
          ? {}
          : {
              explanation: wording.deliveredOn(localTime(content.approvedAt)),
            }),
        loader: false,
        waitsOnYou: false,
        primary: 'download',
        // Changes after delivery make the next version, reviewed and approved
        // again; the approved render stays in the library.
        secondary: {
          kind: 'revise',
          label: wording.REQUEST_CHANGES,
          icon: 'comment',
          onClick: handlers.onRequestChanges,
        },
        showsLastError: false,
      };
    case 'rejected':
      return {
        icon: 'x-circle',
        sentence: wording.REJECTED_SENTENCE,
        explanation: <Rejection content={content} onRetryAdPauses={handlers.onRetryAdPauses} />,
        loader: false,
        // Ads Meta would not pause are still spending: that one is on the operator.
        waitsOnYou: content.adPause?.state === 'failed',
        primary: {
          kind: 'unreject',
          label: wording.CONTENT_UNREJECT,
          icon: 'sync',
          onClick: handlers.onUnreject,
        },
        showsLastError: false,
      };
    case 'failed': {
      // One plain sentence and one button; the server's reading of the stage,
      // the error it stored and what the retry will do are behind Show details.
      return {
        icon: 'x-circle',
        sentence: wording.failureSentence(content.failureStage),
        explanation: (
          <TechnicalDetails>
            {retry !== null && retry.stageMeaning !== '' && <p>{retry.stageMeaning}</p>}
            <pre className="output output--wrap">{content.lastError ?? wording.NO_ERROR_RECORDED}</pre>
            {retry !== null && <RetryPlanView plan={retry} />}
          </TechnicalDetails>
        ),
        loader: false,
        waitsOnYou: true,
        // A budget failure stops in the same place on every retry until the
        // cap moves, so its one action is to move it and retry in one press.
        primary:
          content.failureStage === 'budget'
            ? 'raise-cap'
            : {
                kind: 'retry',
                label: wording.retryLabel(retry?.action ?? null),
                icon: 'sync',
                // Enabled until the server says otherwise: a plan that has not
                // arrived yet must not make the page look like a dead end.
                ...(retry === null || retry.retryable ? { onClick: handlers.onRetry } : {}),
                ...(retry?.blocked == null ? {} : { hint: retry.blocked }),
              },
        showsLastError: true,
      };
    }
  }
}

/**
 * The card under the progress bar: a sentence on what the content is doing,
 * an optional line on why, and the action to take (US-009). It is the only
 * place the page's primary action lives.
 */
function NextStep({
  step,
  content,
  versions,
  busy,
  onRaiseCapAndRetry,
}: {
  readonly step: NextStepView;
  readonly content: ContentData;
  readonly versions: Versions | null;
  readonly busy: Busy;
  readonly onRaiseCapAndRetry: () => void;
}) {
  return (
    <section
      className={`next-step${step.waitsOnYou ? ' next-step--wait' : ''}`}
      aria-label="Next step"
    >
      {step.loader ? (
        <div className="next-step__status">
          <Loader label={step.sentence} />
        </div>
      ) : (
        <p className="next-step__status" role="status">
          <Icon name={step.icon} />
          <span>{step.sentence}</span>
        </p>
      )}
      {step.explanation !== undefined && (
        <div className="next-step__explanation">{step.explanation}</div>
      )}
      <div className="next-step__actions">
        {step.primary === 'download' ? (
          <DownloadCurrent content={content} versions={versions} />
        ) : step.primary === 'raise-cap' ? (
          <RaiseCapAndRetry content={content} disabled={busy !== null} onRaised={onRaiseCapAndRetry} />
        ) : (
          <StepButton action={step.primary} busy={busy} primary />
        )}
        {step.secondary !== undefined && <StepButton action={step.secondary} busy={busy} />}
      </div>
      {!step.loader && (
        <BusyLoader
          busy={busy}
          kinds={[
            // **Raise cap and retry** saves the cap, then runs the one Retry.
            ...(step.primary === 'raise-cap'
              ? (['retry'] as const)
              : typeof step.primary === 'object'
                ? [step.primary.kind]
                : []),
            // **Request changes** only opens the box in the review panel;
            // the submit is there, and so is its Loader.
            ...(step.secondary === undefined || step.secondary.kind === 'revise'
              ? []
              : [step.secondary.kind]),
          ]}
        />
      )}
    </section>
  );
}

function StepButton({
  action,
  busy,
  primary = false,
}: {
  readonly action: StepAction;
  readonly busy: Busy;
  readonly primary?: boolean;
}) {
  return (
    <button
      type="button"
      className={primary ? 'button button--primary' : 'button'}
      onClick={action.onClick}
      disabled={action.onClick === undefined || busy !== null}
      title={action.hint}
    >
      <Icon name={action.icon} />
      {busy !== null && busy === action.kind ? 'Working…' : action.label}
    </button>
  );
}

/**
 * **Download** on a delivered content: the current version's render, or its
 * first variant for an image content with no single render. While the file has
 * no URL yet the button scrolls to the player, where the downloads are.
 */
/**
 * When and why a content was rejected, and how pausing its agent's Meta ads
 * went (rejected-status US-006): pausing, done, or the ads Meta would not
 * pause with what Meta said, and **Try pausing again**. The page's own
 * three-second read of the content is what moves `pausing` on.
 */
function Rejection({
  content,
  onRetryAdPauses,
}: {
  readonly content: ContentData;
  readonly onRetryAdPauses: () => void;
}) {
  const pause = content.adPause;
  return (
    <div className="stack">
      {content.rejectedAt !== null && <p>{wording.rejectedOn(localTime(content.rejectedAt))}</p>}
      {content.rejectionReason !== null && (
        <p>
          <strong>{wording.REJECTION_REASON}:</strong> {content.rejectionReason}
        </p>
      )}
      {pause?.state === 'pausing' && <Loader label={wording.AD_PAUSE_PAUSING} size="sm" />}
      {pause?.state === 'done' && <p>{wording.AD_PAUSE_DONE}</p>}
      {pause?.state === 'failed' && (
        <Notice kind="error">
          <p>{wording.AD_PAUSE_FAILED}</p>
          <ul>
            {pause.failures.map((failure) => (
              <li key={failure.adMetaId}>
                <strong>{failure.adName}</strong>: {failure.message}
              </li>
            ))}
          </ul>
          <button type="button" className="button button--small" onClick={onRetryAdPauses}>
            <Icon name="sync" />
            {wording.AD_PAUSE_RETRY}
          </button>
        </Notice>
      )}
    </div>
  );
}

function DownloadCurrent({
  content,
  versions,
}: {
  readonly content: ContentData;
  readonly versions: Versions | null;
}) {
  const version =
    versions?.versions.find((candidate) => candidate.current) ?? versions?.versions[0] ?? null;
  const file = version === null ? null : (version.render ?? version.variants[0] ?? null);
  const urls = useAssetUrls(content.id, [file?.assetId ?? null]);
  const url = file?.assetId == null ? null : (urls.get(file.assetId)?.url ?? null);
  if (file === null || url === null) {
    return (
      <button
        type="button"
        className="button button--primary"
        onClick={() => {
          revealPanel('player');
        }}
        title={wording.DOWNLOAD_NOT_YET}
      >
        <Icon name="file" />
        {wording.DOWNLOAD}
      </button>
    );
  }
  return (
    <a className="button button--primary" href={url} download={file.name}>
      <Icon name="file" />
      {wording.DOWNLOAD}
    </a>
  );
}



/**
 * Setup is offered while the plan has not been written — the clone is what the
 * planning conversation reads — and on a content that failed at setup. It is
 * safe to run more than once: a clone that succeeded is reused, not repeated.
 */
function canRetrySetup(content: ContentData): boolean {
  if (content.status === 'pending') return true;
  return content.status === 'failed' && content.failureStage === 'setup';
}

/**
 * **Raise cap and retry** (US-061).
 *
 * Only on a content that failed at stage `budget`, because that is the only
 * failure this changes anything about: the gateway refused a provider call,
 * the step stopped, and every retry stops in the same place until this number
 * moves. The field is pre-filled with a cap that has room in it rather than
 * with the current one, so the ordinary case is one press.
 *
 * The cap is raised and the retry is then the ordinary retry — the same
 * `POST /api/contents/:id/retry` the button beside it runs — so nothing here
 * is a second way of resuming a production.
 */
function RaiseCapAndRetry({
  content,
  disabled,
  onRaised,
}: {
  readonly content: ContentData;
  readonly disabled: boolean;
  readonly onRaised: () => void;
}) {
  const [cap, setCap] = useState(() => suggestedCap(content).toFixed(2));
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setWorking(true);
    setError(null);
    raiseSpendCap(content.id, Number(cap))
      .then(() => {
        onRaised();
      })
      .catch((cause: unknown) => {
        setError(describeError(cause));
      })
      .finally(() => {
        setWorking(false);
      });
  };

  return (
    <form className="raise-cap" onSubmit={submit}>
      <label className="field__label" htmlFor="raise-cap">
        New spend cap (USD)
      </label>
      <input
        id="raise-cap"
        className="field__input field__input--narrow"
        type="number"
        min={0.01}
        max={10000}
        step={0.01}
        value={cap}
        onChange={(event) => {
          setCap(event.target.value);
        }}
      />
      <button className="button button--primary" type="submit" disabled={disabled || working}>
        {working ? 'Raising…' : 'Raise cap and retry'}
      </button>
      <span className="muted">
        {content.spendCapUsd === null
          ? `${money(content.spentUsd)} spent so far, under the default cap.`
          : `${money(content.spentUsd)} of ${money(content.spendCapUsd)} spent so far.`}{' '}
        The monthly cap is in Settings → Spend.
      </span>
      {error !== null && <Notice kind="error">{error}</Notice>}
    </form>
  );
}

/**
 * A cap with room in it: five dollars past whichever of the cap and what has
 * been spent is higher, rounded to the dollar. It is a starting point the
 * operator overtypes, not a recommendation — but it is never a number that
 * would be refused again the moment it is saved.
 */
function suggestedCap(content: ContentData): number {
  return Math.ceil(Math.max(content.spendCapUsd ?? 0, content.spentUsd) + 5);
}

/**
 * What pressing **Retry** will do, in the server's own words (US-034).
 *
 * Every sentence here comes off `GET /api/contents/:id/retry` — the same
 * function the `POST` runs — so what the operator reads before pressing the
 * button is what the button does, rather than the page's own account of it.
 */
function RetryPlanView({ plan }: { readonly plan: RetryPlan }) {
  if (!plan.retryable) {
    return <p className="retry-plan__blocked">{plan.blocked}</p>;
  }
  return (
    <div className="retry-plan">
      <p className="retry-plan__summary">
        <strong>{plan.label} will:</strong> {plan.summary}
      </p>
      <ul className="retry-plan__list">
        {plan.willDo.map((line) => (
          <li key={line} className="retry-plan__item">
            {line}
          </li>
        ))}
      </ul>
      {plan.warning !== null && <p className="retry-plan__warning">{plan.warning}</p>}
    </div>
  );
}

/* ------------------------------------------- the review pass and the decision */

/** How each severity is drawn and named; the order the panel groups them in. */
const SEVERITY: Record<
  ReviewSeverity,
  { readonly label: string; readonly tone: 'danger' | 'wait' | 'neutral' }
> = {
  block: { label: wording.FINDING_GROUP.block, tone: 'danger' },
  warning: { label: wording.FINDING_GROUP.warning, tone: 'wait' },
  note: { label: wording.FINDING_GROUP.note, tone: 'neutral' },
};

/**
 * The review pass beside **Approve** (US-043).
 *
 * The decision comes first (US-014): the version to watch, then **Approve
 * version n** and **Request changes**, then the findings that are the
 * evidence. They stay on one panel — PLAN §19 is explicit that delivery is
 * something the operator decides *with* evidence — but a gate is one screen,
 * so the buttons are not at the bottom of a scroll.
 *
 * Every sentence that states a rule — why a pass cannot be run, what an
 * approval will do — comes from the server, because the server is what
 * enforces them. The page renders strings and groups rows.
 */
function ReviewPanel({
  content,
  review,
  busy,
  feedback,
  onFeedback,
  onRunReview,
  onApprove,
  onRequestChanges,
  player,
  children,
}: {
  readonly content: ContentData;
  readonly review: ReviewView;
  /** The player for the current version, which the panel opens with. */
  readonly player?: ReactNode;
  /** The automatic rounds, which belong to this stage. */
  readonly children?: ReactNode;
  readonly busy: Busy;
  /** The **Request changes** text, or `null` while the box is closed. */
  readonly feedback: string | null;
  readonly onFeedback: (text: string | null) => void;
  readonly onRunReview: () => void;
  readonly onApprove: () => void;
  readonly onRequestChanges: () => void;
}) {
  // A rejected content was delivered first: its review reads as delivered,
  // with nothing left to approve, and no changes are made to it.
  const rejected = content.status === 'rejected';
  const delivered = content.status === 'delivered' || rejected;
  const report = review.report;
  // The revision asked of this version, while it runs or after it failed: it
  // runs on after **Request changes** answered, so the poll is how it shows.
  const latest = review.revisions.find((entry) => entry.version === review.version) ?? null;
  // The rules a revision taught are learned in the background once it starts,
  // so they are read again whenever a revision moves on.
  const taught = useTaughtRules(
    content.brandBook?.id ?? null,
    content.id,
    review.revisions.map((entry) => `${String(entry.version)}:${String(entry.producedVersion)}:${String(entry.state)}`).join(),
  );
  const revising = latest?.state === 'running';
  const revisionFailed = latest?.state === 'failed' ? latest : null;
  // The findings about your words are listed once, as suggestions, above the rest.
  const suggested = new Set(review.suggestions.map((finding) => finding.id));
  const groups = REVIEW_SEVERITIES.map((severity) => ({
    severity,
    findings: (report?.findings ?? []).filter(
      (finding) => finding.severity === severity && !suggested.has(finding.id),
    ),
  })).filter((group) => group.findings.length > 0);

  return (
    <Panel
      id="review"
      title={delivered ? wording.DELIVERED : wording.REVIEW}
      icon={delivered ? 'check-circle' : 'graph'}
      meta={
        report === null ? undefined : (
          <Badge tone={report.shippable ? 'done' : 'wait'}>
            {report.shippable ? wording.REVIEW_GOOD : wording.REVIEW_NEEDS_CHANGES}
          </Badge>
        )
      }
      actions={
        review.canReview ? (
          <button
            type="button"
            className="button button--small"
            onClick={onRunReview}
            disabled={busy !== null}
            title={wording.CHECK_VIDEO_HINT}
          >
            <Icon name="sync" />
            {busy === 'review'
              ? wording.CHECKING_VIDEO
              : report === null
                ? wording.CHECK_VIDEO
                : wording.CHECK_VIDEO_AGAIN}
          </button>
        ) : undefined
      }
    >
      {player}

      {revising && <Loader label={wording.revisionRunning(review.version + 1)} size="sm" />}
      {revisionFailed !== null && (
        <Notice kind="error">{wording.revisionFailed(review.version + 1, revisionFailed.message ?? '')}</Notice>
      )}

      <div className="review__decide">
        {delivered ? (
          <p className="review__decide-hint">
            {wording.deliveredOn(review.approvedAt === null ? '' : localTime(review.approvedAt))}
          </p>
        ) : (
          <button
            type="button"
            className="button button--primary"
            onClick={onApprove}
            disabled={busy !== null || revising || !review.canApprove}
          >
            <Icon name="check" />
            {busy === 'approve' ? wording.WORKING : wording.approveVersion(review.version)}
          </button>
        )}
        {!rejected && (
          <button
            type="button"
            className="button"
            onClick={() => {
              onFeedback(feedback === null ? '' : null);
            }}
            disabled={busy !== null || revising}
            aria-expanded={feedback !== null}
            aria-controls="review-feedback-box"
          >
            <Icon name="comment" />
            {wording.REQUEST_CHANGES}
          </button>
        )}
        {!delivered && (
          <p className="review__decide-hint">
            {review.blocking > 0 ? wording.reviewBlocking(review.blocking) : wording.REVIEW_APPROVING}
          </p>
        )}
        {feedback !== null && !rejected && (
          <div className="review__feedback field" id="review-feedback-box">
            <label className="field__label" htmlFor="review-feedback">
              {wording.REVIEW_CHANGES_LABEL}
            </label>
            <textarea
              id="review-feedback"
              className="field__textarea"
              rows={4}
              value={feedback}
              onChange={(event) => {
                onFeedback(event.target.value);
              }}
              disabled={busy === 'revise'}
              placeholder={wording.REVIEW_CHANGES_PLACEHOLDER}
            />
            <p className="field__hint">{wording.reviewChangesHint(review.version)}</p>
            <div className="field__actions">
              <button
                type="button"
                className="button"
                onClick={onRequestChanges}
                disabled={busy !== null || feedback.trim() === ''}
              >
                {busy === 'revise' ? wording.WORKING : wording.requestChangesAndMake(review.version + 1)}
              </button>
            </div>
            <BusyLoader busy={busy} kinds={['revise']} />
          </div>
        )}
      </div>

      {review.blocked !== null && <Notice kind="info">{review.blocked}</Notice>}
      <BusyLoader busy={busy} kinds={['review', 'approve']} />

      {report === null ? (
        <p className="muted">
          {review.canReview ? wording.REVIEW_NOT_CHECKED : wording.REVIEW_NO_FINDINGS_STORED}
        </p>
      ) : (
        <>
          <p className="review__verdict">{report.verdict}</p>
          <div className="review__counts">
            {REVIEW_SEVERITIES.map((severity) => (
              <Badge key={severity} tone={SEVERITY[severity].tone}>
                {review.counts[severity]} {SEVERITY[severity].label.toLowerCase()}
              </Badge>
            ))}
            <Badge tone="neutral">{wording.framesChecked(report.framesInspected)}</Badge>
          </div>

          <ReviewScores report={report} />

          {review.suggestions.length > 0 && (
            <div className="review__group">
              <div className="review__group-title">{wording.REVIEW_SUGGESTIONS}</div>
              <p className="review__decide-hint">{wording.REVIEW_SUGGESTIONS_HINT}</p>
              <ul className="review__findings">
                {review.suggestions.map((finding) => (
                  <Finding key={finding.id} finding={finding} />
                ))}
              </ul>
            </div>
          )}

          {groups.length === 0 ? (
            <p className="muted">{wording.REVIEW_NO_FINDINGS}</p>
          ) : (
            groups.map((group) => (
              <div className="review__group" key={group.severity}>
                <div className="review__group-title">{SEVERITY[group.severity].label}</div>
                <ul className="review__findings">
                  {group.findings.map((finding) => (
                    <Finding key={finding.id} finding={finding} />
                  ))}
                </ul>
              </div>
            ))
          )}

          {report.evidence !== undefined && (report.evidence.lines.length > 0 || report.evidence.gaps.length > 0) && (
            <details className="review__checked">
              <summary>{wording.REVIEW_MEASURED}</summary>
              <ul className="review__checked-list">
                {report.evidence.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
                {report.evidence.gaps.map((gap) => (
                  <li key={gap} className="muted">
                    {wording.notMeasured(gap)}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {report.checked.length > 0 && (
            <details className="review__checked">
              <summary>{wording.checkedClean(report.checked.length)}</summary>
              <ul className="review__checked-list">
                {report.checked.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </details>
          )}

          <Facts
            items={[
              { label: wording.REVIEW_VERSION, value: String(report.version) },
              { label: wording.REVIEW_CHECKED_AT, value: localTime(report.reviewedAt) },
              {
                label: wording.REVIEW_MODEL,
                value: report.model ?? wording.REVIEW_MODEL_DEFAULT,
                mono: report.model !== null,
              },
            ]}
          />
        </>
      )}

      {review.probeWarnings.length > 0 && (
        <details className="review__checked">
          <summary>{wording.probeWarnings(review.probeWarnings.length)}</summary>
          <ul className="review__checked-list">
            {review.probeWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </details>
      )}

      {review.revisions.length > 0 && (
        <div className="review__history">
          <div className="review__group-title">{wording.REVIEW_HISTORY}</div>
          <ol className="review__revisions">
            {review.revisions.map((entry) => (
              <li className="review__revision" key={entry.version}>
                <div className="review__meta">
                  <span>{wording.versionTitle(entry.version)}</span>
                  <span>{wording.revisionMade(entry.producedVersion, entry.state)}</span>
                  {entry.producedAt !== null && <span>{localTime(entry.producedAt)}</span>}
                </div>
                <div className="review__revision-feedback">{entry.feedback}</div>
                {entry.source === 'operator' && (
                  <SavedAsHouseRules rules={taught.byVersion(entry.version)} busy={taught.busy} onDelete={taught.remove} />
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {children}
    </Panel>
  );
}

/**
 * The render's scores, one line per axis, in plain words (synthesis S6): what
 * the review judged the ad on, before the defects it found.
 */
function ReviewScores({ report }: { readonly report: ReviewReport }) {
  const scored = REVIEW_SCORE_AXES.filter((axis) => report.scores[axis] !== undefined);
  if (scored.length === 0) return null;
  const total = scored.reduce((sum, axis) => sum + (report.scores[axis]?.score ?? 0), 0);
  return (
    <details className="review__checked" open>
      <summary>{wording.reviewScoresSummary(total, scored.length * 3)}</summary>
      <ul className="review__checked-list">
        {scored.map((axis) => {
          const score = report.scores[axis];
          return (
            <li key={axis}>
              <Badge tone={score === undefined || score.score <= 1 ? 'danger' : score.score === 2 ? 'neutral' : 'done'}>
                {wording.REVIEW_SCORE_LABEL[axis]} {score?.score}/3
              </Badge>{' '}
              {score?.note}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/** One finding: where it is, what is wrong, what owns it, and what to change. */
function Finding({ finding }: { readonly finding: ReviewFinding }) {
  return (
    <li className={`review__finding review__finding--${finding.severity}`}>
      <div className="review__meta">
        <span className="mono">{finding.id}</span>
        {finding.timestamp !== null && <span className="mono">{finding.timestamp}</span>}
        {finding.scene !== null && <span>{finding.scene}</span>}
        <span>{wording.findingArea(finding.area)}</span>
        {finding.layer !== null && <span>{wording.REVIEW_LAYER_LABEL[finding.layer]}</span>}
        {finding.scope === 'class' && <span>{wording.REVIEW_EVERYWHERE}</span>}
      </div>
      <div className="review__summary">{finding.summary}</div>
      {finding.detail !== null && <div className="review__detail">{finding.detail}</div>}
    </li>
  );
}

/**
 * The automatic revision rounds (US-048).
 *
 * The section sits inside the stage panel (production while a round runs,
 * review after). While a round runs it is one `Loader` line — "Round a of b:
 * <phase>" — and the one button that ends it, **Show me now**; the record
 * (what each round fixed and did not, and the findings being worked on) is
 * folded in a `<details>` under it (US-014). Once the rounds stop, the record
 * is the section.
 *
 * Every sentence comes from the server — the summary, the phase, each round's
 * line — because the rules behind them (how many rounds are allowed, what
 * counts as the same finding twice) are the server's, and a page that wrote its
 * own would be guessing at them.
 */
function RoundsSection({
  auto,
  busy,
  onShowNow,
}: {
  readonly auto: AutoReviewView;
  readonly busy: Busy;
  readonly onShowNow: () => void;
}) {
  const showNow = auto.canShowNow && (
    <button
      type="button"
      className="button button--small"
      onClick={onShowNow}
      disabled={busy !== null}
      title={wording.SHOW_ME_NOW_HINT}
    >
      <Icon name="check-circle" />
      {busy === 'show-now' ? wording.STOPPING : wording.SHOW_ME_NOW}
    </button>
  );

  const history = (
    <>
      <p className="rounds__summary">{auto.summary}</p>

      {auto.running && auto.working.length > 0 && (
        <>
          <div className="rounds__label">{wording.ROUNDS_WORKING_ON}</div>
          <ul className="review__findings">
            {auto.working.map((finding) => (
              <Finding finding={finding} key={finding.id} />
            ))}
          </ul>
        </>
      )}

      {auto.rounds.map((round) => (
        <div className="rounds__round" key={round.round}>
          <div className="rounds__round-title">{round.summary}</div>
          <RoundFindings label={wording.ROUNDS_FIXED} tone="done" findings={round.fixed} />
          <RoundFindings label={wording.ROUNDS_REMAINING} tone="danger" findings={round.remaining} />
          <RoundFindings label={wording.ROUNDS_NEW} tone="wait" findings={round.introduced} />
        </div>
      ))}
    </>
  );

  // A running round is one line and one button (US-014); what the rounds did
  // so far is a click away rather than a scroll between the operator and it.
  if (auto.running) {
    const round = Math.max(auto.round, 1);
    return (
      <section className="rounds" aria-label={wording.ROUNDS}>
        <div className="rounds__header">
          <Loader
            label={wording.roundAt(round, auto.limit, auto.phaseLabel ?? wording.ROUNDS_STARTING)}
            detail={auto.step === null ? undefined : auto.step.title}
          />
          {showNow}
        </div>
        <details className="disclosure">
          <summary className="disclosure__summary">
            <Icon name="chevron-right" />
            {wording.ROUNDS_HISTORY}
          </summary>
          <div className="disclosure__body">{history}</div>
        </details>
      </section>
    );
  }

  return (
    <section className="rounds" aria-label={wording.ROUNDS}>
      <div className="rounds__header">
        <h3 className="rounds__title">
          <Icon name="sync" />
          {wording.ROUNDS}
          <Badge tone="neutral">{wording.roundBadge(auto.round, auto.limit)}</Badge>
        </h3>
        {showNow}
      </div>
      {history}

      {auto.stopped && (
        <Notice kind="info">{wording.ROUNDS_STOPPED}</Notice>
      )}
    </section>
  );
}

/** One group of a round's findings; nothing is drawn for an empty group. */
function RoundFindings({
  label,
  tone,
  findings,
}: {
  readonly label: string;
  readonly tone: 'done' | 'danger' | 'wait';
  readonly findings: readonly ReviewFinding[];
}) {
  if (findings.length === 0) return null;
  return (
    <div className="rounds__group">
      <Badge tone={tone}>
        {label} · {findings.length}
      </Badge>
      <ul className="rounds__list">
        {findings.map((finding) => (
          <li key={`${label}-${finding.id}-${finding.summary}`}>{finding.summary}</li>
        ))}
      </ul>
    </div>
  );
}

/* --------------------------------------------------- versions and the player */

/** How large a still is shown: fitted to the stage, or at its own pixel size. */
const ZOOM_LEVELS = ['fit', '100', '200'] as const;
type ZoomLevel = (typeof ZOOM_LEVELS)[number];

/**
 * The rendered versions of a content, and the player over them (US-042,
 * US-055).
 *
 * Everything on this panel comes out of one answer from the server: which
 * versions exist, which one `current_version` points at, and which asset each
 * file of each is. The bytes themselves never pass through marketingchief —
 * the `<video>` and the `<img>` load straight from object storage through a
 * presigned URL {@link useAssetUrls} fetched, which is why a render is streamed
 * by the bucket rather than by a Node process.
 *
 * A video gets a poster, a captions toggle and the browser's own controls; an
 * image gets a checkerboard and a zoom, because a still with transparency read
 * against the panel colour is a lie about what will be posted.
 */
function VersionsPanel({ versions }: { readonly versions: Versions }) {
  const count = versions.versions.length;
  return (
    <Panel
      title={wording.VERSIONS}
      icon={versions.kind === 'image' ? 'image' : 'device-camera-video'}
      id="player"
      collapsible
      defaultOpen={false}
      summary={wording.versionsSummary(versions.currentVersion, count)}
    >
      <Player versions={versions} />
    </Panel>
  );
}

/**
 * The player itself: the version switcher, the stage and the downloads. The
 * review panel carries it for the version under review, and the Versions panel
 * in Details for every other state.
 */
function Player({ versions }: { readonly versions: Versions }) {
  const [chosen, setChosen] = useState<number | null>(null);
  const [showCaptions, setShowCaptions] = useState(false);
  const [zoom, setZoom] = useState<ZoomLevel>('fit');
  const video = useRef<HTMLVideoElement | null>(null);

  // The switcher opens on the current version and stays where the operator put
  // it, unless a newer render arrived under them — a version that is no longer
  // in the list falls back to the current one rather than emptying the panel.
  const version =
    versions.versions.find((candidate) => candidate.number === chosen) ??
    versions.versions.find((candidate) => candidate.number === versions.currentVersion) ??
    versions.versions[0];

  // `default` on a `<track>` only decides the mode the browser mounts it in, so
  // the toggle has to move the track itself — and again whenever the media
  // element is replaced, which switching version does.
  useEffect(() => {
    const element = video.current;
    if (element === null) return;
    for (const track of Array.from(element.textTracks)) {
      track.mode = showCaptions ? 'showing' : 'hidden';
    }
  }, [showCaptions, version?.number]);

  // Before the early return below, because a hook is not conditional.
  const files =
    version === undefined
      ? []
      : [version.render, version.poster, version.captions, ...version.variants].filter(
          (file): file is VersionFile => file !== null,
        );
  const urls = useAssetUrls(
    versions.contentId,
    files.map((file) => file.assetId),
  );
  const urlFor = (file: VersionFile | null): string | null => {
    if (file === null || file.assetId === null) return null;
    return urls.get(file.assetId)?.url ?? null;
  };

  if (version === undefined) return null;
  const render = version.render;
  const renderUrl = urlFor(render);
  const captionsUrl = urlFor(version.captions);
  const isImage = versions.kind === 'image';

  return (
    <div className="player">
      <div className="player__head">
        <h3 className="player__title">
          <Icon name={isImage ? 'image' : 'device-camera-video'} />
          {wording.versionTitle(version.number)}
          {version.current ? (
            <Badge tone="done">{wording.VERSION_CURRENT}</Badge>
          ) : (
            <Badge tone="neutral">{wording.versionReplaced(versions.currentVersion)}</Badge>
          )}
          <span className="muted">{wording.versionMade(since(version.createdAt))}</span>
        </h3>
        {versions.versions.length > 1 && (
          <Segmented
            ariaLabel={wording.REVIEW_VERSION}
            value={String(version.number)}
            options={versions.versions.map((candidate) => ({
              value: String(candidate.number),
              label: String(candidate.number),
            }))}
            onChange={(value) => {
              setChosen(Number(value));
            }}
          />
        )}
      </div>
      {!isImage && version.contactSheet !== null && (
        <ContactSheet
          contentId={versions.contentId}
          version={version.number}
          sheet={version.contactSheet}
          onSeek={(seconds) => {
            const element = video.current;
            if (element === null) return;
            element.currentTime = seconds;
            // Focus rather than play: the operator asked to *look* at a
            // frame, and a sheet that started the sound every time it was
            // clicked would be unusable in an open-plan office. The controls
            // are one key away once the player has focus.
            element.focus();
          }}
        />
      )}
      <div className={`player__stage${isImage ? ' player__stage--checkerboard' : ''}`}>
        {render === null ? (
          <p className="player__empty">{wording.VERSION_NO_FILE}</p>
        ) : renderUrl === null ? (
          <p className="player__empty">
            {render.assetId === null ? wording.VERSION_NOT_STORED : wording.VERSION_NOT_FETCHED}
          </p>
        ) : isImage ? (
          <img
            className={`player__image${zoom === 'fit' ? '' : ' player__image--zoomed'}`}
            src={renderUrl}
            alt={`Version ${String(version.number)} of this content`}
            style={zoom === 'fit' ? undefined : { width: zoomedWidth(render, zoom) }}
          />
        ) : (
          <video
            className="player__video"
            // Keyed by version, not by URL: a re-signed URL every twelve
            // minutes must not remount a video somebody is watching.
            key={version.number}
            ref={video}
            controls
            playsInline
            preload="metadata"
            poster={urlFor(version.poster) ?? undefined}
          >
            {/* `<source>` before `<track>`, which is the order the HTML spec
                gives a media element's children. */}
            <source src={renderUrl} type={render.mime} />
            {version.captions !== null && captionsUrl !== null && (
              <track kind="captions" label={wording.CAPTIONS} src={captionsUrl} default={showCaptions} />
            )}
          </video>
        )}
      </div>

      <div className="player__bar">
        <div className="player__controls">
          {isImage ? (
            <>
              <span className="player__label">{wording.ZOOM}</span>
              <Segmented
                ariaLabel={wording.ZOOM}
                value={zoom}
                options={ZOOM_LEVELS.map((level) => ({
                  value: level,
                  label: level === 'fit' ? wording.ZOOM_FIT : `${level}%`,
                }))}
                onChange={setZoom}
              />
            </>
          ) : version.captions === null ? (
            <span className="player__label">{wording.NO_CAPTIONS}</span>
          ) : (
            <button
              type="button"
              className={`button${showCaptions ? ' button--active' : ''}`}
              aria-pressed={showCaptions}
              onClick={() => {
                setShowCaptions((previous) => !previous);
              }}
              title={wording.CAPTIONS_HINT}
            >
              <Icon name="quote" />
              {showCaptions ? wording.CAPTIONS_ON : wording.CAPTIONS_OFF}
            </button>
          )}
        </div>
        {render !== null && (
          <div className="player__meta">
            {fileFacts(render).map((fact) => (
              <span key={fact}>{fact}</span>
            ))}
          </div>
        )}
      </div>

      {render !== null && (
        <div className="player__downloads">
          <span className="player__label">{wording.DOWNLOAD}</span>
          <DownloadButton
            file={render}
            url={renderUrl}
            label={isImage ? wording.DOWNLOAD_IMAGE : wording.DOWNLOAD_MAIN}
          />
          {version.variants.map((variant, index) => (
            <DownloadButton
              key={variant.path}
              file={variant}
              url={urlFor(variant)}
              label={variant.preset ?? wording.otherSize(index + 1)}
            />
          ))}
          {version.captions !== null && (
            <DownloadButton file={version.captions} url={captionsUrl} label={wording.CAPTIONS} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The share copy that ships with this version (US-075).
 *
 * Posting is the last manual step marketingchief leaves the operator, so the
 * panel is built for one gesture: press the platform's button, go and paste.
 * Every platform `mc-copy` writes for gets a row whether or not there is a post
 * under it — a missing one is a thing the operator has to know before they open
 * the tab, not an absence they discover there — and a block written under a
 * heading nothing recognises keeps its own name rather than being dropped.
 *
 * The bodies are shown as they will be pasted: preformatted, unwrapped by
 * markdown, with the character count beside each, because the count is what
 * decides whether an X post survives contact with the platform.
 */
function ShareCopyPanel({
  share,
  version,
}: {
  readonly share: ShareCopy;
  readonly version: number;
}) {
  const toast = useToast();
  const [showVariants, setShowVariants] = useState(false);

  const copy = (label: string, body: string): void => {
    navigator.clipboard
      .writeText(body)
      .then(() => {
        toast.ok(wording.copied(label));
      })
      .catch(() => {
        toast.error(wording.COPY_BLOCKED);
      });
  };

  // The posts in the order the step wrote them — the placement's first
  // (synthesis S9) — then a row for the default platform when nothing was
  // written for it, so that missing post is visibly missing. A block under a
  // heading of its own keeps it: an attribution line a licence asks for is
  // usually one, and it is exactly what must not be lost.
  const written = share.posts.map((post, index) => ({
    key: post.platform ?? `other-${String(index)}`,
    label: post.platform === null ? post.label : (SHARE_PLATFORMS.find((one) => one.id === post.platform)?.label ?? post.label),
    post: post as SharePost | null,
  }));
  const fallback = SHARE_PLATFORMS.find((platform) => platform.id === DEFAULT_SHARE_PLATFORM);
  const rows =
    fallback === undefined || share.posts.some((post) => post.platform === DEFAULT_SHARE_PLATFORM)
      ? written
      : [{ key: fallback.id, label: fallback.label, post: null }, ...written];
  const platforms = share.posts.filter((post) => post.platform !== null).length;

  return (
    <Panel
      title={wording.SHARE_COPY}
      icon="comment"
      id="share"
      collapsible
      defaultOpen={false}
      summary={wording.platformsWritten(platforms)}
      meta={
        <span className="panel__meta">
          <Badge tone={platforms > 0 ? 'done' : 'neutral'}>{wording.platformsBadge(platforms)}</Badge>
          <span>{wording.versionTitle(version)}</span>
        </span>
      }
      actions={
        <button
          type="button"
          className="button button--quiet"
          onClick={() => {
            copy(wording.ALL_POSTS, share.text);
          }}
          title={wording.COPY_ALL_HINT}
        >
          <Icon name="copy" />
          {wording.COPY_ALL}
        </button>
      }
    >
      <div className="share">
        {share.credits.map((credit) => (
          <p
            key={credit.line}
            className={`share__credit${credit.satisfied ? '' : ' share__credit--owed'}`}
          >
            <Icon name={credit.satisfied ? 'check-circle' : 'alert'} />
            <span>
              <strong>{credit.line}</strong>{' '}
              {wording.creditLine(credit.source, credit.satisfied, credit.note)}
            </span>
          </p>
        ))}
        {rows.map((row) => (
          <SharePostRow
            key={row.key}
            label={row.label}
            post={row.post}
            onCopy={() => {
              if (row.post !== null) copy(row.label, row.post.body);
            }}
          />
        ))}

        {share.variants !== null && (
          <div className="share__variants">
            <button
              type="button"
              className="button button--quiet"
              aria-expanded={showVariants}
              onClick={() => {
                setShowVariants((previous) => !previous);
              }}
              title={wording.ALTERNATIVES_HINT}
            >
              <Icon name={showVariants ? 'chevron-down' : 'chevron-right'} />
              {showVariants ? wording.HIDE_ALTERNATIVES : wording.SHOW_ALTERNATIVES}
            </button>
            {showVariants && <Markdown text={share.variants} />}
          </div>
        )}
      </div>
    </Panel>
  );
}

/**
 * One platform's post, or the sentence that says why there is none.
 *
 * The button is the whole point of the row, so it is there in both cases — a
 * disabled one when nothing was written, which says the platform was asked for
 * and not answered rather than leaving the row looking like a design choice.
 */
function SharePostRow({
  label,
  post,
  onCopy,
}: {
  readonly label: string;
  readonly post: SharePost | null;
  readonly onCopy: () => void;
}) {
  return (
    <article className="share__post">
      <header className="share__head">
        <span className="share__platform">{label}</span>
        {post === null ? (
          <span className="share__count">{wording.SHARE_NOTHING_WRITTEN}</span>
        ) : (
          <span className="share__count">{wording.characters(post.body.length)}</span>
        )}
        {post === null ? (
          <span className="button button--quiet" aria-disabled="true">
            <Icon name="copy" />
            {wording.COPY}
          </span>
        ) : (
          <button type="button" className="button button--quiet" onClick={onCopy}>
            <Icon name="copy" />
            {wording.COPY}
          </button>
        )}
      </header>
      {post === null ? (
        <p className="share__empty">{wording.shareMissing(label)}</p>
      ) : (
        <pre className="share__body">{post.body}</pre>
      )}
    </article>
  );
}

/**
 * One file, as a link the browser saves rather than plays.
 *
 * Disabled until its URL has been resolved, which is also what it looks like
 * for a file that never reached the bucket: there is nowhere to download it
 * from, and the title says which of the two it is.
 */
function DownloadButton({
  file,
  url,
  label,
}: {
  readonly file: VersionFile;
  readonly url: string | null;
  readonly label: string;
}) {
  const facts = fileFacts(file).join(' · ');
  if (url === null) {
    return (
      <span
        className="button button--quiet"
        aria-disabled="true"
        title={
          file.assetId === null
            ? `${facts} · ${wording.DOWNLOAD_NOT_STORED}`
            : `${facts} · ${wording.DOWNLOAD_WAITING}`
        }
      >
        <Icon name="file" />
        {label}
      </span>
    );
  }
  return (
    <a className="button button--quiet" href={url} download={file.name} title={facts}>
      <Icon name="file" />
      {label}
    </a>
  );
}

/**
 * What the probe measured about a file, on one line: the same numbers the step
 * list shows under an output, because they are the same measurement.
 */
/**
 * The whole render in one picture (US-080).
 *
 * The image is what ffmpeg drew: a frame per second, with a border on the
 * frame each scene starts on. Over it sits one transparent button per tile,
 * positioned from the *stored* grid, so clicking a frame seeks the player to
 * the second that frame was taken at — the sheet is the scrub bar, at a
 * resolution a scrub bar does not have.
 *
 * The buttons are real buttons rather than a click handler on the image
 * because they are the gesture: they are reachable by keyboard, each one says
 * which second it seeks to, and a screen reader gets a list of timestamps
 * rather than one unlabelled picture.
 */
function ContactSheet({
  contentId,
  version,
  sheet,
  onSeek,
}: {
  readonly contentId: string;
  readonly version: number;
  readonly sheet: ContactSheetView;
  readonly onSeek: (seconds: number) => void;
}) {
  const [failed, setFailed] = useState(false);
  const [chosen, setChosen] = useState<number | null>(null);
  const { layout } = sheet;
  const scenes = new Set(layout.scenes);

  if (failed) {
    return (
      <p className="sheet__note">
        The contact sheet of this version could not be loaded. It is drawn into the workspace when
        the render probes clean and mirrored with the rest of the step’s files.
      </p>
    );
  }

  return (
    <figure className="sheet">
      <div className="sheet__frame" style={{ aspectRatio: `${String(layout.width)} / ${String(layout.height)}` }}>
        <img
          className="sheet__image"
          src={contactSheetUrl(contentId, version)}
          alt={`Every ${layout.intervalSeconds === 1 ? 'second' : `${String(layout.intervalSeconds)} seconds`} of version ${String(version)}, ${String(layout.frames)} frames`}
          onError={() => {
            setFailed(true);
          }}
        />
        <div className="sheet__grid">
          {Array.from({ length: layout.frames }, (_, tile) => {
            const seconds = contactSheetTileSeconds(layout, tile);
            const scene = scenes.has(tile);
            return (
              <button
                key={tile}
                type="button"
                className={`sheet__tile${scene ? ' sheet__tile--scene' : ''}${chosen === tile ? ' sheet__tile--chosen' : ''}`}
                style={contactSheetTileBox(layout, tile)}
                title={`${clock(seconds)}${scene ? ' · a scene starts here' : ''}`}
                onClick={() => {
                  setChosen(tile);
                  onSeek(seconds);
                }}
              >
                <span className="visually-hidden">
                  {`Seek to ${clock(seconds)}${scene ? ', where a scene starts' : ''}`}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <figcaption className="sheet__caption">
        {`${String(layout.frames)} frames, one every ${layout.intervalSeconds === 1 ? 'second' : `${String(layout.intervalSeconds)} seconds`} of ${clock(layout.durationMs / 1000)} · ${String(layout.scenes.length)} scene${layout.scenes.length === 1 ? '' : 's'} marked · click a frame to seek the player`}
      </figcaption>
    </figure>
  );
}

/** `m:ss`, which is how long anything on this page is. */
function clock(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(whole / 60))}:${String(whole % 60).padStart(2, '0')}`;
}

function fileFacts(file: VersionFile): string[] {
  const facts: string[] = [];
  if (file.width !== null && file.height !== null) {
    facts.push(`${String(file.width)}×${String(file.height)}`);
  }
  if (file.durationMs !== null) facts.push(`${(file.durationMs / 1000).toFixed(1)} s`);
  facts.push(kilobytes(file.bytes));
  return facts;
}

/**
 * How wide a zoomed still is drawn.
 *
 * The pixel width the probe measured is the honest answer: 100 % of a 1080-wide
 * still is 1080 CSS pixels, which is what "actual size" has to mean for an asset
 * that is going to be posted. A file nothing measured falls back to a multiple
 * of the stage, so the control still does something.
 */
function zoomedWidth(file: VersionFile, zoom: ZoomLevel): string {
  const factor = Number(zoom) / 100;
  return file.width === null
    ? `${String(factor * 100)}%`
    : `${String(Math.round(file.width * factor))}px`;
}

/* ------------------------------------------------------- the production run */

/**
 * Where the loop is right now, and nothing else (US-013): one Loader naming
 * the step, its attempt and how long it has been going, then the steps as a
 * quiet checklist. The loop's settings (iteration, timeout, model, slots) are
 * in the aside's Details, and the log is its own panel in Details.
 *
 * Every number here is the server's own — `GET /api/contents/:id/production`
 * is the loop's account of itself, polled on this page's three-second beat —
 * so the panel holds no state of its own beyond the clock it times with.
 */
function ProductionPanel({
  content,
  production,
  plan,
  children,
}: {
  readonly content: ContentData;
  readonly production: Production | null;
  /** The plan's view of the steps, for the probe facts behind each row. */
  readonly plan: ContentPlan | null;
  /** The automatic rounds, while one of them is producing the next version. */
  readonly children?: ReactNode;
}) {
  // The attempt is timed to the second rather than on the poll's beat.
  const now = useNow();
  const waiting = content.status === 'waiting';
  const failed = content.status === 'failed';

  if (production === null) {
    return (
      <Panel title={wording.PRODUCTION} icon="pulse">
        {children}
        <Skeleton lines={4} />
      </Panel>
    );
  }

  const steps = [...production.steps].sort((left, right) => left.priority - right.priority);
  const step = steps.find((row) => row.stepId === production.currentStepId) ?? null;
  const done = steps.filter((row) => row.status === 'done').length;
  const position = step === null ? Math.min(done + 1, steps.length) : steps.indexOf(step) + 1;
  const attempt = production.running ? production.attempts + 1 : production.attempts;
  const startedAt = step?.startedAt ?? production.startedAt;

  return (
    <Panel title={wording.PRODUCTION} icon="pulse">
      {children}
      {failed ? (
        <p className="muted">{wording.productionStopped(done, steps.length)}</p>
      ) : waiting ? (
        <Notice kind="wait">
          {wording.productionOnHold(
            content.waitingUntil === null ? null : hourMinute(content.waitingUntil),
          )}
        </Notice>
      ) : step === null ? (
        <Loader label={wording.PRODUCTION_STARTING} />
      ) : (
        <Loader
          label={wording.productionAt(position, steps.length, step.title)}
          detail={wording.productionAttempt(
            Math.max(1, attempt),
            production.maxAttempts,
            startedAt === null ? null : clock((now - Date.parse(startedAt)) / 1000),
          )}
        />
      )}

      <ol className="step-checklist" aria-label={wording.PRODUCTION_STEPS}>
        {steps.map((row) => (
          <ChecklistRow
            key={row.stepId}
            step={row}
            probe={plan?.steps.find((planned) => planned.stepId === row.stepId)?.probe ?? null}
            failed={failed && row.stepId === production.currentStepId && row.status !== 'done'}
          />
        ))}
      </ol>

      <p className="field__hint">
        <button
          type="button"
          className="link link--button"
          onClick={() => {
            revealPanel('log');
          }}
        >
          {wording.SHOW_THE_LOG}
        </button>
      </p>
    </Panel>
  );
}

/** What a checklist row's mark says, for a screen reader. */
const CHECKLIST_STATE: Record<StepStatus | 'failed', string> = {
  todo: 'to do',
  'in-progress': 'running',
  done: 'done',
  failed: 'failed',
};

/**
 * One row of the production checklist: the title, and a mark for how far the
 * step got. Everything else about the step — its id, attempts, priority and
 * what the probe measured — waits behind the row's own disclosure (US-013).
 */
function ChecklistRow({
  step,
  probe,
  failed,
}: {
  readonly step: ProductionStep;
  readonly probe: ProbeReport | null;
  /** The step a failed production stopped on. */
  readonly failed: boolean;
}) {
  const verdict = probeSummary(probe);
  const state = failed ? 'failed' : step.status;
  return (
    <li className={`step-checklist__item step-checklist__item--${state}`}>
      <details className="disclosure">
        <summary className="disclosure__summary step-checklist__summary">
          {failed ? (
            <Icon name="x-circle" className="step-checklist__mark" />
          ) : step.status === 'done' ? (
            <Icon name="check" className="step-checklist__mark" />
          ) : step.status === 'in-progress' ? (
            <span className="loader__ring step-checklist__ring" aria-hidden="true" />
          ) : (
            <span className="step-checklist__todo" aria-hidden="true" />
          )}
          <VisuallyHidden>{CHECKLIST_STATE[state]}: </VisuallyHidden>
          <span className="step-checklist__title">{step.title}</span>
          {failed && <Badge tone="danger">{wording.PRODUCTION_FAILED_BADGE}</Badge>}
        </summary>
        <div className="disclosure__body">
          <Facts
            items={[
              { label: wording.PART_ID, value: step.stepId, mono: true },
              { label: wording.PART_TRIES, value: String(step.attempts) },
              { label: wording.PART_ORDER, value: String(step.priority) },
              ...(step.durationMs === null
                ? []
                : [{ label: wording.PART_LAST_TRY, value: elapsed(step.durationMs) }]),
              ...(verdict === null || probe === null
                ? []
                : [
                    {
                      label: wording.PART_CHECK,
                      value: (
                        <Badge
                          tone={verdict.tone}
                          title={wording.partCheckHint(localTime(probe.checkedAt), probe.ok)}
                        >
                          {wording.partCheck(probe.ok, verdict.problems)}
                        </Badge>
                      ),
                    },
                  ]),
              ...(probe?.outputs ?? []).map((output) => ({
                label: output.path,
                value:
                  output.errors.length > 0
                    ? output.errors.join(' ')
                    : probeFacts(output).join(' · ') || wording.PART_FILE_PRESENT,
                mono: true,
              })),
            ]}
          />
        </div>
      </details>
    </li>
  );
}

/**
 * The usage-limit hold, counting down to the second: the next-step card's
 * explanation while a content is held.
 *
 * The hold is on the Claude account the whole server shares, so this is the
 * same expiry the sidebar clock shows; it is repeated here because this is the
 * page where the operator is waiting for it.
 */
function HoldExplanation({ until }: { readonly until: string | null }) {
  const now = useNow();
  return (
    <p>
      {until === null ? (
        'It resumes on its own as soon as the limit lifts.'
      ) : (
        <>
          It resumes on its own in <span className='mono'>{countdown(until, now)}</span>, at{' '}
          {localTime(until)}.
        </>
      )}{' '}
      The hold is on the account the whole server shares, so every held content resumes together.
    </p>
  );
}

/**
 * **The narration script** (US-090): the words, before anything says them.
 *
 * A `script` step writes them and the production stops here. What the operator
 * edits is the file in the workspace itself — the `voiceover` step speaks
 * whatever that file holds, so an edit made here is the narration, not a note
 * about it.
 *
 * The textarea is the whole panel on purpose. The decision is "would a person
 * say this", which is taken by reading it, and every control that is not the
 * words is in the way of that. At the gate the panel opens with what is being
 * decided and **Approve narration**, and the same button follows the text,
 * so the decision is in reach wherever the reading stops (US-014).
 */
function ScriptPanel({
  script,
  busy,
  onSave,
  onApprove,
  collapsible = false,
  withSteps = false,
}: {
  readonly script: ScriptView;
  readonly busy: boolean;
  readonly onSave: (text: string) => void;
  readonly onApprove: (text: string) => void;
  /** The stage panel draws where the operator is among the approvals (US-105). */
  readonly withSteps?: boolean;
  /** Folded in Details, where it is read after the fact rather than decided on. */
  readonly collapsible?: boolean;
}) {
  const [text, setText] = useState(script.text);
  const [touched, setTouched] = useState(false);

  // The server is the source of these words: a save elsewhere, or the step
  // rewriting them in a revision round, replaces what is on screen — unless
  // the operator has started typing, whose edit is not thrown away by a poll.
  useEffect(() => {
    if (!touched) setText(script.text);
  }, [script.text, touched]);

  const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
  const seconds = Math.round((words / SCRIPT_WORDS_PER_MINUTE) * 60);
  const edited = text !== script.text;
  // The gate (US-014): the decision opens the panel and closes it, so the
  // operator never scrolls past the words to find the button.
  const gate = script.awaitingApproval && !collapsible;

  const saveButton = (
    <button
      type="button"
      className="button button--small button--quiet"
      onClick={() => {
        onSave(text);
        setTouched(false);
      }}
      disabled={busy || !edited}
    >
      <Icon name="pencil" />
      {wording.SAVE_NARRATION}
    </button>
  );
  const approveButton = (small: boolean) => (
    <button
      type="button"
      className={`button button--primary${small ? ' button--small' : ''}`}
      onClick={() => {
        onApprove(text);
        setTouched(false);
      }}
      disabled={busy || text.trim() === ''}
    >
      <Icon name="check" />
      {wording.APPROVE_NARRATION}
    </button>
  );

  return (
    <Panel
      id="script"
      title={wording.NARRATION}
      icon="file"
      collapsible={collapsible}
      defaultOpen={false}
      summary={wording.narrationSummary(words, seconds)}
      meta={
        script.awaitingApproval ? (
          <Badge tone="wait">{wording.WAITING_FOR_YOU}</Badge>
        ) : script.approvedAt === null ? (
          <Badge>{wording.NARRATION_BADGE_NOT_APPROVED}</Badge>
        ) : (
          <Badge tone="done">{wording.APPROVED}</Badge>
        )
      }
      actions={
        gate ? (
          saveButton
        ) : (
          <>
            {saveButton}
            {approveButton(true)}
          </>
        )
      }
    >
      {withSteps && gate && <GateSteps current="narration" withNarration />}
      {gate && (
        <>
          <Notice kind="wait">
            {script.narrationChanged
              ? wording.NARRATION_CHANGED_NOTICE
              : wording.NARRATION_GATE_NOTICE}
          </Notice>
          <div className="script__decide">{approveButton(false)}</div>
        </>
      )}
      <p className="muted">
        {script.awaitingApproval ? wording.NARRATION_AT_GATE : wording.NARRATION_AFTER_GATE}
      </p>
      <HowApprovalWorks />
      <textarea
        className="field__input field__input--script"
        rows={12}
        value={text}
        spellCheck
        onChange={(event) => {
          setText(event.target.value);
          setTouched(true);
        }}
        aria-label={wording.NARRATION_FIELD}
      />
      {gate && <div className="script__decide">{approveButton(false)}</div>}
      <Facts
        items={[
          { label: wording.NARRATION_WORDS, value: String(words) },
          { label: wording.NARRATION_LENGTH, value: wording.narrationLength(seconds) },
          ...(script.approvedAt === null
            ? []
            : [{ label: wording.NARRATION_APPROVED_AT, value: localTime(script.approvedAt) }]),
        ]}
      />
      <p className="field__hint">{wording.NARRATION_HINT}</p>
      <ScriptChanges changes={script.scriptChanges} />
    </Panel>
  );
}

/**
 * The ideas-document lines the planner changed (US-016), under the words: the
 * document's line, the plan's and why. A record, never a choice — the plan
 * already says the new line.
 */
function ScriptChanges({ changes }: { readonly changes: readonly ScriptChange[] }) {
  if (changes.length === 0) return null;
  return (
    <section className="script-changes" aria-label={wording.SCRIPT_CHANGES_TITLE}>
      <strong>{wording.SCRIPT_CHANGES_TITLE}</strong>
      <p className="field__hint">{wording.SCRIPT_CHANGES_HINT}</p>
      <ol className="script-changes__list">
        {changes.map((change) => (
          <li key={change.line}>
            <span className="script-changes__line">
              <span className="script-changes__from">{change.document}</span> →{' '}
              {change.plan === '' ? <em>{wording.SCRIPT_CHANGES_DROPPED}</em> : change.plan}
            </span>
            <span className="script-changes__why">{change.why}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Words a minute the estimate under the textarea assumes; the same figure the
 * server uses, and the middle of a presenter's range.
 */
const SCRIPT_WORDS_PER_MINUTE = 150;

/* ------------------------------------------------------------ the plan sketch */

/**
 * What a running fit or adjustment is doing and how long it has taken
 * (US-110), so a wait of a minute or more does not read as a stuck page: the
 * request in quotes, "about a minute", a clock ticking every second from the
 * run's start, and after `LONG_WAIT_SECONDS` the reassurance that leaving is
 * safe. Its own component, so the tick re-renders this and not the panel.
 */
function SketchWaiting({
  request,
  startedAt,
}: {
  /** The operator's request; `null` for a fit to the approved narration. */
  readonly request: string | null;
  readonly startedAt: string | null;
}) {
  const now = useNow();
  const seconds = startedAt === null ? 0 : Math.max(0, (now - Date.parse(startedAt)) / 1000);
  return (
    <div className="sketch__waiting">
      <Loader
        label={request === null ? wording.SCENES_FITTING : wording.workingOnRequest(request)}
        detail={seconds >= wording.LONG_WAIT_SECONDS ? wording.TAKING_LONGER : wording.USUALLY_A_MINUTE}
      />
      {startedAt !== null && (
        <p className="sketch__elapsed">
          <Icon name="clock" />
          <span>{wording.timePassed(clock(seconds))}</span>
        </p>
      )}
    </div>
  );
}

/**
 * The storyboard of `content/plan.md` as one card per scene, and the one
 * decision in front of it (US-094): approve the scenes before anything is
 * generated from them.
 *
 * The cards are the server's reading of the file, polled — a fit run or an
 * adjustment rewrites the file in another process, and while one is running
 * the cards give way to a sentence saying so, because the scenes on screen
 * would be the ones being replaced.
 *
 * Under the cards, a prompt box asks for a change (US-098). The prompt is
 * cleared once the server takes it, and kept aside until the adjustment
 * ends: a failed one hands it back, so the operator can reword it rather
 * than type it again.
 *
 * Each card also has an edit mode (US-100) for the small fixes that need no
 * agent: its name, its duration and its on-screen text. One card at a time,
 * so a save — which redraws every card from the answer — never lands on top
 * of a second half-typed card.
 *
 * The box helps with the wording (US-106): example requests under it fill it
 * without sending, each card's "Change this scene" starts a request naming the
 * scene, and Ctrl+Enter / Cmd+Enter sends.
 */
function SketchPanel({
  sketch,
  starting,
  withNarration,
  busy,
  onApprove,
  onAdjust,
  onEditScene,
  onUndo,
  onTryAgain,
}: {
  readonly sketch: SketchView | null;
  /** The narration is approved and the run has not reached this gate yet (US-105). */
  readonly starting: boolean;
  /** Whether a narration approval came before this one. */
  readonly withNarration: boolean;
  readonly busy: Busy;
  readonly onApprove: () => void;
  readonly onAdjust: (prompt: string) => Promise<void>;
  readonly onEditScene: (sceneNumber: number, edit: SketchSceneEdit) => Promise<void>;
  readonly onUndo: () => Promise<void>;
  readonly onTryAgain: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  /**
   * The one card in edit mode, if any. Its draft lives here rather than in the
   * card so the panel knows about unsaved changes (US-109); the poll replaces
   * the scenes, never the draft.
   */
  const [edit, setEdit] = useState<SceneEditing | null>(null);
  const editing = edit?.number ?? null;
  const [editRefusal, setEditRefusal] = useState<EditRefusal | null>(null);
  /** The card whose "Edit this scene" gets focus back once its edit mode closes (US-111). */
  const [returnTo, setReturnTo] = useState<number | null>(null);
  /** What the live region reads out; `count` makes the same sentence twice a new one. */
  const [announcement, setAnnouncement] = useState<{ readonly text: string; readonly count: number }>({
    text: '',
    count: 0,
  });
  const announce = useCallback((text: string): void => {
    setAnnouncement((current) => ({ text, count: current.count + 1 }));
  }, []);
  /** "Save your changes to scene N first?": what to do once the operator has answered. */
  const [asking, setAsking] = useState<{ readonly then: () => void } | null>(null);
  /** "Make the video from these scenes?" is open. */
  const [approving, setApproving] = useState(false);
  const unsaved = edit !== null && !sameDraft(edit.draft, edit.original);
  /** Anything a reload or a closed tab would throw away. */
  const leaving = unsaved || prompt.trim() !== '';
  const [refusal, setRefusal] = useState<string | null>(null);
  /** Why a "Fix this for me" or "Fix all problems" request was refused. */
  const [fixRefusal, setFixRefusal] = useState<string | null>(null);
  /** An undo just went through (US-112); forgotten once there is a new change to undo. */
  const [undone, setUndone] = useState(false);
  const [undoRefusal, setUndoRefusal] = useState<string | null>(null);
  const canUndo = sketch?.canUndo ?? false;
  const state = sketch?.state ?? null;
  const box = useRef<HTMLTextAreaElement | null>(null);
  /** Bumped by an example or "Change this scene": focus the box once it holds the new text. */
  const [focusBox, setFocusBox] = useState(0);
  /** The card picked on the timeline strip, lit up for `SCENE_HIGHLIGHT_MS`. */
  const [highlighted, setHighlighted] = useState<number | null>(null);
  /** Bumped by every pick, so picking the same scene again restarts the timer. */
  const [highlightCount, setHighlightCount] = useState(0);

  useEffect(() => {
    if (highlightCount === 0) return;
    const timer = window.setTimeout(() => {
      setHighlighted(null);
    }, SCENE_HIGHLIGHT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [highlightCount]);

  useEffect(() => {
    const element = box.current;
    if (focusBox === 0 || element === null) return;
    const end = element.value.length;
    element.focus({ preventScroll: true });
    element.setSelectionRange(end, end);
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
  }, [focusBox]);

  /** Put a request in the box to be finished or sent by the operator (US-106). */
  const startRequest = (text: string): void => {
    setPrompt(text);
    setRefusal(null);
    setFocusBox((count) => count + 1);
  };

  useEffect(() => {
    if (canUndo) setUndone(false);
    setUndoRefusal(null);
  }, [canUndo]);

  const undo = (): void => {
    setUndoRefusal(null);
    onUndo()
      .then(() => {
        setUndone(true);
        announce(wording.SCENES_UNDONE);
      })
      .catch((cause: unknown) => {
        setUndoRefusal(wording.plainError(cause));
      });
  };

  // A pass that starts rewrites the scenes under the card being edited.
  useEffect(() => {
    if (state === 'preparing' || state === 'adjusting') setEdit(null);
  }, [state]);

  /**
   * A run starting, finishing or failing is read out (US-111), and a finished
   * one takes focus to "Last change", which says what it did. The first
   * reading is where the operator arrived, not a change, so it says nothing.
   */
  const request = sketch?.run?.request ?? null;
  const error = sketch?.error ?? null;
  const seen = useRef<SketchState | null | undefined>(undefined);
  useEffect(() => {
    const before = seen.current;
    seen.current = state;
    if (before === undefined || before === state) return;
    const wasWorking = before === 'preparing' || before === 'adjusting';
    if (state === 'preparing' || state === 'adjusting') {
      announce(request === null ? wording.SCENES_FITTING : wording.workingOnRequest(request));
    } else if (state === 'failed') {
      announce(error === null ? wording.SCENES_FAILED : `${wording.SCENES_FAILED} ${error}`);
    } else if (state === 'ready' && wasWorking) {
      announce(wording.SCENES_UPDATED);
      document.getElementById(LAST_CHANGE_ID)?.focus();
    }
  }, [state, request, error, announce]);

  // Back to the card's "Edit this scene" once its edit mode has closed (US-111).
  useEffect(() => {
    if (returnTo === null || editing !== null || busy !== null) return;
    document.getElementById(editButtonId(returnTo))?.focus();
    setReturnTo(null);
  }, [returnTo, editing, busy]);

  // A question about a card that is no longer being edited has no answer left.
  useEffect(() => {
    if (edit === null) setAsking(null);
  }, [edit]);

  // The browser's own leave-page warning (US-109).
  useEffect(() => {
    if (!leaving) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [leaving]);

  const blockedNow = sketch === null || sketch.state !== 'ready' || sketch.problems.length > 0;
  // A save that brought problems along closes the way to approval again.
  useEffect(() => {
    if (blockedNow) setApproving(false);
  }, [blockedNow]);

  const startEdit = (scene: SketchScene): void => {
    const fresh = draftOf(scene);
    setEdit({ number: scene.number, original: fresh, draft: fresh });
    setEditRefusal(null);
  };

  const leaveEdit = (): void => {
    setEdit(null);
    setEditRefusal(null);
  };

  /** Save the card being edited; `false` when it was refused (the card says why). */
  const saveEdit = async (): Promise<boolean> => {
    if (edit === null) return true;
    const { draft } = edit;
    const secondsValue = Number(draft.seconds.trim().replace(',', '.'));
    if (draft.seconds.trim() === '' || !Number.isFinite(secondsValue) || secondsValue <= 0) {
      setEditRefusal({ text: wording.SCENE_LENGTH_REFUSED, field: 'seconds' });
      return false;
    }
    setEditRefusal(null);
    try {
      await onEditScene(edit.number, {
        name: draft.name.trim(),
        seconds: secondsValue,
        // An emptied block is a removed one, not a refusal.
        text: draft.text.map((block) => block.value.trim()).filter((block) => block !== ''),
      });
      setEdit(null);
      announce(wording.sceneSaved(edit.number));
      return true;
    } catch (cause: unknown) {
      setEditRefusal({ text: wording.plainError(cause), field: refusedField(cause) });
      return false;
    }
  };

  /**
   * Do `then` once the card being edited is out of the way (US-109): at once
   * when nothing in it changed, after "Save your changes to scene N first?" when
   * something did.
   */
  const afterEdit = (then: () => void): void => {
    if (unsaved) {
      setAsking({ then });
      return;
    }
    leaveEdit();
    then();
  };

  const adjust = (): void => {
    setRefusal(null);
    // A request that then fails comes back through "Change my request" (US-110).
    onAdjust(prompt)
      .then(() => {
        setPrompt('');
      })
      .catch((cause: unknown) => {
        setRefusal(wording.plainError(cause));
      });
  };

  /**
   * A problem's ready-made request, sent as is (US-104). It never goes through
   * the request box: nothing the operator typed there is replaced or cleared.
   */
  const fix = (fixPrompt: string): void => {
    afterEdit(() => {
      setFixRefusal(null);
      onAdjust(fixPrompt).catch((cause: unknown) => {
        setFixRefusal(wording.plainError(cause));
      });
    });
  };

  /**
   * **Try again** after a failed run (US-110): the same request once more, or
   * — for a fit, or a request the server no longer remembers — a new fit.
   */
  const retry = (): void => {
    const request = sketch?.run?.request ?? null;
    if (request === null) {
      onTryAgain();
      return;
    }
    fix(request);
  };

  /** Scroll to a scene picked on the timeline strip and light its card up for a moment (US-108). */
  const pick = (sceneNumber: number): void => {
    const card = document.getElementById(sceneCardId(sceneNumber));
    if (card === null) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    card.focus({ preventScroll: true });
    card.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
    setHighlighted(sceneNumber);
    setHighlightCount((count) => count + 1);
  };

  const where = (
    <>
      <GateSteps current="scenes" withNarration={withNarration} />
      <p className="gate-steps__next">{wording.SCENES_APPROVAL_STARTS}</p>
      <HowApprovalWorks />
    </>
  );

  // Always drawn, so it is in place before the first change it has to read out.
  const liveRegion = (
    <div className="visually-hidden" aria-live="polite" aria-atomic="true">
      {announcement.text !== '' && <p key={announcement.count}>{announcement.text}</p>}
    </div>
  );

  if (sketch === null) {
    return (
      <Panel id="sketch" className="sketch" title={wording.SCENES} icon="eye">
        {where}
        {liveRegion}
        <Skeleton lines={6} />
      </Panel>
    );
  }

  const working = sketch.state === 'preparing' || sketch.state === 'adjusting';
  const blocked = blockedNow;
  const general = sketch.problems.filter((problem) => problem.sceneNumber === null);
  const count = sketch.scenes.length;
  // A failed run put the plan back as it was, so a request can go from there too.
  const canFix = busy === null && (sketch.state === 'ready' || sketch.state === 'failed');
  const canSend = canFix && prompt.trim() !== '';

  const approveButton = (
    <button
      type="button"
      className="button button--primary"
      onClick={() => {
        afterEdit(() => {
          setApproving(true);
        });
      }}
      disabled={busy !== null || blocked}
    >
      <Icon name="check" />
      {busy === 'sketch' ? wording.WORKING : wording.APPROVE_SCENES}
    </button>
  );

  return (
    <Panel
      id="sketch"
      className="sketch"
      title={wording.SCENES}
      icon="eye"
      meta={
        <>
          {working ? (
            <Badge tone="active" pulse>
              {wording.WORKING}
            </Badge>
          ) : (
            <Badge tone="wait">{wording.WAITING_FOR_YOU}</Badge>
          )}
          <span className="sketch__summary">
            {wording.scenesSummary(count, sketch.totalSeconds)}
          </span>
          {sketch.previousTotalSeconds !== null &&
            sketch.previousTotalSeconds !== sketch.totalSeconds && (
              <span className="sketch__summary sketch__summary--change">
                {wording.lengthChange(sketch.previousTotalSeconds, sketch.totalSeconds)}
              </span>
            )}
        </>
      }
    >
      {where}
      {liveRegion}
      <ScenesIntro />
      {starting ? (
        <Loader label={wording.SCENES_COMING} />
      ) : working ? (
        <>
          <SketchWaiting
            request={sketch.state === 'adjusting' ? (sketch.run?.request ?? '') : null}
            startedAt={sketch.run?.startedAt ?? null}
          />
          {/* The scenes as they were, to look at but not to touch until the run ends. */}
          <ol className="sketch__scenes sketch__scenes--waiting" inert aria-busy="true">
            {sketch.scenes.map((scene) => (
              <SketchCard
                key={scene.number}
                scene={scene}
                highlighted={false}
                problems={[]}
                canFix={false}
                onFix={fix}
                draft={null}
                onDraft={() => undefined}
                refusal={null}
                canEdit={false}
                canSave={false}
                onEdit={() => undefined}
                onRequest={() => undefined}
                onCancel={() => undefined}
                onSave={() => undefined}
              />
            ))}
          </ol>
        </>
      ) : (
        <>
          {sketch.state === 'failed' ? (
            <Notice kind="error">
              <p>
                {wording.SCENES_FAILED}
                {sketch.error !== null && ` ${sketch.error}`}
              </p>
              <div className="sketch__decide">
                <button
                  type="button"
                  className="button button--small"
                  onClick={retry}
                  disabled={busy !== null}
                >
                  <Icon name="sync" />
                  {wording.TRY_AGAIN}
                </button>
                <button
                  type="button"
                  className="button button--small button--quiet"
                  onClick={() => {
                    startRequest(sketch.run?.request ?? '');
                  }}
                  disabled={busy !== null}
                >
                  <Icon name="pencil" />
                  {wording.CHANGE_MY_REQUEST}
                </button>
              </div>
            </Notice>
          ) : (
            <Notice kind="wait">{wording.SCENES_GATE_NOTICE}</Notice>
          )}
          {sketch.problems.length > 1 && (
            <div className="sketch__decide">
              <button
                type="button"
                className="button"
                onClick={() => {
                  fix(wording.fixAllPrompt(sketch.problems.map((problem) => problem.fixPrompt)));
                }}
                disabled={!canFix}
              >
                <Icon name="sync" />
                {wording.FIX_ALL}
              </button>
            </div>
          )}
          {fixRefusal !== null && (
            <p className="sketch__refusal" role="alert">
              {fixRefusal}
            </p>
          )}
          {general.map((problem, index) => (
            <Notice key={index} kind="error">
              <SketchProblemLine problem={problem} canFix={canFix} onFix={fix} />
            </Notice>
          ))}
          {undone && (
            <Notice kind="ok">
              <p>{wording.SCENES_UNDONE}</p>
            </Notice>
          )}
          {(sketch.lastChange !== null || sketch.canUndo) && (
            <section className="sketch__last-change" aria-labelledby="sketch-last-change">
              <div className="sketch__last-change-head">
                <h3 id={LAST_CHANGE_ID} className="sketch__last-change-title" tabIndex={-1}>
                  {wording.SCENES_LAST_CHANGE}
                </h3>
                {sketch.canUndo && (
                  <button
                    type="button"
                    className="button button--small button--quiet"
                    onClick={undo}
                    disabled={busy !== null || sketch.state !== 'ready' || editing !== null}
                  >
                    <Icon name="history" />
                    {wording.UNDO_LAST_CHANGE}
                  </button>
                )}
              </div>
              <p>{sketch.lastChange ?? wording.SCENES_CHANGED}</p>
            </section>
          )}
          {undoRefusal !== null && (
            <p className="sketch__refusal" role="alert">
              {undoRefusal}
            </p>
          )}
          {sketch.removed.length > 0 && (
            <p className="sketch__removed">{wording.removedScenes(sketch.removed)}</p>
          )}
          <SketchTiming sketch={sketch} />
          <SceneStrip scenes={sketch.scenes} problems={sketch.problems} onPick={pick} />
          <ol className="sketch__scenes">
            {sketch.scenes.map((scene) => (
              <SketchCard
                key={scene.number}
                scene={scene}
                highlighted={highlighted === scene.number}
                problems={sketch.problems.filter(
                  (problem) => problem.sceneNumber === scene.number,
                )}
                canFix={canFix}
                onFix={fix}
                draft={edit !== null && edit.number === scene.number ? edit.draft : null}
                onDraft={(change) => {
                  setEdit((current) =>
                    current === null ? null : { ...current, draft: change(current.draft) },
                  );
                }}
                refusal={editing === scene.number ? editRefusal : null}
                canEdit={sketch.state === 'ready' && busy === null}
                canSave={sketch.state === 'ready' && busy === null}
                onEdit={() => {
                  afterEdit(() => {
                    startEdit(scene);
                  });
                }}
                onRequest={() => {
                  startRequest(wording.sceneRequestStart(scene.number, scene.name));
                }}
                onCancel={() => {
                  leaveEdit();
                  setReturnTo(scene.number);
                }}
                onSave={() => {
                  void saveEdit().then((saved) => {
                    if (saved) setReturnTo(scene.number);
                  });
                }}
              />
            ))}
          </ol>
          {blocked && sketch.problems.length > 0 && (
            <p className="sketch__hint">{wording.SCENES_HAVE_PROBLEMS}</p>
          )}
          <div className="sketch__decide">
            {approveButton}
            <BusyLoader busy={busy} kinds={['sketch']} label={wording.SCENES_BUSY} />
          </div>
          <div className="sketch__adjust field">
            <label className="field__label" htmlFor="sketch-prompt">
              {wording.SCENES_REQUEST_LABEL}
            </label>
            <textarea
              id="sketch-prompt"
              ref={box}
              className="field__textarea"
              rows={3}
              value={prompt}
              onChange={(event) => {
                setPrompt(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
                event.preventDefault();
                if (canSend) afterEdit(adjust);
              }}
              placeholder={wording.SCENES_REQUEST_PLACEHOLDER}
              aria-describedby={
                refusal === null ? 'sketch-prompt-hint' : 'sketch-prompt-hint sketch-prompt-error'
              }
              aria-invalid={refusal === null ? undefined : true}
            />
            <p className="field__hint" id="sketch-prompt-hint">
              {wording.SCENES_REQUEST_HINT}
            </p>
            {busy === null && (
              <ul className="sketch__examples" aria-label={wording.SCENES_REQUEST_EXAMPLES_LABEL}>
                {wording.SCENES_REQUEST_EXAMPLES.map((example) => (
                  <li key={example}>
                    <button
                      type="button"
                      className="button button--small button--quiet sketch__example"
                      onClick={() => {
                        startRequest(example);
                      }}
                    >
                      {example}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="field__actions">
              <button
                type="button"
                className="button"
                onClick={() => {
                  afterEdit(adjust);
                }}
                disabled={!canSend}
              >
                <Icon name="sync" />
                {wording.CHANGE_WITH_REQUEST}
              </button>
              {refusal !== null && (
                <span className="sketch__refusal" id="sketch-prompt-error" role="alert">
                  {refusal}
                </span>
              )}
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={approving && !blocked}
        title={wording.APPROVE_SCENES_TITLE}
        confirmLabel={wording.APPROVE_SCENES_YES}
        cancelLabel={wording.GO_BACK}
        onConfirm={() => {
          setApproving(false);
          onApprove();
        }}
        onCancel={() => {
          setApproving(false);
        }}
      >
        <p>{wording.approveScenesFacts(count, sketch.totalSeconds)}</p>
        <p>{wording.APPROVE_SCENES_COST}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={asking !== null && edit !== null}
        title={wording.saveSceneFirst(edit?.number ?? 0)}
        confirmLabel={wording.SAVE}
        busyLabel={wording.SAVING}
        busy={busy === 'sketch'}
        alternative={{
          label: wording.DISCARD,
          onClick: () => {
            const next = asking;
            setAsking(null);
            leaveEdit();
            next?.then();
          },
        }}
        onConfirm={() => {
          const next = asking;
          void saveEdit().then((saved) => {
            setAsking(null);
            if (saved) next?.then();
          });
        }}
        onCancel={() => {
          setAsking(null);
        }}
      >
        <p>{wording.SCENE_UNSAVED}</p>
      </ConfirmDialog>
    </Panel>
  );
}

/**
 * Where the operator is among the approvals (US-105): the narration, the
 * scenes, and the video that is made once both are agreed. The step being
 * decided is highlighted and the ones behind it are ticked. A plan without a
 * `script` step has no narration to approve, so its strip starts at the scenes.
 */
function GateSteps({
  current,
  withNarration,
}: {
  readonly current: 'narration' | 'scenes';
  readonly withNarration: boolean;
}) {
  const steps = [
    ...(withNarration ? [{ key: 'narration', label: wording.GATE_STEP_NARRATION }] : []),
    { key: 'scenes', label: wording.GATE_STEP_SCENES },
    { key: 'video', label: wording.GATE_STEP_VIDEO },
  ];
  const at = steps.findIndex((step) => step.key === current);
  return (
    <ol className="planning-steps gate-steps" aria-label={wording.GATE_STEPS_LABEL}>
      {steps.map((step, index) => (
        <li
          key={step.key}
          className={`planning-steps__item planning-steps__item--${
            index < at ? 'done' : index === at ? 'current' : 'todo'
          }`}
          aria-current={index === at ? 'step' : undefined}
        >
          <span className="planning-steps__number" aria-hidden="true">
            {index < at ? <Icon name="check" /> : String(index + 1)}
          </span>
          <span>
            <span className="visually-hidden">{String(index + 1)} </span>
            {step.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** One on-screen text block while a card is edited; `key` keeps React's inputs apart. */
interface TextDraft {
  readonly key: number;
  readonly value: string;
}

/** A card's inputs, filled from the scene when **Edit** is pressed. */
interface SceneDraft {
  readonly name: string;
  readonly seconds: string;
  readonly text: readonly TextDraft[];
}

function draftOf(scene: SketchScene): SceneDraft {
  return {
    name: scene.name,
    seconds: String(scene.seconds),
    text: scene.text.map((value, key) => ({ key, value })),
  };
}

/** Whether the operator changed anything since **Edit this scene** was pressed (US-109). */
function sameDraft(a: SceneDraft, b: SceneDraft): boolean {
  return (
    a.name === b.name &&
    a.seconds === b.seconds &&
    a.text.length === b.text.length &&
    a.text.every((block, index) => block.value === b.text[index]?.value)
  );
}

/** The card being edited: its scene, the inputs as they were filled, and as they are now. */
interface SceneEditing {
  readonly number: number;
  readonly original: SceneDraft;
  readonly draft: SceneDraft;
}

/** Why a card's save was refused, and the input it is about (`null`: the card as a whole). */
interface EditRefusal {
  readonly text: string;
  readonly field: 'name' | 'seconds' | 'text' | null;
}

/** The input a refused scene edit is about, from the server's error code. */
function refusedField(cause: unknown): EditRefusal['field'] {
  if (!(cause instanceof ApiError)) return null;
  if (cause.code === 'invalid_name') return 'name';
  if (cause.code === 'invalid_seconds') return 'seconds';
  if (cause.code === 'invalid_text') return 'text';
  return null;
}

/** The "Last change" heading, where focus goes once a run has finished (US-111). */
const LAST_CHANGE_ID = 'sketch-last-change';

function textInputId(sceneNumber: number, key: number): string {
  return `sketch-scene-${String(sceneNumber)}-text-${String(key)}`;
}

function editButtonId(sceneNumber: number): string {
  return `sketch-scene-${String(sceneNumber)}-edit`;
}

/** How long a card picked on the timeline strip stays lit up. */
const SCENE_HIGHLIGHT_MS = 2000;

function sceneCardId(sceneNumber: number): string {
  return `sketch-scene-${String(sceneNumber)}`;
}

/**
 * The whole video as one bar of scenes (US-108): each segment is as wide as its
 * share of the total length, never narrower than 24px, and the bar scrolls
 * sideways when that does not fit. Picking a segment goes to its card.
 */
function SceneStrip({
  scenes,
  problems,
  onPick,
}: {
  readonly scenes: readonly SketchScene[];
  readonly problems: readonly SketchProblem[];
  readonly onPick: (sceneNumber: number) => void;
}) {
  if (scenes.length === 0) return null;
  return (
    <ol className="scene-strip" aria-label={wording.SCENE_STRIP_LABEL}>
      {scenes.map((scene) => {
        const troubled = problems.some((problem) => problem.sceneNumber === scene.number);
        return (
          <li
            key={scene.number}
            className="scene-strip__item"
            style={{ flexGrow: Math.max(scene.seconds, 0) }}
          >
            <button
              type="button"
              className={`scene-strip__segment${troubled ? ' scene-strip__segment--problem' : ''}`}
              aria-label={wording.sceneSegmentLabel(scene, troubled)}
              title={wording.sceneSegmentLabel(scene, troubled)}
              onClick={() => {
                onPick(scene.number);
              }}
            >
              {troubled && <Icon name="alert" className="scene-strip__alert" />}
              <span className="scene-strip__number">{scene.number}</span>
              <span className="scene-strip__name">{scene.name}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function SketchCard({
  scene,
  problems,
  canFix,
  onFix,
  draft,
  onDraft,
  refusal,
  canEdit,
  canSave,
  onEdit,
  onRequest,
  onCancel,
  onSave,
  highlighted,
}: {
  readonly scene: SketchScene;
  readonly problems: readonly SketchProblem[];
  /** Whether "Fix this for me" can be pressed now. */
  readonly canFix: boolean;
  readonly onFix: (fixPrompt: string) => void;
  /** The card's inputs while it is the one in edit mode; `null` shows the scene. */
  readonly draft: SceneDraft | null;
  readonly onDraft: (change: (current: SceneDraft) => SceneDraft) => void;
  /** Why the last save of this card was refused. */
  readonly refusal: EditRefusal | null;
  /** **Edit** can be pressed: the sketch is `ready` and nothing is running. */
  readonly canEdit: boolean;
  readonly canSave: boolean;
  /** The panel asks first when another card holds unsaved changes (US-109). */
  readonly onEdit: () => void;
  /** Start a request about this scene in the box under the cards (US-106). */
  readonly onRequest: () => void;
  readonly onCancel: () => void;
  readonly onSave: () => void;
  /** Picked on the timeline strip a moment ago (US-108). */
  readonly highlighted: boolean;
}) {
  const editing = draft !== null;

  const setText = (key: number, value: string): void => {
    onDraft((current) => ({
      ...current,
      text: current.text.map((block) => (block.key === key ? { key, value } : block)),
    }));
  };

  const addText = (): void => {
    onDraft((current) => ({
      ...current,
      text: [
        ...current.text,
        { key: Math.max(-1, ...current.text.map((block) => block.key)) + 1, value: '' },
      ],
    }));
  };

  const removeText = (key: number): void => {
    onDraft((current) => ({ ...current, text: current.text.filter((block) => block.key !== key) }));
  };

  const save = (event: FormEvent): void => {
    event.preventDefault();
    onSave();
  };

  const className = `sketch-card${problems.length > 0 ? ' sketch-card--problem' : ''}${
    editing ? ' sketch-card--editing' : ''
  }${scene.change === null ? '' : ` sketch-card--${scene.change}`}${
    highlighted ? ' sketch-card--highlighted' : ''
  }`;
  const changeBadge =
    scene.change === null ? null : (
      <Badge tone={scene.change === 'new' ? 'ready' : 'active'}>
        {wording.SCENE_CHANGE[scene.change]}
      </Badge>
    );
  const nameId = `sketch-scene-${String(scene.number)}-name`;
  const secondsId = `sketch-scene-${String(scene.number)}-seconds`;
  const errorId = `sketch-scene-${String(scene.number)}-error`;
  /** The error under the card, linked to the input it is about (US-111). */
  const describedBy = (field: NonNullable<EditRefusal['field']>) => {
    const about = refusal !== null && (refusal.field ?? 'name') === field;
    return {
      'aria-describedby': about ? errorId : undefined,
      'aria-invalid': about ? true : undefined,
    };
  };
  const title = (
    <h3 className="sketch-card__title">
      <span className="sketch-card__number">{wording.sceneLabel(scene.number)}</span>{' '}
      {scene.name}
    </h3>
  );

  const problemLines = problems.map((problem, index) => (
    <div key={index} className="sketch-card__problem" role="alert">
      <Icon name="alert" />
      <div>
        <SketchProblemLine problem={problem} canFix={canFix} onFix={onFix} />
      </div>
    </div>
  ));

  const visual = (
    <>
      <dt>{wording.SCENE_SHOWN}</dt>
      <dd>
        {scene.visual === null ? (
          <span className="muted">{wording.SCENE_SHOWN_UNKNOWN}</span>
        ) : (
          <>
            <strong>{wording.VISUAL_KIND[scene.visual.kind]}</strong>
            {scene.framing !== null && (
              <>
                {' '}
                <span className="sketch-card__shot" title={wording.SHOT_LABEL_TITLE}>
                  {wording.SHOT_FRAMING[scene.framing]}
                </span>
              </>
            )}
            {scene.visual.description === '' ? null : ` — ${scene.visual.description}`}
          </>
        )}
      </dd>
    </>
  );

  const narration = (
    <>
      <dt>{wording.SCENE_NARRATION}</dt>
      <dd>
        {scene.voiceover.length === 0 ? (
          <span className="muted">{wording.NO_NARRATION}</span>
        ) : (
          scene.voiceover.map((line, index) => (
            <p key={index} className="sketch-card__narration">
              {line}
            </p>
          ))
        )}
        {editing && <p className="sketch-card__hint">{wording.NARRATION_BY_REQUEST}</p>}
      </dd>
    </>
  );

  const placements =
    scene.placements.length > 0 ? (
      <>
        <dt>{wording.SCENE_ALSO}</dt>
        <dd>
          <ul className="sketch-card__placements">
            {scene.placements.map((placement) => (
              <li key={placement.stepId}>{wording.placementSentence(placement)}</li>
            ))}
          </ul>
        </dd>
      </>
    ) : null;

  if (draft !== null) {
    return (
      <li className={className} id={sceneCardId(scene.number)} tabIndex={-1}>
        <form onSubmit={save} aria-label={wording.editSceneLabel(scene.number)}>
          {title}
          <div className="sketch-card__head sketch-card__head--editing">
            <div className="field sketch-card__name">
              <label className="field__label" htmlFor={nameId}>
                {wording.sceneNameLabel(scene.number)}
              </label>
              <input
                id={nameId}
                className="field__input"
                value={draft.name}
                onChange={(event) => {
                  const name = event.target.value;
                  onDraft((current) => ({ ...current, name }));
                }}
                required
                autoFocus
                {...describedBy('name')}
              />
            </div>
            <div className="field sketch-card__seconds">
              <label className="field__label" htmlFor={secondsId}>
                {wording.SCENE_LENGTH}
              </label>
              <input
                id={secondsId}
                className="field__input field__input--narrow"
                type="number"
                inputMode="decimal"
                min="0.1"
                step="0.1"
                value={draft.seconds}
                onChange={(event) => {
                  const seconds = event.target.value;
                  onDraft((current) => ({ ...current, seconds }));
                }}
                required
                {...describedBy('seconds')}
              />
            </div>
          </div>
          {problemLines}
          <dl className="sketch-card__facts">
            {visual}
            <dt>{wording.TEXT_ON_SCREEN}</dt>
            <dd>
              <ul className="sketch-card__text-edits">
                {draft.text.map((block, index) => (
                  <li key={block.key} className="sketch-card__text-edit">
                    <label
                      className="field__label sketch-card__text-label"
                      htmlFor={textInputId(scene.number, block.key)}
                    >
                      {wording.textOnScreenLabel(index + 1)}
                    </label>
                    <input
                      id={textInputId(scene.number, block.key)}
                      className="field__input"
                      value={block.value}
                      {...describedBy('text')}
                      onChange={(event) => {
                        setText(block.key, event.target.value);
                      }}
                    />
                    <button
                      type="button"
                      className="button button--small button--quiet button--danger button--icon"
                      aria-label={wording.removeTextLabel(index + 1)}
                      title={wording.removeTextLabel(index + 1)}
                      onClick={() => {
                        removeText(block.key);
                      }}
                    >
                      <Icon name="trash" />
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" className="button button--small" onClick={addText}>
                <Icon name="plus" />
                {wording.ADD_TEXT}
              </button>
            </dd>
            {narration}
            {placements}
          </dl>
          <div className="sketch-card__actions">
            <button type="submit" className="button button--small button--primary" disabled={!canSave}>
              <Icon name="check" />
              {wording.SAVE}
            </button>
            <button type="button" className="button button--small button--quiet" onClick={onCancel}>
              {wording.CANCEL}
            </button>
            {refusal !== null && (
              <span className="sketch__refusal" id={errorId} role="alert">
                {refusal.text}
              </span>
            )}
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className={className} id={sceneCardId(scene.number)} tabIndex={-1}>
      <div className="sketch-card__head">
        {title}
        {changeBadge}
        <span className="sketch-card__time">
          {wording.sceneTiming(scene.seconds, scene.startsAt)}
          {scene.plannedSeconds !== null && scene.plannedSeconds !== scene.seconds && (
            <span className="muted">{` · ${TIMING_WORDS.planned(scene.plannedSeconds)}`}</span>
          )}
          {scene.voicedSeconds !== null && scene.voicedSeconds !== scene.seconds && (
            <span className="muted">{` · ${TIMING_WORDS.voiced(scene.voicedSeconds)}`}</span>
          )}
          <button
            id={editButtonId(scene.number)}
            type="button"
            className="button button--small button--quiet sketch-card__edit"
            onClick={onEdit}
            disabled={!canEdit}
            aria-label={wording.editSceneLabel(scene.number)}
          >
            <Icon name="pencil" />
            {wording.EDIT_THIS_SCENE}
          </button>
          <button
            type="button"
            className="button button--small button--quiet sketch-card__edit"
            onClick={onRequest}
            aria-label={wording.changeSceneLabel(scene.number)}
          >
            <Icon name="sync" />
            {wording.CHANGE_THIS_SCENE}
          </button>
        </span>
      </div>
      {problemLines}
      <dl className="sketch-card__facts">
        {visual}
        <dt>{wording.TEXT_ON_SCREEN}</dt>
        <dd>
          {scene.text.length === 0 ? (
            <span className="muted">{wording.NO_TEXT_ON_SCREEN}</span>
          ) : (
            scene.text.map((block, index) => (
              <p key={index} className="sketch-card__text">
                {block}
              </p>
            ))
          )}
        </dd>
        {narration}
        {placements}
      </dl>
    </li>
  );
}

/**
 * What the scene timings follow, in the operator's words (synthesis C3). Kept
 * with the one panel that says them.
 */
const TIMING_WORDS = {
  VOICE: 'The scenes follow the recorded voice: each one starts just before its words are said.',
  PLAN: 'The scenes keep the timings from the plan.',
  LISTEN: 'Listen to the voice the scenes are timed to.',
  take: (speaker: string | null): string => (speaker === null ? 'Recorded voice' : `Recorded voice: ${speaker}`),
  plannedTotal: (seconds: number): string => `The plan said ${wording.length(seconds)}.`,
  planned: (seconds: number): string => `planned ${wording.length(seconds)}`,
  voiced: (seconds: number): string => `the voice gave it ${wording.length(seconds)}`,
} as const;

/**
 * Where the scene seconds come from, and the take to hear (synthesis C3): once
 * the voice is recorded the gate opens on it, the scenes are cut to where its
 * lines fall, and the operator hears what they were cut to before approving.
 * When the voice moved the length far from the plan, that is said here rather
 * than left to be noticed; when the plan keeps its own timings, the reason is.
 */
function SketchTiming({ sketch }: { readonly sketch: SketchView }) {
  const voice = sketch.timingSource === 'voice';
  if (!voice && sketch.timingReason === null && sketch.takes.length === 0) return null;
  return (
    <section className="sketch__timing" aria-label={TIMING_WORDS.LISTEN}>
      <Notice kind={sketch.timingWarning !== null ? 'warn' : 'info'}>
        <p>
          {voice ? TIMING_WORDS.VOICE : TIMING_WORDS.PLAN}
          {voice && sketch.plannedTotalSeconds !== null && sketch.plannedTotalSeconds !== sketch.totalSeconds
            ? ` ${TIMING_WORDS.plannedTotal(sketch.plannedTotalSeconds)}`
            : ''}
          {!voice && sketch.timingReason !== null ? ` ${sketch.timingReason}` : ''}
        </p>
        {sketch.timingWarning !== null && <p>{sketch.timingWarning}</p>}
        {sketch.takes.map((take) => (
          <p key={take.url} className="sketch__take">
            <span className="muted">{TIMING_WORDS.take(take.speaker)}</span>{' '}
            <audio controls preload="none" src={take.url} aria-label={TIMING_WORDS.take(take.speaker)} />
          </p>
        ))}
      </Notice>
    </section>
  );
}

/**
 * One timing problem: the plain sentence, the button that asks for it to be
 * fixed, and the technical message behind "Show details" (US-104).
 */
function SketchProblemLine({
  problem,
  canFix,
  onFix,
}: {
  readonly problem: SketchProblem;
  readonly canFix: boolean;
  readonly onFix: (fixPrompt: string) => void;
}) {
  return (
    <>
      <p>
        <span className="visually-hidden">{wording.PROBLEM_PREFIX}</span>
        {problem.plain}
      </p>
      <div className="sketch__fix">
        <button
          type="button"
          className="button button--small"
          onClick={() => {
            onFix(problem.fixPrompt);
          }}
          disabled={!canFix}
        >
          <Icon name="sync" />
          {wording.FIX_THIS}
        </button>
      </div>
      <TechnicalDetails>{problem.message}</TechnicalDetails>
    </>
  );
}

/**
 * The technical reading of a problem or a failure, folded away: the operator
 * reads the plain sentence above it, and a technical operator loses nothing.
 */
function TechnicalDetails({ children }: { readonly children: ReactNode }) {
  return (
    <details className="disclosure sketch__details">
      <summary className="disclosure__summary">
        <Icon name="chevron-right" />
        {wording.SHOW_DETAILS}
      </summary>
      <div className="disclosure__body sketch__technical">{children}</div>
    </details>
  );
}

/* ------------------------------------------------------------ the planning */

/**
 * The planning conversation: an interactive `claude` in the content's own
 * container, in `/workspace`.
 *
 * The server owns the PTY, so the terminal survives a reload and is rejoined
 * rather than restarted — which is also why **Resume planning** is the label
 * once a plan exists: the conversation starts from the plan on disk with the
 * edit prompt, and never renumbers a step out from under a production.
 *
 * Above the steps, once one has started, the same conversation without the
 * terminal (US-023): the planner's latest message and a box to answer it.
 */
function PlanningPanel({
  content,
  planning,
  brandBook,
  failed,
  busy,
  onStart,
  onStop,
}: {
  readonly content: { readonly id: string; readonly name: string; readonly containerId: string | null };
  readonly planning: Planning | null;
  /** The book the plan is made in; its name links to the page that refreshes it. */
  readonly brandBook: ContentBrandBook | null;
  /** Failed at setup: there is nothing to plan in until the clone is retried. */
  readonly failed: boolean;
  readonly busy: Busy;
  readonly onStart: () => void;
  readonly onStop: () => void;
}) {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const terminalId = planning?.terminalId ?? null;
  const running = planning?.running === true;
  const resuming = planning?.nextMode === 'edit';
  const brand = planning?.brand ?? null;
  const [markingReady, setMarkingReady] = useState(false);
  // What the operator types while planning is learned from in the background
  // (the ad bible US-026), so the rules it taught are read again every half
  // minute while the conversation runs, and once when it stops.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      setTick((n) => n + 1);
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, [running]);
  const taught = useTaughtRules(brandBook?.id ?? null, content.id, `${String(running)}:${String(tick)}`);

  return (
    <Panel
      className={terminalId === null ? undefined : 'panel--terminal'}
      title="Planning"
      icon="terminal"
      meta={
        <Badge tone={running ? 'active' : 'neutral'}>
          {running ? 'conversation running' : 'not running'}
        </Badge>
      }
      actions={
        failed ? undefined : running ? (
          <button
            type="button"
            className="button button--quiet"
            onClick={onStop}
            disabled={busy !== null}
          >
            {busy === 'planning' ? 'Working…' : 'Stop planning'}
          </button>
        ) : (
          <button type="button" className="button" onClick={onStart} disabled={busy !== null}>
            <Icon name="terminal" />
            {busy === 'planning'
              ? brand?.waiting === true
                ? 'Reading the brand profile…'
                : 'Working…'
              : resuming
                ? 'Resume planning'
                : 'Start planning'}
          </button>
        )
      }
    >
      {!failed && (
        <PlanningConversation
          contentId={content.id}
          canStart={content.containerId !== null}
          whenIdle="hide"
          onMarkReady={() => {
            setMarkingReady(true);
          }}
        />
      )}
      <SavedAsHouseRules
        rules={taught.fromPlanning}
        busy={taught.busy}
        onDelete={taught.remove}
        title="Learned while planning, saved as house rules:"
      />
      {markingReady && (
        <MarkReadyDialog
          content={content}
          onClose={() => {
            setMarkingReady(false);
          }}
          // The page reads the content again on its own beat.
          onDone={() => Promise.resolve()}
        />
      )}
      {!failed && <PlanningSteps current={planningStep(planning)} />}
      {/* Why pressing Start takes a minute, and what it would otherwise plan
          without: the extraction of its brand book that its creation began (US-071). */}
      {brand !== null && brand.extracting.length > 0 ? (
        <Loader label={`Reading the brand profile of brand book ${brand.extracting.join(', ')}…`} />
      ) : (
        <BusyLoader
          busy={busy}
          kinds={['planning']}
          label={
            running
              ? 'Stopping the planning conversation…'
              : brand?.waiting === true
                ? `Reading the brand profile of ${brandBook === null ? 'the brand book' : `brand book ${brandBook.name}`} before the conversation starts…`
                : 'Starting the planning conversation in the container…'
          }
        />
      )}
      {brand?.warning !== null && brand?.warning !== undefined && (
        <Notice kind="warn">
          <strong>Planning goes ahead without a fresh brand profile.</strong> {brand.warning} Press{' '}
          <strong>Refresh</strong> on{' '}
          {brandBook === null ? (
            'the brand book'
          ) : (
            <>
              brand book{' '}
              <Link className="link" href={brandBookPath(brandBook.id)}>
                {brandBook.name}
              </Link>
            </>
          )}{' '}
          to try the extraction again.
        </Notice>
      )}
      {failed ? (
        <p className="muted">
          Setup did not finish, so there is no container to plan in yet. Retry it from the card
          above; planning starts from the clones it makes.
        </p>
      ) : !desktop ? (
        <>
          <p className="muted">
            Planning runs in a terminal that needs a wider screen. It keeps running on the server;
            come back on a desktop to answer Claude’s questions.
          </p>
          {running && <Loader label="The planning conversation is running on the server…" />}
        </>
      ) : terminalId === null ? (
        <p className="muted">
          {resuming
            ? 'A plan is already written. Resuming opens the conversation on it with the edit prompt: steps are changed and added, never renumbered.'
            : 'Planning is a conversation. It reads the clones, asks a handful of questions and writes the plan; nothing is produced until you mark the content ready.'}{' '}
          It runs in <code className="mono">/workspace</code> inside this content’s own container,
          so closing this tab does not interrupt it.
        </p>
      ) : (
        <Suspense fallback={<Skeleton lines={8} />}>
          <TerminalPane terminalId={terminalId} size="tall" />
        </Suspense>
      )}
      {planning !== null && !running && planning.exitCode !== null && (
        <p className="field__hint">
          The last conversation exited with code {String(planning.exitCode)}.{' '}
          {planning.plan.exists
            ? 'The plan it wrote is under Advanced details.'
            : 'It wrote no plan; starting again begins a fresh conversation.'}
        </p>
      )}
    </Panel>
  );
}

/** The three things the operator does in planning, in order (US-012). */
const PLANNING_STEPS = [
  'Start planning',
  'Answer Claude’s questions in the terminal below',
  'Mark ready when the plan is written',
] as const;

/**
 * Which of `PLANNING_STEPS` is the operator's now, read off the plan state:
 * a plan that parses is waiting to be marked, a running session is waiting
 * for answers, and anything else is waiting to be started.
 */
function planningStep(planning: Planning | null): 0 | 1 | 2 {
  if (planning?.plan.parses === true) return 2;
  if (planning?.running === true) return 1;
  return 0;
}

function PlanningSteps({ current }: { readonly current: 0 | 1 | 2 }) {
  return (
    <ol className="planning-steps" aria-label="How planning works">
      {PLANNING_STEPS.map((label, index) => (
        <li
          key={label}
          className={`planning-steps__item planning-steps__item--${
            index < current ? 'done' : index === current ? 'current' : 'todo'
          }`}
          aria-current={index === current ? 'step' : undefined}
        >
          <span className="planning-steps__number" aria-hidden="true">
            {index < current ? <Icon name="check" /> : String(index + 1)}
          </span>
          <span>{label}</span>
        </li>
      ))}
    </ol>
  );
}

/**
 * The plan file, as the server last read it: whether it is there, when it was
 * written, how many steps it has and everything the parser could not accept.
 *
 * It is in Advanced details (US-115): the next-step card already says whether
 * the plan is written, and a plan with problems says so in a notice above the
 * section, with a link that opens it here. Once it parses it carries its own **Mark ready**, which opens the
 * same cost-estimate dialog as the next-step card (US-012).
 */
function PlanPanel({
  planning,
  readyErrors,
  busy,
  onMarkReady,
}: {
  readonly planning: Planning | null;
  readonly readyErrors: readonly PlanProblem[] | null;
  readonly busy: Busy;
  readonly onMarkReady: () => void;
}) {
  if (planning === null) {
    return (
      <Panel id="plan" title="The plan" icon="tasklist">
        <Skeleton lines={3} />
      </Panel>
    );
  }

  const plan: PlanStatus = planning.plan;
  // The errors a refused "Mark ready" came back with are the same ones the
  // poll reports; showing the fresher of the two keeps the panel from
  // contradicting the toast the operator has just read.
  const errors = readyErrors ?? plan.errors;
  const state = !plan.exists ? 'none' : plan.parses && errors.length === 0 ? 'ready' : 'problems';
  // A plan that exists but does not parse has at least one problem, even if
  // the parser reported it as nothing more than "does not parse".
  const problems = Math.max(errors.length, 1);

  return (
    <Panel
      id="plan"
      title="The plan"
      icon="tasklist"
      meta={
        <Badge tone={state === 'ready' ? 'ready' : state === 'problems' ? 'danger' : 'neutral'}>
          {state === 'ready'
            ? 'Ready to mark'
            : state === 'problems'
              ? `${String(problems)} ${problems === 1 ? 'problem' : 'problems'}`
              : 'No plan yet'}
        </Badge>
      }
    >
      <Facts
        items={[
          { label: 'File', value: plan.path, mono: true },
          {
            label: 'Written',
            value: plan.updatedAt === null ? <span className="muted">never</span> : since(plan.updatedAt),
          },
          {
            label: 'Steps',
            value: plan.exists ? String(plan.stepCount) : <span className="muted">none yet</span>,
          },
          ...(plan.sceneCount === 0
            ? []
            : [{ label: 'Scenes', value: String(plan.sceneCount) }]),
        ]}
      />

      {!plan.exists && (
        <p className="field__hint">
          The conversation writes <code className="mono">{plan.path}</code> itself. This panel
          re-reads it every few seconds, so it fills in while you watch.
        </p>
      )}

      <Decisions decisions={plan.decisions} />

      <Claims claims={plan.claims} repairs={plan.repairs} />

      <PlanProblems title="Errors" problems={errors} />
      <PlanProblems title="Warnings" problems={plan.warnings} warn />

      <MergedRulesList rules={plan.rules} />

      {state === 'ready' && (
        <>
          <p className="field__hint">
            {plan.stepCount === 1 ? 'One step' : `${String(plan.stepCount)} steps`}, no errors.{' '}
            <strong>Mark ready</strong> turns them into the content’s steps.
          </p>
          <div>
            <button
              type="button"
              className="button button--primary"
              onClick={onMarkReady}
              disabled={busy !== null}
            >
              <Icon name="check" />
              {busy === 'ready' ? 'Working…' : 'Mark ready'}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

/**
 * The rules in force for this content, in the precedence `rules/limits.json`
 * states: the built-in tone and format files, then whatever Settings overrides,
 * then the plan's own `**Rules:**` line — each value clamped to the hard floors.
 *
 * It is here because every arithmetic refusal beside it quotes one of these
 * numbers *and* says where it came from; without the table the operator has to
 * guess which of the three layers to go and edit.
 */
function MergedRulesList({ rules }: { readonly rules: readonly MergedRule[] }) {
  if (rules.length === 0) return null;

  const overridden = rules.filter((rule) => rule.origin !== 'built-in').length;
  return (
    <details className="plan-rules">
      <summary>
        Rules in force{' '}
        <span className="muted">
          ({String(rules.length)} fields
          {overridden === 0 ? ', all built-in' : `, ${String(overridden)} overridden`})
        </span>
      </summary>
      <ul className="plan-rules__list">
        {rules.map((rule) => (
          <li className="plan-rules__item" key={rule.field}>
            <code className="mono">{rule.field}</code>
            <strong className="plan-rules__value">{rule.value}</strong>
            <span className="plan-rules__source">
              {rule.source}
              {rule.clampedFrom !== null && ` — clamped from ${rule.clampedFrom}`}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

/* --------------------------------------------------------- the cost estimate */

/**
 * Roughly what producing this plan will cost, before anything is spent.
 *
 * The prices are a static sheet on the server (`gateway/prices.ts`), read from
 * the research behind the plan, so this is an order of magnitude and not a
 * quote — which is why every assumption behind the figure is one disclosure
 * away, and why an estimate over the cap is a warning rather than a refusal.
 *
 * The panel itself shows one total and one sentence (US-115); the arithmetic
 * per step, the cap and the assumptions are behind **Show details**.
 */
function EstimatePanel({
  estimate,
  kind,
}: {
  readonly estimate: CostEstimate;
  readonly kind: ContentData['kind'];
}) {
  return (
    <Panel title="Estimated cost" icon="tag" className="panel--aside">
      <p className="estimate-total">
        <strong className="estimate-total__amount mono">{money(estimate.usd)}</strong>
        <span className="muted">{wording.estimatedCostSentence(kind)}</span>
      </p>
      {estimate.overCap && <Notice kind="warn">{wording.overCapNotice(estimate.capUsd ?? 0)}</Notice>}
      <TechnicalDetails>
        <EstimateSummary estimate={estimate} withWarning={false} />
      </TechnicalDetails>
    </Panel>
  );
}

/**
 * What the production has cost on the Claude subscription so far (US-036).
 *
 * Kept apart from the estimate above on purpose. That is the **provider
 * ledger**: real money, billed by ElevenLabs and OpenRouter, measured against
 * the spend cap, and a step is refused when it would go over. This is what the
 * Claude CLI reported for the agent runs themselves — usage on a subscription
 * nobody is invoiced per-step for — so it is labelled an estimate, it is never
 * added to the ledger, and no step is ever refused over it.
 *
 * Only steps that have actually run report anything, so the panel appears with
 * the first finished step and grows from there. A step whose agent was reaped
 * before it could print its `result` event reports nothing at all, which is
 * why the footnote says how many of the finished steps are behind the figure.
 */
function ClaudeUsagePanel({ steps }: { readonly steps: readonly PlanStep[] }) {
  const reported = steps.filter((step) => step.claude !== null);
  if (reported.length === 0) return null;

  const ran = steps.filter((step) => step.startedAt !== null).length;
  const usd = sum(reported.map((step) => step.claude?.costUsd ?? null));
  const turns = sum(reported.map((step) => step.claude?.numTurns ?? null));
  const tokens = sum(reported.map((step) => step.claude?.tokens?.total ?? null));
  const ms = sum(reported.map((step) => step.durationMs));

  return (
    <Panel
      title="Claude usage (estimate)"
      icon="pulse"
      id="claude-usage"
      collapsible
      defaultOpen={false}
      summary={`${usd === null ? 'not reported' : claudeMoney(usd)} over ${String(reported.length)} ${
        reported.length === 1 ? 'step' : 'steps'
      }`}
      meta={usd === null ? undefined : <Badge tone="neutral">{claudeMoney(usd)}</Badge>}
    >
      <Facts
        items={[
          {
            label: 'Claude (estimate)',
            value: (
              <strong className="mono">{usd === null ? 'not reported' : claudeMoney(usd)}</strong>
            ),
          },
          ...(turns === null
            ? []
            : [{ label: 'Turns', value: String(turns), mono: true }]),
          ...(tokens === null
            ? []
            : [{ label: 'Tokens', value: tokenCount(tokens), mono: true }]),
          ...(ms === null ? [] : [{ label: 'Agent time', value: elapsed(ms), mono: true }]),
          {
            label: 'Steps counted',
            value: `${String(reported.length)} of ${String(ran)}`,
            mono: true,
          },
        ]}
      />
      <p className="field__hint">
        What the Claude CLI reported for the agent runs themselves, at API prices. It is
        subscription usage, not a bill: it is <strong>not</strong> part of the estimated cost
        beside it and nothing is counted against the spend cap for it.
        {reported.length < ran &&
          ` ${String(ran - reported.length)} ${ran - reported.length === 1 ? 'step' : 'steps'} reported nothing — an agent that is stopped mid-turn never says what it used.`}
      </p>
    </Panel>
  );
}

/** Adds up the figures that were reported; `null` when none of them were. */
function sum(values: readonly (number | null)[]): number | null {
  const reported = values.filter((value): value is number => value !== null);
  return reported.length === 0 ? null : reported.reduce((total, value) => total + value, 0);
}

/**
 * The estimate itself: the arithmetic per step type, the cap it was measured
 * against, and the assumptions. Rendered in the aside while a content is being
 * planned, and again in the **Mark ready** dialog, so the figure the operator
 * agrees to is the one they have been watching.
 */
function EstimateSummary({
  estimate,
  withWarning = true,
}: {
  readonly estimate: CostEstimate;
  /** Off where the panel around it already says the estimate is over the cap. */
  readonly withWarning?: boolean;
}) {
  const capLabel = estimate.capSource === 'content' ? 'this content’s spend cap' : 'the default spend cap in Settings';

  return (
    <>
      {withWarning && estimate.overCap && (
        <Notice kind="warn">
          <strong>
            {money(estimate.usd)} is over {capLabel} of {money(estimate.capUsd ?? 0)}.
          </strong>{' '}
          This is a warning, not a refusal: the plan can be marked ready and produced either way,
          and the gateway is what actually stops a call that would go over. Raise the cap on this
          content, or let the conversation plan fewer provider steps.
        </Notice>
      )}

      {estimate.lines.length === 0 ? (
        <p className="field__hint">
          {estimate.totalSteps === 0
            ? 'Nothing to price yet: the estimate fills in as the plan does.'
            : 'No step here calls a provider, so producing it costs nothing beyond Claude’s own time.'}
        </p>
      ) : (
        <Facts
          items={[
            ...estimate.lines.map((line) => ({
              label: line.label ?? STEP_LABEL[line.type],
              value: (
                <>
                  <span className="mono">{money(line.usd)}</span>{' '}
                  <span className="muted">{line.basis}</span>
                </>
              ),
            })),
            {
              label: 'Estimate',
              value: <strong className="mono">{money(estimate.usd)}</strong>,
            },
            {
              label: estimate.capSource === 'content' ? 'Spend cap' : 'Default cap',
              value:
                estimate.capUsd === null ? (
                  <span className="muted">none</span>
                ) : (
                  <span className="mono">{money(estimate.capUsd)}</span>
                ),
            },
          ]}
        />
      )}

      <details className="disclosure">
        <summary className="disclosure__summary">
          <Icon name="chevron-right" />
          What this assumes
        </summary>
        <div className="disclosure__body">
          <ul className="estimate-assumptions">
            {estimate.assumptions.map((assumption) => (
              <li key={assumption}>{assumption}</li>
            ))}
          </ul>
        </div>
      </details>
    </>
  );
}

/** A USD figure as every panel on this page prints it. */
function money(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

/**
 * Claude's own figure, which for a single step is routinely under a cent.
 *
 * {@link money} is the gateway ledger's format and rounds a $0.004 step to
 * `$0.00`; a subscription total that reads as nothing is worse than four
 * decimals, so this keeps them until the figure is worth whole cents.
 */
function claudeMoney(usd: number): string {
  return usd >= 0.995 ? money(usd) : `$${usd.toFixed(4)}`;
}

/** A token count at a glance: `412`, `118k`, `2.9M`. */
function tokenCount(tokens: number): string {
  if (tokens < 1000) return String(tokens);
  if (tokens < 1_000_000) return `${String(Math.round(tokens / 1000))}k`;
  return `${(tokens / 1_000_000).toFixed(1)}M`;
}

/** A parse error or a warning, each with the line of the plan it is on. */
function PlanProblems({
  title,
  problems,
  warn = false,
}: {
  readonly title: string;
  readonly problems: readonly PlanProblem[];
  readonly warn?: boolean;
}) {
  if (problems.length === 0) return null;
  return (
    <div className={warn ? 'plan-problems plan-problems--warn' : 'plan-problems'}>
      <p className="plan-problems__title">
        {problems.length} {problems.length === 1 ? title.replace(/s$/, '') : title}
      </p>
      <ul className="plan-problems__list">
        {problems.map((problem, position) => (
          <li key={`${String(problem.line)}-${String(position)}`}>
            {problem.line > 0 && <span className="plan-problems__line mono">line {problem.line}</span>}
            {problem.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ----------------------------------------------------------- the steps */

/**
 * What each type of step produces, as an icon and the word its group is
 * headed with.
 *
 * Octicons has no musical note and no waveform, so `music` borrows the
 * activity line and `sfx` the zap; every other type has something literal.
 * The vocabulary itself is the server's — a type the page did not expect
 * would be a compile error here, not a blank icon in production.
 */
const STEP_ICON: Record<StepType, IconName> = {
  research: 'telescope',
  brand: 'paintbrush',
  capture: 'device-camera',
  image: 'image',
  animate: 'play',
  talk: 'comment',
  script: 'file',
  voiceover: 'unmute',
  music: 'pulse',
  sfx: 'zap',
  clip: 'device-camera-video',
  edit: 'pencil',
  captions: 'quote',
  compose: 'stack',
  render: 'package',
  review: 'eye',
  copy: 'comment',
  variant: 'copy',
};

const STEP_LABEL: Record<StepType, string> = {
  research: 'Research',
  brand: 'Brand',
  capture: 'Capture',
  image: 'Images',
  animate: 'Animations',
  talk: 'Talking head',
  script: 'Narration',
  voiceover: 'Voiceover',
  music: 'Music',
  sfx: 'Sound effects',
  clip: 'Clips',
  edit: 'Edits',
  captions: 'Captions',
  compose: 'Composition',
  render: 'Renders',
  review: 'Review',
  copy: 'Share copy',
  variant: 'Variants',
};

const STEP_STATUS_LABEL: Record<StepStatus, string> = {
  todo: 'to do',
  'in-progress': 'running',
  done: 'done',
};

/**
 * The plan as a list of steps, grouped by what they produce, with the file
 * itself underneath.
 *
 * Grouping is the point of this panel: the question it answers is "what will
 * this content actually produce?", and eight renders and two voiceovers read
 * as two answers rather than ten. The groups sit in the order production
 * reaches them — a group where its first step falls — so reading down the
 * panel is still reading the plan in the order it runs.
 */
function StepsPanel({
  plan,
  current,
  id,
  title,
  collapsible = false,
}: {
  readonly plan: ContentPlan | null;
  /** The step the loop is on right now, so the list can time it (US-035). */
  readonly current: string | null;
  readonly id: string;
  /** "What will be produced" as the `ready` stage panel, "Plan steps" in Details. */
  readonly title: string;
  readonly collapsible?: boolean;
}) {
  // A step that is running is timed from its own `startedAt`, so the panel
  // needs a clock as well as the poll — a duration that only moves every three
  // seconds reads as a page that has stopped.
  const now = useNow();
  // One `Panel` for both branches, so a folded panel keeps its state when the
  // plan arrives.
  const frame = {
    id,
    title,
    icon: 'tasklist' as const,
    collapsible,
    defaultOpen: false,
  };
  if (plan === null) {
    return (
      <Panel {...frame} summary="reading…">
        <Skeleton lines={6} />
      </Panel>
    );
  }

  const steps = plan.steps;
  const done = steps.filter((step) => step.status === 'done').length;
  const paid = steps.filter((step) => step.usesProvider).length;

  return (
    <Panel
      {...frame}
      summary={
        steps.length === 0
          ? 'no steps yet'
          : `${String(done)} of ${String(steps.length)} steps done`
      }
      meta={
        steps.length === 0 ? undefined : (
          <span className="panel__meta mono">
            {done}/{steps.length} done
          </span>
        )
      }
    >
      {steps.length === 0 ? (
        <p className="muted">
          No steps yet. The planning conversation writes them into{' '}
          <code className="mono">{plan.plan.path}</code>, and this panel lists them as soon as the
          file parses.
        </p>
      ) : (
        <>
          <p className="field__hint">
            {plan.source === 'plan' ? (
              <>
                The steps <code className="mono">{plan.plan.path}</code> declares right now.{' '}
                <strong>Mark ready</strong> turns them into this content’s steps; until then the
                conversation can still change them.
              </>
            ) : (
              <>
                The steps this content produces, in the order production runs them. Their statuses
                are the production’s, not the file’s.
              </>
            )}{' '}
            {paid === 0
              ? 'None of them calls a provider, so producing this costs nothing beyond Claude.'
              : `${String(paid)} of them ${paid === 1 ? 'calls' : 'call'} a provider, so ${
                  paid === 1 ? 'it counts' : 'they count'
                } against the spend cap.`}
          </p>

          {groupSteps(steps).map((group) => (
            <section className="plan-group" key={group.type}>
              <h3 className="plan-group__title">
                <Icon name={STEP_ICON[group.type]} />
                {STEP_LABEL[group.type]}
                <span className="plan-group__count mono">{group.steps.length}</span>
              </h3>
              <ol className="plan-steps">
                {group.steps.map((step) => (
                  <StepRow
                    step={step}
                    running={step.stepId === current && step.status === 'in-progress'}
                    now={now}
                    key={step.stepId}
                  />
                ))}
              </ol>
            </section>
          ))}
        </>
      )}

      {plan.markdown !== null && (
        <details className="disclosure">
          <summary className="disclosure__summary">
            <Icon name="chevron-right" />
            The plan as written
          </summary>
          <div className="disclosure__body">
            <p className="field__hint">
              <code className="mono">{plan.plan.path}</code>, exactly as it is on the data volume.
              Everything above is read out of it.
            </p>
            <Markdown text={plan.markdown} />
          </div>
        </details>
      )}
    </Panel>
  );
}

/**
 * One step: what it is, when it runs, where it lands and what it will cost —
 * and, once production has reached it, how it went (US-035).
 *
 * The aside reads left to right as the production's own account of the step:
 * how long it took (or how long it has been going), how many of its three
 * attempts are gone, what the probe made of its outputs, and the status that
 * follows from all three.
 */
function StepRow({
  step,
  running,
  now,
}: {
  readonly step: PlanStep;
  /** The step the loop is producing right now, timed from its `startedAt`. */
  readonly running: boolean;
  readonly now: number;
}) {
  const timing = stepTiming(step, running, now);
  const probe = probeSummary(step.probe);
  return (
    <li className={`plan-step plan-step--${step.status}${running ? ' plan-step--running' : ''}`}>
      <span className="plan-step__marker" title={STEP_LABEL[step.type]}>
        <Icon name={STEP_ICON[step.type]} />
      </span>
      <span className="plan-step__id mono">{step.stepId}</span>
      <span className="plan-step__title">{step.title}</span>
      <span className="plan-step__aside">
        {step.usesProvider && (
          <Badge tone="wait" title="This step generates its output through a paid provider">
            uses a provider
          </Badge>
        )}
        {timing !== null && (
          <span
            className="plan-step__duration mono"
            title={running ? 'Running for' : 'How long the last attempt took'}
          >
            {timing}
          </span>
        )}
        {step.attempts > 0 && (
          <span className="plan-step__attempts mono" title="Production attempts so far">
            ×{step.attempts}
          </span>
        )}
        {probe !== null && (
          <Badge tone={probe.tone} title={probe.title}>
            {probe.label}
          </Badge>
        )}
        <span className="plan-step__priority mono" title="Lower priorities run first">
          P{step.priority}
        </span>
        <Badge tone={STEP_TONE[step.status]} pulse={step.status === 'in-progress'}>
          {STEP_STATUS_LABEL[step.status]}
        </Badge>
      </span>
      {step.outputs.length > 0 && (
        <ul className="plan-step__outputs">
          {step.outputs.map((output) => (
            <OutputRow key={output} path={output} probe={probeFor(step, output)} />
          ))}
          {(step.probe?.checks ?? [])
            .filter((check) => !check.ok)
            .map((check) => (
              <li key={check.name}>
                <span className="mono">{check.name}</span>
                <span className="plan-step__problem">
                  exited {check.exitCode === null ? 'on its timeout' : String(check.exitCode)}
                  {check.output === '' ? '' : ` — ${firstLine(check.output)}`}
                </span>
              </li>
            ))}
        </ul>
      )}
    </li>
  );
}

/**
 * One declared output, with what the probe measured about it (PLAN §8).
 *
 * The path is the promise; the measurements are the evidence. They sit on the
 * same line because the question the panel answers is "is this the file the
 * plan asked for?", and a duration next to the name answers it at a glance —
 * while a probe error is the reason the step is not done, so it is drawn as a
 * failure rather than as more detail.
 */
function OutputRow({
  path,
  probe,
}: {
  readonly path: string;
  readonly probe: OutputProbe | null;
}) {
  const facts = probe === null ? [] : probeFacts(probe);
  return (
    <li>
      <span className="mono">{path}</span>
      {facts.length > 0 && <span className="plan-step__measured">{facts.join(' · ')}</span>}
      {(probe?.errors ?? []).map((error) => (
        <span className="plan-step__problem" key={error}>
          {error}
        </span>
      ))}
    </li>
  );
}

/**
 * How long this step took, or how long the one running has been going.
 *
 * A running step is timed from `startedAt` against the browser's clock, which
 * is a few seconds' worth of drift at worst and is the only honest reading
 * while the attempt is still in flight; a finished one shows the duration the
 * server measured for its last attempt.
 */
function stepTiming(step: PlanStep, running: boolean, now: number): string | null {
  if (running && step.startedAt !== null) return elapsed(now - Date.parse(step.startedAt));
  return step.durationMs === null ? null : elapsed(step.durationMs);
}

/**
 * The probe report as one badge: what the server checked about this step's
 * outputs, and whether it was clean (PLAN §8).
 *
 * The detail is under the step — the measurements on each output and the
 * errors that came with them — so this is only the verdict, which is what the
 * list is read for: a step that claims to be done with a dirty report is why
 * it went back to `in-progress`.
 */
function probeSummary(
  probe: ProbeReport | null,
): {
  readonly tone: 'ready' | 'danger';
  readonly label: string;
  readonly title: string;
  readonly problems: number;
} | null {
  if (probe === null) return null;
  const problems =
    probe.outputs.filter((output) => !output.ok).length +
    probe.checks.filter((check) => !check.ok).length;
  const measured = `${String(probe.outputs.length)} ${
    probe.outputs.length === 1 ? 'output' : 'outputs'
  }`;
  if (probe.ok) {
    return {
      tone: 'ready',
      problems,
      label: `probe ok · ${measured}`,
      title: `Checked ${localTime(probe.checkedAt)}: every declared output is there and measures up.`,
    };
  }
  return {
    tone: 'danger',
    problems,
    label:
      problems === 0
        ? 'probe failed'
        : `probe: ${String(problems)} ${problems === 1 ? 'problem' : 'problems'}`,
    title: `Checked ${localTime(probe.checkedAt)}: ${probe.errors[0] ?? 'the outputs did not measure up.'}`,
  };
}

/** What the last probe said about one of a step's declared outputs. */
function probeFor(step: PlanStep, path: string): OutputProbe | null {
  return step.probe?.outputs.find((output) => output.path === path) ?? null;
}

/**
 * The measurements worth showing on one line, in the order they answer the
 * plan: how long, how big, how smooth, how loud. A measurement the probe did
 * not take is simply absent — an output that is only checked for existing
 * shows its size and nothing else.
 */
function probeFacts(probe: OutputProbe): string[] {
  const facts: string[] = [];
  if (probe.durationMs !== null) facts.push(`${(probe.durationMs / 1000).toFixed(1)} s`);
  if (probe.width !== null && probe.height !== null) {
    facts.push(`${String(probe.width)}×${String(probe.height)}`);
  }
  if (probe.fps !== null) facts.push(`${String(probe.fps)} fps`);
  if (probe.loudnessLufs !== null) facts.push(`${probe.loudnessLufs.toFixed(1)} LUFS`);
  if (probe.sampleRateHz !== null) facts.push(`${(probe.sampleRateHz / 1000).toFixed(1)} kHz`);
  if (probe.bytes !== null && probe.bytes > 0) facts.push(kilobytes(probe.bytes));
  return facts;
}

/** A file size as the step list reads it: kB up to a megabyte, then MB. */
function kilobytes(bytes: number): string {
  return bytes < 1_000_000
    ? `${String(Math.max(1, Math.round(bytes / 1000)))} kB`
    : `${(bytes / 1_000_000).toFixed(1)} MB`;
}

function firstLine(text: string): string {
  return (text.trim().split('\n')[0] ?? '').trim();
}

/** The steps of one type, in the order production reaches them. */
interface StepGroup {
  readonly type: StepType;
  readonly steps: readonly PlanStep[];
}

/**
 * Groups the steps by type. A `Map` keeps its insertion order, so walking the
 * steps in priority order puts each group where its first step falls.
 */
function groupSteps(steps: readonly PlanStep[]): StepGroup[] {
  const byType = new Map<StepType, PlanStep[]>();
  for (const step of [...steps].sort((left, right) => left.priority - right.priority)) {
    const group = byType.get(step.type);
    if (group === undefined) byType.set(step.type, [step]);
    else group.push(step);
  }
  return [...byType].map(([type, grouped]) => ({ type, steps: grouped }));
}

/* ------------------------------------------------------------- the terminal */

/**
 * A browser terminal into the content's own container.
 *
 * The server owns the PTY, so a terminal opened here survives a reload and is
 * rejoined rather than replaced: the panel looks for one already attached to
 * this container before it offers to open another.
 */
function ContainerPanel({
  content,
  onRetrySetup,
  busy,
}: {
  readonly content: ContentData;
  readonly onRetrySetup: () => void;
  readonly busy: Busy;
}) {
  const toast = useToast();
  const containerId = content.containerId;
  const [terminalId, setTerminalId] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  /** One the operator closed here, so a stale list does not re-attach it. */
  const [closed, setClosed] = useState<string | null>(null);
  // Below `lg` the pane is not rendered at all: mounting it would open a
  // WebSocket onto a PTY nothing on screen could show or type into.
  const desktop = useMediaQuery(DESKTOP_QUERY);
  // Folded by default: the terminal, and the look for one to rejoin, only
  // happen while the panel is open.
  const [open, setOpen] = usePanelOpen('container');
  const frame = {
    title: 'Container terminal',
    icon: 'terminal' as const,
    id: 'container',
    collapsible: true,
    defaultOpen: false,
    open,
    onOpenChange: setOpen,
  };

  // A terminal the server still knows about — from a reload, or another tab —
  // is re-attached to rather than duplicated.
  useEffect(() => {
    if (containerId === null || !open) return;
    const controller = new AbortController();
    fetchTerminals(controller.signal)
      .then((terminals) => {
        const existing = terminals.find(
          (terminal) => terminal.container === containerId && terminal.status === 'running',
        );
        if (existing !== undefined && existing.id !== closed) setTerminalId(existing.id);
      })
      .catch(() => {
        // Nothing to re-attach to is not worth a toast; opening one still works.
      });
    return () => {
      controller.abort();
    };
  }, [containerId, closed, open]);

  if (containerId === null) {
    return (
      <Panel {...frame} summary="no container running">
        <p className="muted">
          No container is running for this content, so there is nothing to open a terminal into. Its
          workspace on the server is untouched — running the setup again starts a container around
          the same files.
        </p>
        {canRetrySetup(content) && (
          <button
            type="button"
            className="button"
            onClick={onRetrySetup}
            disabled={busy !== null}
            title="Clone the repositories again, around the same workspace"
          >
            <Icon name="sync" />
            {busy === 'setup' ? 'Working…' : 'Retry setup'}
          </button>
        )}
        <BusyLoader busy={busy} kinds={['setup']} />
      </Panel>
    );
  }

  const onOpen = (): void => {
    setWorking(true);
    createTerminal({ container: containerId, cwd: '/workspace' })
      .then((terminal) => {
        setClosed(null);
        setTerminalId(terminal.id);
      })
      .catch((cause: unknown) => {
        toast.error(`Could not open a terminal: ${describeError(cause)}`);
      })
      .finally(() => {
        setWorking(false);
      });
  };

  const onClose = (): void => {
    if (terminalId === null) return;
    const closing = terminalId;
    setClosed(closing);
    setTerminalId(null);
    closeTerminal(closing).catch((cause: unknown) => {
      toast.error(`Could not close the terminal: ${describeError(cause)}`);
    });
  };

  return (
    <Panel
      {...frame}
      summary={terminalId === null ? 'container running' : 'terminal attached'}
      meta={<Badge tone={terminalId === null ? 'neutral' : 'active'}>{terminalId === null ? 'ready' : 'attached'}</Badge>}
      actions={
        terminalId === null ? (
          <button type="button" className="button" onClick={onOpen} disabled={working}>
            <Icon name="terminal" />
            {working ? 'Opening…' : 'Open terminal'}
          </button>
        ) : (
          <button type="button" className="button button--quiet" onClick={onClose}>
            Close terminal
          </button>
        )
      }
    >
      {terminalId === null ? (
        <p className="field__hint">
          A shell inside this content’s own container, in{' '}
          <code className="mono">/workspace</code> — the clones, the plan and everything produced so
          far. The shell lives on the server, so reloading this page rejoins it.
        </p>
      ) : desktop ? (
        <Suspense fallback={<Skeleton lines={6} />}>
          <TerminalPane terminalId={terminalId} />
        </Suspense>
      ) : (
        <Notice kind="info">
          <strong>Terminal access is available on desktop.</strong> A shell needs a keyboard and a
          screen this narrow cannot give it. It keeps running on the server; everything else on this
          page works here.
        </Notice>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------ small pieces */

function RepositoryList({ content }: { readonly content: ContentData }) {
  if (content.repositories.length === 0) return <span className="muted">none</span>;
  return (
    <>
      {content.repositories.map((link, position) => (
        <span key={link.repositoryId}>
          {position > 0 && ', '}
          {link.repositoryName}
          {link.role === 'primary' && content.repositories.length > 1 && (
            <span className="muted"> (primary)</span>
          )}
        </span>
      ))}
    </>
  );
}

/**
 * The container id, shortened the way `docker ps` shortens it. The full id is
 * on the title, so it can still be copied out of the page.
 */
function ContainerId({ id }: { readonly id: string | null }) {
  if (id === null) return <span className="muted">not running</span>;
  return <span title={id}>{id.slice(0, 12)}</span>;
}

/**
 * The model this one content's clips are generated on (US-085).
 *
 * The side column rather than a dialog, because it is a standing fact about
 * the content and not a recovery from a refusal: the comparison this exists
 * for is made by creating a second content on the same brief and changing this
 * one field. Clearing it is its own answer — a content with no override
 * follows the plan's `**Model:**` line and then Settings, wherever those go
 * next — so the empty option says that rather than naming the install's model
 * as if it had been chosen here.
 *
 * The list is the same catalogue Settings → Video generation lists, and a
 * catalogue that could not be read leaves a text field: an operator who knows
 * the id must still be able to pin a content to it.
 */
/**
 * **Take over** / **Hand back** (one-shot ads US-020): autopilot off or on for
 * this content alone. Taken over, today's gates apply from the next one.
 */
function AutopilotButton({
  content,
  onChanged,
}: {
  readonly content: ContentData;
  readonly onChanged: (content: ContentData) => void;
}) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (): void => {
    setWorking(true);
    setError(null);
    setAutopilot(content.id, !content.autopilot)
      .then(onChanged)
      .catch((cause: unknown) => {
        setError(describeError(cause));
      })
      .finally(() => {
        setWorking(false);
      });
  };

  return (
    <>
      {error !== null && (
        <span className="sketch__refusal" role="alert">
          {error}
        </span>
      )}
      <button
        type="button"
        className="button"
        disabled={working}
        onClick={toggle}
        title={content.autopilot ? wording.AUTOPILOT_TAKE_OVER_TITLE : wording.AUTOPILOT_HAND_BACK_TITLE}
      >
        <Icon name={content.autopilot ? 'pencil' : 'sync'} />
        {content.autopilot ? wording.AUTOPILOT_TAKE_OVER : wording.AUTOPILOT_HAND_BACK}
      </button>
    </>
  );
}

function VideoModelPanel({
  content,
  onChanged,
}: {
  readonly content: ContentData;
  readonly onChanged: (content: ContentData) => void;
}) {
  const [catalogue, setCatalogue] = useState<VideoCatalogueView | null>(null);
  const [choice, setChoice] = useState(content.videoModel ?? '');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = usePanelOpen('video-model');

  // The catalogue is read when the panel is unfolded, not with the page.
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    fetchVideoModels(controller.signal)
      .then(setCatalogue)
      .catch(() => undefined);
    return () => {
      controller.abort();
    };
  }, [open]);

  useEffect(() => {
    setChoice(content.videoModel ?? '');
  }, [content.videoModel]);

  const save = (event: FormEvent): void => {
    event.preventDefault();
    setWorking(true);
    setError(null);
    setVideoModel(content.id, choice.trim() === '' ? null : choice.trim())
      .then((updated) => {
        onChanged(updated);
      })
      .catch((cause: unknown) => {
        setError(describeError(cause));
      })
      .finally(() => {
        setWorking(false);
      });
  };

  const listed = catalogue?.models.find((model) => model.model === content.videoModel) ?? null;
  const dirty = choice.trim() !== (content.videoModel ?? '');

  return (
    <Panel
      title="Video model"
      icon="device-camera-video"
      id="video-model"
      collapsible
      defaultOpen={false}
      open={open}
      onOpenChange={setOpen}
      summary={content.videoModel ?? 'the plan’s, or this install’s'}
      meta={
        content.videoModel === null ? (
          <Badge tone="neutral">install</Badge>
        ) : (
          <Badge tone="wait">override</Badge>
        )
      }
    >
      <form className="stack" onSubmit={save}>
        <label className="field__label" htmlFor="content-video-model">
          Generate clips on
        </label>
        {catalogue !== null && catalogue.models.length > 0 ? (
          <select
            id="content-video-model"
            className="field__input"
            value={choice}
            onChange={(event) => {
              setChoice(event.target.value);
            }}
          >
            <option value="">This install’s model ({catalogue.model})</option>
            {catalogue.models.map((model) => (
              <option key={model.model} value={model.model}>
                {model.name} —{' '}
                {model.usdPerSecond === null
                  ? `priced per ${model.priceUnit}`
                  : `$${model.usdPerSecond.toFixed(4)} a second`}
              </option>
            ))}
          </select>
        ) : (
          <input
            id="content-video-model"
            className="field__input"
            value={choice}
            placeholder={catalogue?.model ?? 'This install’s model'}
            onChange={(event) => {
              setChoice(event.target.value);
            }}
            autoComplete="off"
          />
        )}
        <button className="button" type="submit" disabled={working || !dirty}>
          {working ? 'Saving…' : content.videoModel === null ? 'Pin this content' : 'Save'}
        </button>
        {error !== null && <Notice kind="error">{error}</Notice>}
      </form>
      <p className="field__hint">
        {content.videoModel === null
          ? 'This content follows the model its plan names, and this install’s setting when the plan names none.'
          : `Clips are generated on ${content.videoModel}, whatever the plan and Settings say.`}{' '}
        {listed?.usdPerSecond != null &&
          `$${listed.usdPerSecond.toFixed(4)} a second at the catalogue’s cheapest sku. `}
        A model already generated on stays in the ledger below under its own name.
      </p>
    </Panel>
  );
}

/** The languages the language panel offers; the content's own is added when it is another. */
const CONTENT_LANGUAGE_CHOICES: readonly string[] = [
  'nl-NL',
  'nl-BE',
  'en-US',
  'en-GB',
  'de-DE',
  'fr-FR',
  'es-ES',
  'it-IT',
];

/**
 * The language this content is written and spoken in, and for Dutch how it
 * addresses the viewer (synthesis C2).
 *
 * A content made before contents had a language states none: the voice then
 * gets its script as written and is told no language, which is what happened
 * before. Choosing one here is how such a content is given its own — the next
 * voiceover, transcription and text check use it — without touching Settings.
 */
function LanguagePanel({
  content,
  onChanged,
}: {
  readonly content: ContentData;
  readonly onChanged: (content: ContentData) => void;
}) {
  const [language, setLanguage] = useState(content.language ?? '');
  const [address, setAddress] = useState<ContentAddress | ''>(content.address ?? '');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = usePanelOpen('language');

  useEffect(() => {
    setLanguage(content.language ?? '');
    setAddress(content.address ?? '');
  }, [content.language, content.address]);

  const dutch = language !== '' && isDutchLanguage(language);
  const dirty =
    language !== (content.language ?? '') || (dutch && address !== (content.address ?? ''));

  const save = (event: FormEvent): void => {
    event.preventDefault();
    if (language === '') return;
    setWorking(true);
    setError(null);
    setContentLanguage(content.id, language, dutch && address !== '' ? address : null)
      .then((updated) => {
        onChanged(updated);
      })
      .catch((cause: unknown) => {
        setError(describeError(cause));
      })
      .finally(() => {
        setWorking(false);
      });
  };

  const choices =
    content.language === null || CONTENT_LANGUAGE_CHOICES.includes(content.language)
      ? CONTENT_LANGUAGE_CHOICES
      : [content.language, ...CONTENT_LANGUAGE_CHOICES];

  return (
    <Panel
      title="Language"
      icon="comment"
      id="language"
      collapsible
      defaultOpen={content.language === null}
      open={open}
      onOpenChange={setOpen}
      summary={
        content.language === null
          ? 'not stated'
          : `${languageLabel(content.language)}${content.address === null ? '' : `, ${content.address}`}`
      }
      meta={content.language === null ? <Badge tone="wait">not stated</Badge> : undefined}
    >
      <form className="stack" onSubmit={save}>
        <label className="field__label" htmlFor="content-language-edit">
          Written and spoken in
        </label>
        <select
          id="content-language-edit"
          className="field__input"
          value={language}
          onChange={(event) => {
            setLanguage(event.target.value);
          }}
        >
          {content.language === null && <option value="">Not stated — choose one</option>}
          {choices.map((option) => (
            <option key={option} value={option}>
              {languageLabel(option)}
            </option>
          ))}
        </select>
        {dutch && (
          <div className="field__actions" role="radiogroup" aria-label="How the ad addresses the viewer">
            {(
              [
                ['je', 'je — informal'],
                ['u', 'u — formal'],
                ['', 'not decided'],
              ] as const
            ).map(([value, label]) => (
              <label className="radio" key={value === '' ? 'none' : value}>
                <input
                  type="radio"
                  name="content-address-edit"
                  value={value}
                  checked={address === value}
                  onChange={() => {
                    setAddress(value);
                  }}
                />
                {label}
              </label>
            ))}
          </div>
        )}
        <button className="button" type="submit" disabled={working || !dirty || language === ''}>
          {working ? 'Saving…' : 'Save'}
        </button>
        {error !== null && <Notice kind="error">{error}</Notice>}
      </form>
      <p className="field__hint">
        {content.language === null
          ? 'This content was made before contents had a language. Until it has one, it is spoken in its product’s language, else the default saved in Settings → Defaults — and with neither, the voice gets the script as written and is told no language. Choose the one it is in.'
          : 'The next voiceover, transcription and on-screen text check use it. A voiceover already made keeps the language it was spoken in until it is made again.'}
      </p>
    </Panel>
  );
}

/**
 * What this content's clips cost, per clip and per model (US-085).
 *
 * The ledger's own rows, so a `cached` replay at $0 and a call refused by the
 * spend cap are both here — the question "why is there no clip for scene 3"
 * is answered by the same list as "what did scene 2 cost". The per-model
 * summary above it counts only the calls that produced a clip, because an
 * average that divided by the refusals would report a model as cheap when
 * nothing was ever generated on it.
 */
/**
 * "From the library": every library item a file of this content was copied out
 * of (US-028), so a visual can be traced back to where it came from. An item
 * deleted since keeps its place, under the file's name, and still links — the
 * Library page then says it is gone.
 */
function LibraryUsesPanel({ contentId }: { readonly contentId: string }) {
  const [uses, setUses] = useState<readonly ContentLibraryUse[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchContentLibraryUses(contentId, controller.signal)
      .then(setUses)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        redirectIfUnauthorised(cause);
        // Provenance is a nicety beside the production: a failed read hides
        // the strip rather than putting an error panel on the page.
        setUses([]);
      });
    return () => {
      controller.abort();
    };
  }, [contentId]);

  if (uses === null || uses.length === 0) return null;

  return (
    <Panel
      id="from-the-library"
      title="From the library"
      icon="image"
      collapsible
      summary={`${String(uses.length)} ${uses.length === 1 ? 'item' : 'items'}`}
      meta={<Badge tone="neutral">{uses.length}</Badge>}
    >
      <ul className="library-strip">
        {uses.map((use) => (
          <li key={use.assetId} className="tile library-strip__item">
            <Link
              className="library-strip__link"
              href={libraryItemHref(use.itemId)}
              title={use.path}
              aria-label={`${use.title} in the library`}
            >
              <span className="tile__thumb">
                {use.previewUrl === null ? (
                  <Icon
                    name={use.moving ? 'device-camera-video' : 'image'}
                    className="tile__icon"
                  />
                ) : use.moving ? (
                  <video
                    className="tile__image"
                    src={`${use.previewUrl}#t=0.1`}
                    preload="metadata"
                    muted
                    playsInline
                    aria-hidden="true"
                  />
                ) : (
                  <img className="tile__image" src={use.previewUrl} alt="" loading="lazy" />
                )}
              </span>
              <span className="tile__body">
                <span className="tile__name">{use.title}</span>
                <span className="tile__badges">
                  {use.kind === null ? (
                    <Badge tone="wait">Removed from the library</Badge>
                  ) : (
                    <Badge>{LIBRARY_KIND_LABEL[use.kind]}</Badge>
                  )}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/** `moment` changes whenever an entry may have been written: the status, queued, running. */
function HistoryPanel({ contentId, moment }: { readonly contentId: string; readonly moment: string }) {
  const [events, setEvents] = useState<ContentEvent[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchContentHistory(contentId, controller.signal)
      .then(setEvents)
      .catch(() => {
        // The history is a record, not a control: a failed read leaves it out.
      });
    return () => {
      controller.abort();
    };
  }, [contentId, moment]);

  if (events === null || events.length === 0) return null;
  const latest = events[events.length - 1];

  return (
    <Panel
      title="History"
      icon="history"
      id="history"
      collapsible
      defaultOpen={false}
      summary={latest === undefined ? undefined : `${latest.title}, ${localTime(latest.createdAt)}`}
    >
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">What</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td className="mono">
                  <time dateTime={event.createdAt}>{localTime(event.createdAt)}</time>
                </td>
                <td>
                  <strong>{event.title}</strong>
                  {event.detail !== null && <div className="muted">{event.detail}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function ClipLedgerPanel({ contentId }: { readonly contentId: string }) {
  const [ledger, setLedger] = useState<ClipLedger | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchClipLedger(contentId, controller.signal)
      .then(setLedger)
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(describeError(cause));
      });
    return () => {
      controller.abort();
    };
  }, [contentId]);

  if (error !== null) {
    return (
      <Panel
        title="Clips"
        icon="tag"
        id="clips"
        collapsible
        defaultOpen={false}
        summary="could not load"
      >
        <Notice kind="error">{error}</Notice>
      </Panel>
    );
  }
  if (ledger === null || ledger.rows.length === 0) return null;

  return (
    <Panel
      title="Clips"
      icon="tag"
      id="clips"
      collapsible
      defaultOpen={false}
      summary={`${String(ledger.rows.length)} ${ledger.rows.length === 1 ? 'call' : 'calls'}, ${ledgerMoney(
        ledger.totalUsd,
      )}`}
      meta={<Badge tone="neutral">{ledgerMoney(ledger.totalUsd)}</Badge>}
    >
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Model</th>
              <th scope="col">Clips</th>
              <th scope="col">Seconds</th>
              <th scope="col">Per clip</th>
              <th scope="col">Per second</th>
              <th scope="col">Total</th>
            </tr>
          </thead>
          <tbody>
            {ledger.models.map((model) => (
              <tr key={model.model}>
                <th scope="row" className="mono">
                  {model.model}
                </th>
                <td>{model.clips}</td>
                <td>{model.seconds === 0 ? <span className="muted">—</span> : model.seconds}</td>
                <td className="mono">
                  {model.usdPerClip === null ? (
                    <span className="muted">—</span>
                  ) : (
                    ledgerMoney(model.usdPerClip)
                  )}
                </td>
                <td className="mono">
                  {model.usdPerSecond === null ? (
                    <span className="muted">—</span>
                  ) : (
                    ledgerMoney(model.usdPerSecond)
                  )}
                </td>
                <td className="mono">{ledgerMoney(model.costUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="field__label">Every call, in the order it was billed</p>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Model</th>
              <th scope="col">What</th>
              <th scope="col">Length</th>
              <th scope="col">Cost</th>
            </tr>
          </thead>
          <tbody>
            {ledger.rows.map((row) => (
              <tr key={row.id}>
                <td>{localTime(row.createdAt)}</td>
                <td className="mono">
                  {row.model ?? <span className="muted">not recorded</span>}
                </td>
                <td>
                  {row.operation === 'video_upscale'
                    ? 'Upscale'
                    : row.operation === 'video_animate'
                      ? 'Animation'
                      : row.operation === 'video_talk'
                        ? 'Talking head'
                        : 'Clip'}
                  {row.status === 'ok' ? '' : ` — ${row.status}`}
                  {row.error === null ? '' : ` (${row.error})`}
                </td>
                <td>
                  {row.seconds === null ? (
                    <span className="muted">—</span>
                  ) : (
                    `${String(row.seconds)}s`
                  )}
                </td>
                <td className="mono">{ledgerMoney(row.costUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="field__hint">
        The gateway's own rows. A <strong>cached</strong> call cost nothing because an identical
        clip had already been paid for in this content; a <strong>refused</strong> one never
        reached OpenRouter at all. The per-model figures above count only the calls that produced
        a clip.
      </p>
    </Panel>
  );
}

/**
 * Money in the clip ledger: cents, except under a cent, where a per-second
 * rate rounded to `$0.00` would read as free rather than as not much.
 */
function ledgerMoney(usd: number): string {
  return usd !== 0 && Math.abs(usd) < 0.01 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(2)}`;
}

/** What a version in the crumbs means: pinned by Mark ready, or followed until then. */
function versionPinTitle(what: string, pin: ContentVersionPin): string {
  return pin.pinned
    ? `Pinned at Mark ready: this content stays on version ${String(pin.version)} of the ${what} until it goes back to planning.`
    : `Follows the current ${what} until it is marked ready.`;
}
